import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { loadMap, isWalkable, resolveCollision, drawMap, DEFAULT_SPAWN, DEFAULT_SPAWN_Y } from './mapUtils';
import HallDropdown from './hall/HallDropdown';
import HallMemberMenu from './hall/HallMemberMenu';
import PlayerPopupHeader, { canLinkHallProfile } from './hall/PlayerPopupHeader';
import PlayerAudioPanel from './hall/PlayerAudioPanel';
import VoiceQuestionUI from './hall/VoiceQuestionUI';
import HallChatPanel from './hall/HallChatPanel';
import { useXpTicker, useCurrentXp } from './useXpTicker';
import { formatXpLabel, getGuestHallChatCount, bumpGuestHallChatCount, setGuestHallChatCount, GUEST_HALL_CHAT_LIMIT, requestSignInPrompt } from './xp';
import {
  getPlayerAudioPref,
  patchPlayerAudio,
  readPlayerAudioMap
} from './playerAudio';
import {
  readLocalProgress,
  writeLocalProgress,
  shouldPersist,
  shouldWriteServer,
  pickNewer,
  fetchServerProgress,
  saveServerProgress,
  saveServerProgressBeacon,
  getPlayerPosition,
  getPlayerDuration,
  readPlayerClock,
  getLocalResumeStartSeconds,
  safeSeekTo,
  shouldHoldMomentProgress,
  shouldSkipProgressWrite,
  MOMENT_HOLD_WATCH_MS,
} from './videoProgress';
import { buildVideoMomentUrl, formatVideoTime } from './videoMoment';
import {
  CLIP_ARM_MS,
  CLIP_CREATED_EVENT,
  CLIP_TITLE_MAX,
  buildClipUrl,
  clampClipRange,
  rememberClipTitle,
  clipPauseDecision,
  replayClipFromStart,
  clipRangeForPlayerDuration,
  clipRangeFromClockTaps,
  inferClipIsLive,
  resolveClipClock,
} from './clipShare';
import { fetchHallTvMedia } from './hallTvMedia';
import { attachYoutubeEmbed, YT_STATE } from './youtubeEmbedPlayer';
import { remoteSeatClipY } from './seatClip';
import {
  rememberChatBubble,
  forgetChatBubble,
  pruneChatBubbles,
  chatBubbleFor,
  chatBubbleAlpha,
  drawChatBubble,
  actionableChatBubbleLink,
  chatBubbleHitAt,
  chatBubbleVariant
} from './chatBubble';
import {
  shouldEmitMove,
  applySnapshotPose,
  sampleInterp,
  viewUnchanged,
  moveStateToInt,
  intToMoveState,
  applyClockOffset,
  VIEW_EMIT_MIN_MS
} from './hallMovement';
import {
  STAGE_STAIR_NAMES,
  TEAM_SEAT_SIT_Y,
  isCreatorHall,
  hasStageAccess,
  canUseLanSeats,
  voiceGroup,
  shouldVoiceConnect,
  voiceGainFor,
  teamHasFreeSeat
} from './hallVoice';
import { playHallAnnouncement } from './hallAnnounce';
import {
  MIC_GATE_OFFSET_DEFAULT,
  MIC_GATE_OFFSET_MAX,
  MIC_GATE_OFFSET_MIN,
  MIC_GATE_OFFSET_STORAGE_KEY,
  REMOTE_TALKING_LEVEL,
  SPEECH_HIGHPASS_HZ,
  SPEECH_LOWPASS_HZ,
  clampMicGateOffset,
  createMicGateState,
  formatGateOffset,
  levelDbFromSamples,
  micGateMeterView,
  readStoredMicGateOffset,
  stepMicGate
} from './micGate';

function isIPadTouchDevice() {
  if (typeof navigator === 'undefined') return false;
  if (/iPad/i.test(navigator.userAgent)) return true;
  // iPadOS 13+ Safari reports as Macintosh desktop, so YouTube uses the
  // desktop player (tap = pause) instead of mobile double-tap ±10 skip.
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
}

function isMobileShareDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Windows/i.test(ua)) return false;
  if (/Android|iPhone|iPod/i.test(ua)) return true;
  return isIPadTouchDevice();
}

function preferNativeShare() {
  return isMobileShareDevice() && typeof navigator.share === 'function';
}

// Landscape next/prev gutter: 56px on desktop (w-14), half that on mobile.
const MOBILE_UI_BREAKPOINT = 1024;
const LANDSCAPE_ARROW_DESKTOP_W = 56;
const LANDSCAPE_ARROW_MOBILE_W = 28;
// Unscaled hall-chat layout width. When the leftover strip is narrower, the
// panel is drawn at this size and CSS-scaled down so the TV never shrinks.
const LANDSCAPE_CHAT_LOGICAL_W = 280;

function landscapeArrowW(canvasW) {
  return canvasW < MOBILE_UI_BREAKPOINT
    ? LANDSCAPE_ARROW_MOBILE_W
    : LANDSCAPE_ARROW_DESKTOP_W;
}

function flagsFromMembership(membership) {
  if (!membership) return { hallMuted: false, timedOut: false, timeoutExpiresAt: null, isVip: false };
  const timeoutExpiresAt = membership.timeoutExpiresAt || null;
  const timedOut = !!(timeoutExpiresAt && new Date(timeoutExpiresAt).getTime() > Date.now());
  return {
    hallMuted: !!membership.isMuted,
    timedOut,
    timeoutExpiresAt,
    isVip: !!membership.isVip
  };
}

function hallRestrictionCopy(kicked) {
  const reason = kicked?.reason || 'denied';
  if (reason === 'kicked') {
    return {
      title: 'Kicked from this hall',
      body: 'A hall admin ended your session. You can rejoin.',
      canRejoin: true
    };
  }
  if (reason === 'banned' || reason === 'banned_hall') {
    return {
      title: 'Banned from this hall',
      body: 'You cannot join this hall until a hall admin unbans you.',
      canRejoin: false
    };
  }
  if (reason === 'timeout') {
    const until = kicked?.timeoutExpiresAt
      ? new Date(kicked.timeoutExpiresAt).toLocaleString()
      : null;
    return {
      title: 'Timed out from this hall',
      body: until
        ? `You cannot rejoin until ${until}.`
        : 'You cannot rejoin until the timeout expires.',
      canRejoin: false
    };
  }
  if (reason === 'banned_site') {
    return {
      title: 'Account banned',
      body: 'Your account cannot join halls.',
      canRejoin: false
    };
  }
  return {
    title: 'Cannot join this hall',
    body: 'You do not have access to this hall right now.',
    canRejoin: false
  };
}


const LiveWorld = ({
  videoId, videoUrl, user, metaBar,
  // When true, never apply start=/seek resume (breaks live embeds → black TV)
  isLive = false,
  // Hall (room) identity — slug of the hall this LiveWorld instance belongs to.
  // Defaults to 'commons' so existing mount sites continue to work.
  hallSlug = 'commons',
  hallDisplayName = 'Commons',
  onChangeHall, // optional: (slug) => void. Wires the controls-bar dropdown.
  // False until /api/auth/status resolves. Connecting before that joins as Guest,
  // then a second socket as the signed-in user — same mic, hear-yourself echo.
  authReady = true,
  // Video interaction controls
  votes, isVoting, isAuthenticated: isAuthProp,
  onVote, onNext, onPrev, canGoPrev, onShare,
  dbVideoId,
  momentStartSeconds = 0,
  clipId = null,
  clipStartSeconds = 0,
  clipEndSeconds = 0,
  onPlayHallMedia,
}) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null); // Draws sprites/names above the TV iframe
  const tvIframeRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const { xp: xpDisplay, hallXp: hallXpDisplay } = useCurrentXp({
    isAuthenticated: !!isAuthProp,
    seedXp: user?.xp,
    hallSlug
  });
  const [players, setPlayers] = useState({});
  // Note: playersRef is declared further down with the other refs; we set it
  // every time `players` changes via a useEffect below the component setup.
  const [myPosition, setMyPosition] = useState(DEFAULT_SPAWN); // Flush with the bottom of the stage barrier
  const [localStream, setLocalStream] = useState(null);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [isLocalMuted, setIsLocalMuted] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(true); // false = stream stopped, iOS audio session released
  const [playerAudio, setPlayerAudio] = useState(readPlayerAudioMap);
  const [selfMutedPlayers, setSelfMutedPlayers] = useState({});
  // Staff hall-mute / timeout flags keyed by playerId. Distinct from local
  // per-user mute (red username) and from the player's own self-mute.
  const [hallModeration, setHallModeration] = useState({});
  const [isAudioContextStarted, setIsAudioContextStarted] = useState(false);
  const [audioReadyTrigger, setAudioReadyTrigger] = useState(0);
  const [globalVolume, setGlobalVolume] = useState(1.0);
  const [talkingPlayers, setTalkingPlayers] = useState({});
  const [isLocalTalking, setIsLocalTalking] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(null);
  // Initial camera pre-centered on default spawn (960, 1593) with viewport 1200x800 and 100px landscape bottom margin
  const [camera, setCamera] = useState({ x: 360, y: 893 });
  // Refs for TV overlay - updated directly in the draw loop, no React state = no re-renders
  const tvContainerRef = useRef(null);
  const tvControlsRef = useRef(null);     // positioned same as TV, holds vote/nav buttons above canvas layers
  const blackoutScrollRef = useRef(null); // transparent scroll-passthrough over the blackout area
  const hallChatSlotRef = useRef(null);   // docked chat in theater black around the TV
  const hallChatScaleRef = useRef(null);  // inner panel; CSS-scaled on narrow landscape
  const liveWorldWrapperRef = useRef(null); // flex-column wrapper: controls bar + canvas = 100vh
  const controlsBarRef     = useRef(null); // controls bar — measured so canvas gets the remainder
  const canvasScaleRef = useRef({ scaleX: 1, scaleY: 1 });
  const videoUrlRef = useRef(videoUrl);

  // Sprite animation state
  const [spriteSheet, setSpriteSheet] = useState(null);
  const [spriteSheetLoaded, setSpriteSheetLoaded] = useState(false);

  // Base logical dimensions (used as fallback / initial value only).
  // The canvas is resized to match the container on mount/resize, so the actual
  // visible area adapts to whatever width/height the container has.
  const VIEWPORT_WIDTH = 1200;
  const VIEWPORT_HEIGHT = 800;
  // Track actual rendered canvas size so the camera bounds update on resize.
  const [viewportSize, setViewportSize] = useState({ width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT });
  const viewportSizeRef = useRef({ width: VIEWPORT_WIDTH, height: VIEWPORT_HEIGHT });

  // Sprite configuration (800x1088 sprite sheet: 10 frames wide, 17 rows tall)
  const spriteConfig = {
    frameWidth: 80,       // Width of ONE frame in source (800px / 10 max frames per row)
    frameHeight: 64,      // Height of ONE frame in source (1088px / 17 rows)
    scale: 3,             // Scale factor for rendering (3x = 240x192 displayed)
    renderWidth: 240,     // Displayed width (80 * 3)
    renderHeight: 192,    // Displayed height (64 * 3)
    characterWidth: 66,   // Actual character model width at 3x scale (22 * 3)
    characterHeight: 138, // Actual character model height at 3x scale (46 * 3)
    idleFrames: 5,        // Frames in idle animation  (row 0, srcY = 0)
    walkFrames: 8,        // Frames in walk animation  (row 1, srcY = 64)
    runFrames: 8,         // Frames in run animation   (row 2, srcY = 128)
    animationSpeed: 120   // Milliseconds per frame
  };

  // World Y for all team-seated players.  Chosen so the nametag (drawn at
  // player.y - characterHeight - 5) sits flush just below the stage TV bottom
  // (TV_BOTTOM_WORLD_Y = 1312).  1312 + 138 + 5 = 1455.


  const interactionRef = useRef({
    startX: 0,
    startY: 0,
    startClientX: 0,
    startClientY: 0,
    isDragging: false,
    isActive: false,
    lastType: null // 'touch' or 'mouse'
  });

  const requestRef = useRef();
  const drawFnRef  = useRef(null);  // always points to the latest draw() closure
  const ctxCacheRef  = useRef(null); // cached 2d context for main canvas
  const octxCacheRef = useRef(null); // cached 2d context for overlay canvas
  // Fixed TV rectangle — computed once per viewport change, read every frame.
  // Using a fixed ref means the TV never resizes or repositions while the camera moves.
  const fixedTvRectRef = useRef(null);
  // Last TV/scrollDiv DOM values — only write to DOM when they change
  const prevTvStyleRef    = useRef({ l: null, t: null, w: null, h: null, display: null });
  const prevScrollStyleRef = useRef({ display: null, h: null });
  const prevChatSlotStyleRef = useRef({ l: null, t: null, w: null, h: null, s: null, iw: null, ih: null, display: null });
  const keysPressed = useRef({}); // Track which keys are currently pressed
  const dragTargetRef = useRef(null); // World-space {x,y} the player is dragging toward, null when inactive
  const movementLoopRef = useRef(null); // Track movement animation frame
  const cameraRef = useRef({ x: 360, y: 893 }); // Pre-centered on default spawn (960, 1593) with 100px bottom margin
  const socketRef = useRef(null);       // Always-current socket ref (no stale-closure issues)
  const playersRef = useRef({});        // Always-current players state ref (for use in callbacks)
  const lastMoveEmitRef = useRef(0);
  const lastSentMoveStateRef = useRef(null);
  const lastViewEmitRef = useRef(0);
  const lastViewBoxRef = useRef(null);
  const remoteInterpRef = useRef({});
  const snapshotClockOffsetRef = useRef(null);
  const emitMoveRef = useRef(() => {});
  const applyProximityVoiceGainsRef = useRef(() => {});
  // (mirror effect lives just after the players useState declaration)
  const videoIdRef = useRef(videoId);   // Always-current videoId ref (TV content, NOT room)
  const momentStartRef = useRef(momentStartSeconds);
  const prevMomentStartRef = useRef(null);
  const hallSlugRef = useRef(hallSlug); // Always-current hall slug (socket room)
  const hallModerationRef = useRef({});

  useEffect(() => {
    const onLogout = () => {
      const sock = socketRef.current;
      if (!sock) return;
      sock.emit('leave-hall');
    };
    window.addEventListener('bigplays:logout', onLogout);
    return () => window.removeEventListener('bigplays:logout', onLogout);
  }, []);
  const mapDataRef = useRef(null);      // Always-current map data ref (used in socket handlers to avoid stale closure)
  // Cached per-hall membership from the server; updated by hall-member-updated event.
  const [hallMembership, setHallMembership] = useState(null);
  const hallMembershipRef = useRef(null);
  const [hallMeta, setHallMeta] = useState({ slug: hallSlug, roomType: null, ownerUserId: null });
  const hallMetaRef = useRef({ slug: hallSlug, roomType: null, ownerUserId: null });
  const stageAccessRef = useRef(false);
  const syncVoicePeersRef = useRef(() => {});
  const connectingPeersRef = useRef(new Set());
  const [hallKicked, setHallKicked] = useState(null); // { reason, timeoutExpiresAt? }
  const hallKickedRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatPolicyDenied, setChatPolicyDenied] = useState(false);
  const [chatHasMore, setChatHasMore] = useState(false);
  const [chatLoadingOlder, setChatLoadingOlder] = useState(false);
  const [chatLiveMessage, setChatLiveMessage] = useState(null);
  const chatMessagesRef = useRef([]);
  const chatBubblesRef = useRef({});
  const chatBubbleHitsRef = useRef([]);
  const activateChatBubbleLinkRef = useRef(() => {});
  const chatComposerFocusedRef = useRef(false);
  const chatViewportHoldUntilRef = useRef(0);
  const updateCanvasScaleRef = useRef(() => {});

  useEffect(() => {
    hallKickedRef.current = null;
    setHallKicked(null);
    chatMessagesRef.current = [];
    chatBubblesRef.current = {};
    chatBubbleHitsRef.current = [];
    setChatMessages([]);
    setChatPolicyDenied(false);
    setChatHasMore(false);
    setChatLiveMessage(null);
    remoteInterpRef.current = {};
    snapshotClockOffsetRef.current = null;
    lastViewBoxRef.current = null;
    lastViewEmitRef.current = 0;
    lastMoveEmitRef.current = 0;
    lastSentMoveStateRef.current = null;
    hallMetaRef.current = { slug: hallSlug, roomType: null, ownerUserId: null };
    setHallMeta({ slug: hallSlug, roomType: null, ownerUserId: null });
    setHallMembership(null);
    hallMembershipRef.current = null;
    stageAccessRef.current = false;
  }, [hallSlug]);

  // Right-click / long-press moderation menu on other players
  const [menuTarget, setMenuTarget] = useState(null); // { userId, username } or null
  const longPressTimerRef = useRef(null);

  // Tap own sprite → mic gate sensitivity panel
  const [showMicGatePanel, setShowMicGatePanel] = useState(false);
  const [audioTarget, setAudioTarget] = useState(null);
  const [micGateOffset, setMicGateOffset] = useState(readStoredMicGateOffset);
  const [micGateMeter, setMicGateMeter] = useState({
    levelDb: -80,
    noiseFloorDb: -62,
    openThresholdDb: -51,
    open: false,
    measured: false
  });
  const micGateOffsetRef = useRef(micGateOffset);
  const myPositionRef = useRef(myPosition);

  // Volume slider pop-up (shown for 10s when voice status text is clicked)
  const [showVolume, setShowVolume] = useState(false);
  const volTimeoutRef = useRef(null);
  const handleVoiceClick = () => {
    setShowVolume(true);
    if (volTimeoutRef.current) clearTimeout(volTimeoutRef.current);
    volTimeoutRef.current = setTimeout(() => setShowVolume(false), 5000);
  };

  // TV fullscreen expand — when true the TV iframe is CSS-fullscreen (fixed inset-0)
  const [isTvExpanded, setIsTvExpanded] = useState(false);
  const [shareToast, setShareToast] = useState('');
  const isTvExpandedRef = useRef(false);
  // YouTube IFrame Player API instance — gives synchronous getCurrentTime() for reliable seeking
  const ytPlayerRef = useRef(null);
  // Playback position persistence (YouTube continue-watching style)
  const progressRestoredForRef = useRef(null); // youtubeVideoId last restored
  const lastSavedProgressRef = useRef({ youtubeVideoId: null, position: null });
  // Timestamp-link hold: freeze prior resume until 1 min of watching, then ask.
  const momentHoldRef = useRef(null);
  const beginMomentProgressHoldRef = useRef(() => {});
  const [momentResumePrompt, setMomentResumePrompt] = useState(null);
  const [clipArmedUntil, setClipArmedUntil] = useState(0);
  const [clipArmStart, setClipArmStart] = useState(null);
  const [clipDraft, setClipDraft] = useState(null);
  const [clipTitleInput, setClipTitleInput] = useState('');
  const [clipSubmitting, setClipSubmitting] = useState(false);
  const [localClip, setLocalClip] = useState(null);
  const [clipReplayNonce, setClipReplayNonce] = useState(0);
  const clipDraftRef = useRef(null);
  const isClipPlaybackRef = useRef(false);
  const lastClipPlaybackRef = useRef(false);
  const prevClipPlaybackRef = useRef(false);
  const clipRangeRef = useRef({ start: 0, end: 0 });
  const clipUnplayableNotifiedRef = useRef(null);
  const lastClipClockRef = useRef({ t: null, atMs: 0, duration: null, looksLive: false, stuckAtHead: false });
  const propClipStart = Math.max(0, Math.floor(Number(clipStartSeconds) || 0));
  const propClipEnd = Math.max(0, Math.floor(Number(clipEndSeconds) || 0));
  const activeClipStart = localClip ? localClip.startSeconds : propClipStart;
  const activeClipEnd = localClip ? localClip.endSeconds : propClipEnd;
  const isClipPlayback = activeClipEnd > activeClipStart;
  clipDraftRef.current = clipDraft;
  isClipPlaybackRef.current = isClipPlayback;
  clipRangeRef.current = { start: activeClipStart, end: activeClipEnd };
  const clipButtonActive = !!clipDraft || clipArmedUntil > Date.now();
  // Live DVR resume: seek only after PLAYING (start=/early seekTo → black TV)
  const pendingLiveSeekRef = useRef(null); // { youtubeVideoId, position } | null
  const userRef = useRef(user);
  // Frozen once per videoId — do NOT read localStorage into iframe src every render
  // or periodic progress saves will change start= and reload the player.
  // Lives never get start= (black iframe); they resume via seekTo after play starts.
  const [embedResumeStart, setEmbedResumeStart] = useState(() => (
    isLive ? 0 : getLocalResumeStartSeconds(videoId)
  ));
  // Lives cannot use start=/end= (black player). Keep one iframe per live and seek.
  // VODs remount when the clip range or resume start changes so YouTube buffers that point.
  const tvPlaybackKey = isLive
    ? 'live'
    : (isClipPlayback
      ? `clip-${clipId || 'x'}-${activeClipStart}-${activeClipEnd}`
      : `vod-${embedResumeStart || 0}`);
  // Double-tap/click detection on the TV side zones
  const lastTapRef = useRef({ time: 0, zone: null });
  const singleTapTimerRef = useRef(null);
  // iOS fires a synthetic click after touchend; ignore it so one finger tap is one tap.
  const lastTouchTapAtRef = useRef(0);
  const seekHintRef = useRef(null);
  const seekHintTimerRef = useRef(null);
  const flashSeekHint = (deltaSeconds) => {
    const el = seekHintRef.current;
    if (!el) return;
    el.textContent = deltaSeconds < 0 ? '<  −10' : '+10  >';
    el.style.left = deltaSeconds < 0 ? '7%' : 'auto';
    el.style.right = deltaSeconds > 0 ? '7%' : 'auto';
    el.style.opacity = '1';
    clearTimeout(seekHintTimerRef.current);
    seekHintTimerRef.current = setTimeout(() => {
      el.style.opacity = '0';
    }, 550);
  };
  const seekTvSide = (deltaSeconds) => {
    const player = ytPlayerRef.current;
    if (player?.getCurrentTime) {
      player.seekTo(Math.max(0, player.getCurrentTime() + deltaSeconds), true);
    }
    flashSeekHint(deltaSeconds);
  };
  const seekTvToMoment = (seconds) => {
    const player = ytPlayerRef.current;
    if (!player?.seekTo) return;
    const t = Math.max(0, Math.floor(Number(seconds) || 0));
    try {
      player.seekTo(t, true);
      if (typeof player.playVideo === 'function') player.playVideo();
    } catch (_) {}
  };
  const seekLiveEdge = (player) => {
    const target = player || ytPlayerRef.current;
    if (!target?.seekTo) return;
    try {
      const duration = typeof target.getDuration === 'function' ? target.getDuration() : 0;
      const edge = Number.isFinite(duration) && duration > 1 ? duration : 1e9;
      target.seekTo(edge, true);
      if (typeof target.playVideo === 'function') target.playVideo();
    } catch (_) {}
  };
  // YouTube's native ±10 skip only runs while the iframe has keyboard focus.
  // We cannot inject keys into a cross-origin embed, so arrows just focus it
  // (same as a play/pause click) and the next arrow is handled by YouTube.
  const tvFocusTimerRef = useRef(null);
  const focusHall = () => {
    clearTimeout(tvFocusTimerRef.current);
    const iframe = tvIframeRef.current;
    if (iframe && document.activeElement === iframe) {
      iframe.blur();
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      canvas.focus({ preventScroll: true });
    } catch {
      canvas.focus();
    }
  };
  const focusTvPlayer = () => {
    const iframe = tvIframeRef.current;
    if (!iframe) return;
    try {
      iframe.focus({ preventScroll: true });
    } catch {
      iframe.focus();
    }
    // After skip, give the hall the keys again so WASD/E can unfocus without a click.
    // While the iframe is focused the browser will not send those keys to us.
    clearTimeout(tvFocusTimerRef.current);
    tvFocusTimerRef.current = setTimeout(() => {
      if (document.activeElement === tvIframeRef.current) focusHall();
    }, 800);
  };

  // Seating system
  const [isSitting, setIsSitting] = useState(false);
  const isSittingRef = useRef(false);
  const currentSeatRef = useRef(null);   // which seat the player is in
  const nearSeatRef = useRef(null);      // seat within proximity (set each draw frame)
  // Seated camera: lerps from current camera toward a stage-focused target
  const seatedCameraRef = useRef({ x: 360, y: 910 });
  const seatCameraTargetRef = useRef({ x: 360, y: 910 });

  const emitMove = (x, y, moveState, immediate) => {
    const sock = socketRef.current;
    if (!sock) return;
    const now = Date.now();
    if (!shouldEmitMove({
      now,
      lastEmitAt: lastMoveEmitRef.current || null,
      moveState,
      lastSentMoveState: lastSentMoveStateRef.current,
      immediate
    })) return;
    lastMoveEmitRef.current = now;
    lastSentMoveStateRef.current = moveState;
    sock.emit('move', {
      x,
      y,
      hallSlug: hallSlugRef.current,
      moveState,
      immediate: !!immediate
    });
  };
  emitMoveRef.current = emitMove;

  const seedRemotePose = (player) => {
    if (!player?.id) return;
    remoteInterpRef.current[player.id] = applySnapshotPose(null, {
      x: player.x,
      y: player.y,
      s: moveStateToInt(player.moveState || 'idle'),
      tp: 1
    }, Date.now());
  };

  // Sit the player into an audience seat: occupancy check, teleport, camera lerp toward stage
  const sitDown = useCallback((seat, playersSnapshot) => {
    if (isSittingRef.current || !seat) return;

    // Prevent sitting if another player already occupies this seat.
    // Audience sit Y = seat.y + half renderHeight; check within 60 world units of seat centre.
    const expectedSitY = seat.y + Math.round(spriteConfig.renderHeight * 0.5);
    const occupied = Object.values(playersSnapshot || {}).some(p => {
      const dx = p.x - seat.cx;
      const dy = p.y - expectedSitY;
      return dx * dx + dy * dy < 60 * 60;
    });
    if (occupied) return;

    const canvas = canvasRef.current;
    const sc = canvasScaleRef.current.scale || 1;
    const logW = canvas ? canvas.width / sc : 960;
    const logH = canvas ? canvas.height / sc : 600;
    // Landscape: pan so bottom of viewport = bottom of LAN desks (y:1488), stage fills screen above.
    // Portrait: anchor to the bottom of the map so the seated view shows from the map's bottom edge.
    const LAN_DESK_BOTTOM_Y = 1488;
    const MAP_HEIGHT = 1920;
    const seatPortrait = window.innerHeight > window.innerWidth;
    seatCameraTargetRef.current = {
      x: Math.max(0, Math.min(1920 - logW, 960 - logW / 2)),
      y: seatPortrait
        ? Math.max(0, MAP_HEIGHT - logH)
        : Math.max(0, LAN_DESK_BOTTOM_Y - logH),
    };
    seatedCameraRef.current = { x: cameraRef.current.x, y: cameraRef.current.y };
    isSittingRef.current = true;
    setIsSitting(true);
    currentSeatRef.current = seat;
    // Show top 50% of the sprite above the chair back; hide the bottom 50% behind it.
    const sitY = expectedSitY;
    setMyPosition({ x: seat.cx, y: sitY });
    emitMoveRef.current(seat.cx, sitY, 'idle', true);
    setTimeout(() => syncVoicePeersRef.current({ x: seat.cx, y: sitY }), 0);
  }, []);

  // Sit the player into a TEAM seat (Team A or B): occupancy check, bottom-of-map camera, team facing
  const applyTeamSeatSit = useCallback((seat) => {
    if (!seat) return;
    const sitY = TEAM_SEAT_SIT_Y;
    const canvas = canvasRef.current;
    const sc = canvasScaleRef.current.scale || 1;
    const logW = canvas ? canvas.width / sc : 960;
    const logH = canvas ? canvas.height / sc : 600;
    const MAP_HEIGHT = 1920;
    seatCameraTargetRef.current = {
      x: Math.max(0, Math.min(1920 - logW, 960 - logW / 2)),
      y: Math.max(0, MAP_HEIGHT - logH),
    };
    seatedCameraRef.current = { x: cameraRef.current.x, y: cameraRef.current.y };
    isSittingRef.current = true;
    setIsSitting(true);
    currentSeatRef.current = seat;
    setMyPosition({ x: seat.cx, y: sitY });
    emitMoveRef.current(seat.cx, sitY, 'idle', true);
    setTimeout(() => syncVoicePeersRef.current({ x: seat.cx, y: sitY }), 0);
  }, []);

  const lanSitAllowed = useCallback(() => canUseLanSeats({
    isCreatorHall: isCreatorHall(hallMetaRef.current)
      || isCreatorHall({ slug: hallSlugRef.current }),
    membership: hallMembershipRef.current,
    user: userRef.current,
    ownerUserId: hallMetaRef.current?.ownerUserId
  }), []);

  const sitInTeamSeat = useCallback((seat, playersSnapshot) => {
    if (isSittingRef.current || !seat) return;
    if (!lanSitAllowed()) return;
    const occupied = Object.values(playersSnapshot || {}).some(p => {
      const dx = p.x - seat.cx;
      const dy = p.y - TEAM_SEAT_SIT_Y;
      return Math.sqrt(dx * dx + dy * dy) < 70;
    });
    if (occupied) return;
    applyTeamSeatSit(seat);
  }, [applyTeamSeatSit, lanSitAllowed]);
  const applyTeamSeatSitRef = useRef(applyTeamSeatSit);
  applyTeamSeatSitRef.current = applyTeamSeatSit;

  // Stand the player up: teleport to concourse in front of the seat rows, restore normal camera
  const standUp = useCallback(() => {
    const seat = currentSeatRef.current;
    if (!seat || !isSittingRef.current) return;
    isSittingRef.current = false;
    setIsSitting(false);
    currentSeatRef.current = null;
    const standX = seat.cx;
    // Team seats are on-stage. Teleport back into the audience concourse, flush
    // with the bottom of the stage-barrier collision box (y=1544–1592).
    const standY = DEFAULT_SPAWN_Y;
    setMyPosition({ x: standX, y: standY });
    emitMoveRef.current(standX, standY, 'idle', true);
    setTimeout(() => syncVoicePeersRef.current({ x: standX, y: standY }), 0);
  }, []);

  // Animation state tracking per player
  const animationStateRef = useRef({});
  const lastAnimationUpdateRef = useRef(0);

  // Store our effective character ID (userId or socket.id)
  const myPlayerIdRef = useRef(null);

  // WebRTC and Audio refs
  const peersRef = useRef({});
  const gainNodesRef = useRef({});
  const audioElementsRef = useRef({});
  const audioCtxRef = useRef(null);
  const playerVolumesRef = useRef({}); // last-computed normalised volume (0–1) per player, for iOS fallback
  const analyserNodesRef = useRef({});
  const localAnalyserRef = useRef(null);
  const localStreamRef = useRef(null); // gated outbound stream sent to peers
  const localMicRawStreamRef = useRef(null); // raw getUserMedia tracks (must be stopped on teardown)
  const localGateGainRef = useRef(null); // GainNode: 0 = gated closed, 1 = transmitting
  const localMicSourceRef = useRef(null);
  const localSpeechHighpassRef = useRef(null);
  const localSpeechLowpassRef = useRef(null);
  const localSpeechSinkRef = useRef(null);
  const speechLevelScratchRef = useRef(null);
  const micGateOpenRef = useRef(false);
  const micGateStateRef = useRef(createMicGateState());
  const micGateTickAtRef = useRef(0);

  // Outbound gate follows the room-noise floor. The slider is a few dB of margin.
  useEffect(() => {
    micGateOffsetRef.current = micGateOffset;
  }, [micGateOffset]);
  useEffect(() => {
    myPositionRef.current = myPosition;
  }, [myPosition]);

  useEffect(() => {
    hallMetaRef.current = hallMeta;
    hallMembershipRef.current = hallMembership;
    stageAccessRef.current = hasStageAccess({
      isCreatorHall: isCreatorHall(hallMeta) || isCreatorHall({ slug: hallSlugRef.current }),
      membership: hallMembership,
      user,
      ownerUserId: hallMeta.ownerUserId
    });
  }, [hallMeta, hallMembership, user]);

  useXpTicker({
    enabled: !!socket,
    isAuthenticated: !!isAuthProp,
    hallSlug
  });

  const setMicGateOffsetPersisted = useCallback((next) => {
    const clamped = clampMicGateOffset(Math.round(Number(next)));
    setMicGateOffset(clamped);
    micGateOffsetRef.current = clamped;
    try {
      localStorage.setItem(MIC_GATE_OFFSET_STORAGE_KEY, String(clamped));
    } catch (_) {}
  }, []);

  // Tear down ALL state related to a single remote peer id.  Idempotent.
  // Used wherever we need to start fresh for a player (rejoin, failure, recreate).
  const cleanupPeer = (id) => {
    if (peersRef.current[id]) {
      try { peersRef.current[id].close(); } catch {}
      delete peersRef.current[id];
    }
    if (gainNodesRef.current[id]) {
      try { gainNodesRef.current[id].disconnect(); } catch {}
      delete gainNodesRef.current[id];
    }
    if (analyserNodesRef.current[id]) {
      try { analyserNodesRef.current[id].disconnect(); } catch {}
      delete analyserNodesRef.current[id];
    }
    if (audioElementsRef.current[id]) {
      try {
        audioElementsRef.current[id].srcObject = null;
        audioElementsRef.current[id].remove();
      } catch {}
      delete audioElementsRef.current[id];
    }
    if (animationStateRef.current[id]) {
      delete animationStateRef.current[id];
    }
    if (remoteInterpRef.current[id]) {
      delete remoteInterpRef.current[id];
    }
    if (playerVolumesRef.current[id] !== undefined) {
      delete playerVolumesRef.current[id];
    }
  };

  // Wire statechange on a freshly created AudioContext so we can swap between
  // the GainNode (boosted) output path and the <audio>-element fallback path.
  //
  // iOS suspends the AudioContext when the browser is minimized or screen-locked.
  // When that happens, the GainNode → audioCtx.destination chain goes silent.
  // We detect the suspension via 'statechange' and immediately unmute each player's
  // <audio> element (which iOS keeps alive at the OS level) so audio continues.
  // When iOS resumes the context, we re-mute the elements and let the GainNode
  // take over again — restoring the full 2.25× boost for foreground use.
  const setupAudioCtxListeners = (ctx) => {
    ctx.addEventListener('statechange', () => {
      const isRunning = ctx.state === 'running';
      Object.keys(audioElementsRef.current).forEach(id => {
        const el = audioElementsRef.current[id];
        if (!el) return;
        if (isRunning) {
          // GainNode is active — mute the fallback element to prevent double playback
          el.muted = true;
        } else {
          // AudioContext suspended/interrupted — unmute fallback element
          el.muted = false;
          el.volume = Math.min(Math.max(playerVolumesRef.current[id] ?? 1, 0), 1);
        }
      });
    });
  };

  // Initialize AudioContext
  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'playback' });
      setupAudioCtxListeners(ctx);
      audioCtxRef.current = ctx;
    }
    return audioCtxRef.current;
  };

  const startAudioContext = async () => {
    const ctx = getAudioCtx();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    setIsAudioContextStarted(true);
  };

  const releaseLocalMicGraph = () => {
    try { localMicSourceRef.current?.disconnect(); } catch {}
    try { localSpeechHighpassRef.current?.disconnect(); } catch {}
    try { localSpeechLowpassRef.current?.disconnect(); } catch {}
    try { localSpeechSinkRef.current?.disconnect(); } catch {}
    try { localGateGainRef.current?.disconnect(); } catch {}
    try { localAnalyserRef.current?.disconnect(); } catch {}
    localMicSourceRef.current = null;
    localSpeechHighpassRef.current = null;
    localSpeechLowpassRef.current = null;
    localSpeechSinkRef.current = null;
    localGateGainRef.current = null;
    localAnalyserRef.current = null;
    speechLevelScratchRef.current = null;
  };

  const readSpeechLevelDbRef = useRef(() => -80);
  readSpeechLevelDbRef.current = () => {
    const analyser = localAnalyserRef.current;
    if (!analyser) return -80;
    const n = analyser.fftSize;
    if (!speechLevelScratchRef.current || speechLevelScratchRef.current.length !== n) {
      speechLevelScratchRef.current = new Float32Array(n);
    }
    analyser.getFloatTimeDomainData(speechLevelScratchRef.current);
    return levelDbFromSamples(speechLevelScratchRef.current);
  };

  const writeMicGateGainRef = useRef(() => {});
  writeMicGateGainRef.current = (open) => {
    const gateGain = localGateGainRef.current;
    const audioCtx = audioCtxRef.current;
    if (!gateGain || !audioCtx) return;
    if (open === micGateOpenRef.current) return;
    micGateOpenRef.current = open;
    const now = audioCtx.currentTime;
    gateGain.gain.cancelScheduledValues(now);
    gateGain.gain.setValueAtTime(gateGain.gain.value, now);
    // Short attack / slightly longer release so word endings aren't chopped.
    gateGain.gain.setTargetAtTime(open ? 1 : 0, now, open ? 0.015 : 0.05);
  };

  const forceMicGateClosed = () => {
    stepMicGate(micGateStateRef.current, readSpeechLevelDbRef.current(), 0, {
      offsetDb: micGateOffsetRef.current,
      forceClosed: true
    });
    writeMicGateGainRef.current(false);
  };

  // Mic → speech-band meter, and mic → gateGain → peers.
  // The meter chain is silenced. Peers hear the full mic only while the gate is open.
  const setupGatedMicOutbound = (audioCtx, rawStream) => {
    releaseLocalMicGraph();
    micGateStateRef.current = createMicGateState();
    micGateOpenRef.current = false;
    micGateTickAtRef.current = 0;

    const source = audioCtx.createMediaStreamSource(rawStream);
    const highpass = audioCtx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = SPEECH_HIGHPASS_HZ;
    highpass.Q.value = 0.707;
    const lowpass = audioCtx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = SPEECH_LOWPASS_HZ;
    lowpass.Q.value = 0.707;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0;
    // Zero gain into the outbound stream keeps the meter running without
    // playing it through the speakers (that ducks the hall TV on iOS).
    const sink = audioCtx.createGain();
    sink.gain.value = 0;
    const gateGain = audioCtx.createGain();
    gateGain.gain.value = 0; // start closed — no breathing burst on join
    const dest = audioCtx.createMediaStreamDestination();

    source.connect(highpass);
    highpass.connect(lowpass);
    lowpass.connect(analyser);
    analyser.connect(sink);
    sink.connect(dest);

    source.connect(gateGain);
    gateGain.connect(dest);

    localMicSourceRef.current = source;
    localSpeechHighpassRef.current = highpass;
    localSpeechLowpassRef.current = lowpass;
    localSpeechSinkRef.current = sink;
    localAnalyserRef.current = analyser;
    localGateGainRef.current = gateGain;
    localMicRawStreamRef.current = rawStream;

    return dest.stream;
  };

  const toggleLocalMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        const newMutedState = !isLocalMuted;
        const hallMuted = !!hallMembership?.isMuted;
        // Stay silent while a hall moderator has you muted.
        audioTrack.enabled = !newMutedState && !hallMuted;
        setIsLocalMuted(newMutedState);

        if (newMutedState || hallMuted) {
          forceMicGateClosed();
        }

        if (socket) {
          socket.emit('self-muted', { isMuted: newMutedState, hallSlug: hallSlugRef.current });
        }
      }
    }
  };

  // Keep a stable ref so the browser-extension hotkey bridge always calls the latest mute toggle.
  const toggleLocalMuteRef = useRef(toggleLocalMute);
  toggleLocalMuteRef.current = toggleLocalMute;

  // Courtesy: if staff hall-mutes you, disable the outbound gated track and close
  // the noise gate. Listeners also zero your volume from hallModeration.
  useEffect(() => {
    const silenced = isLocalMuted || !!hallMembership?.isMuted;
    const stream = localStream || localStreamRef.current;
    stream?.getAudioTracks?.().forEach((track) => {
      track.enabled = !silenced;
    });
    if (silenced) forceMicGateClosed();
  }, [isLocalMuted, hallMembership?.isMuted, localStream]);

  // Browser extension / external hotkey bridge (content script dispatches this event).
  useEffect(() => {
    const onToggleMute = () => {
      toggleLocalMuteRef.current?.();
    };
    window.addEventListener('bigplays:toggle-mute', onToggleMute);
    return () => window.removeEventListener('bigplays:toggle-mute', onToggleMute);
  }, []);

  // Full audio graph teardown — stops mic tracks, closes AudioContext, disconnects all Web Audio
  // nodes, detaches all audio elements, and closes all RTCPeerConnections. This fully releases
  // the iOS AVAudioSession so it no longer ducks the YouTube embedded video volume.
  const disableVoice = () => {
    // 1. Stop raw mic tracks (gated outbound tracks are derived from AudioContext)
    const raw = localMicRawStreamRef.current;
    if (raw) raw.getTracks().forEach(t => t.stop());
    localMicRawStreamRef.current = null;
    const stream = localStreamRef.current;
    if (stream) stream.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;

    releaseLocalMicGraph();
    micGateOpenRef.current = false;
    micGateStateRef.current = createMicGateState();
    micGateTickAtRef.current = 0;

    // 2. Close AudioContext entirely (suspend is not enough — iOS keeps session for suspended ctx)
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    localAnalyserRef.current = null;

    // 3. Disconnect and clear all Web Audio nodes
    Object.values(gainNodesRef.current).forEach(n => { try { n.disconnect(); } catch {} });
    gainNodesRef.current = {};
    Object.values(analyserNodesRef.current).forEach(n => { try { n.disconnect(); } catch {} });
    analyserNodesRef.current = {};

    // 4. Detach and remove all remote audio elements from the DOM.
    // Calling .remove() is important — leaving orphaned elements with srcObject=null
    // in the DOM causes them to accumulate across toggle cycles and can confuse
    // the statechange handler which iterates audioElementsRef.
    Object.values(audioElementsRef.current).forEach(el => {
      el.srcObject = null;
      try { el.remove(); } catch {}
    });
    audioElementsRef.current = {};

    // 5. Close all RTCPeerConnections — remote sides will detect disconnection and clean up too
    Object.values(peersRef.current).forEach(pc => { try { pc.close(); } catch {} });
    peersRef.current = {};

    socketRef.current?.emit('hall-voice', { voiceOn: false });
    setLocalStream(null);
    setIsMicEnabled(false);
    setIsLocalMuted(false);
    setIsVoiceActive(false);
    setIsAudioContextStarted(false);
    setIsLocalTalking(false);
    setTalkingPlayers({});

    // Force iOS to re-evaluate audio routing now that the audio session is released.
    // Muting then unmuting the YouTube player causes iOS to apply the correct (un-ducked) volume.
    setTimeout(() => {
      const player = ytPlayerRef.current;
      if (player?.mute) {
        player.mute();
        setTimeout(() => { player.unMute?.(); }, 300);
      } else {
        tvIframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'mute', args: [] }), '*'
        );
        setTimeout(() => {
          tvIframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'unMute', args: [] }), '*'
          );
        }, 300);
      }
    }, 150);
  };

  // Full voice re-initialization. Creates a fresh AudioContext and mic stream, then waits briefly
  // for remote peers to detect our closed connections before sending new WebRTC offers to all
  // current players so two-way audio re-establishes cleanly.
  const enableVoice = async () => {
    try {
      const rawStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      });

      // Create a fresh AudioContext (old one was closed)
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'playback' });
      setupAudioCtxListeners(audioCtx);
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') await audioCtx.resume().catch(() => {});
      setIsAudioContextStarted(true);

      // Gated outbound stream — peers only hear audio above the noise gate
      const outboundStream = setupGatedMicOutbound(audioCtx, rawStream);
      localStreamRef.current = outboundStream;
      setLocalStream(outboundStream);
      setIsMicEnabled(true);
      setIsVoiceActive(true);
      socketRef.current?.emit('hall-voice', { voiceOn: true });

      // Brief pause so remote peers can detect our closed connections and run
      // cleanupPeer before receiving our fresh offers (avoids glare conflicts).
      // 200 ms is sufficient with our 5 s ping interval and connection-state
      // failure handlers that trigger cleanup almost immediately on disconnection.
      await new Promise(r => setTimeout(r, 200));

      syncVoicePeersRef.current();
    } catch (err) {
      console.error('Error re-enabling voice:', err);
    }
  };

  // Create a peer connection for a remote player character
  const createPeer = (targetId, currentSocket, stream) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun.cloudflare.com:3478' },
      ]
    });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        currentSocket.emit('webrtc-ice-candidate', {
          candidate: event.candidate,
          to: targetId,
          hallSlug: hallSlugRef.current
        });
      }
    };

    // Queue for ICE candidates that arrive before remote description
    pc.iceCandidatesQueue = [];

    pc.ontrack = (event) => {
      const remoteStream = event.streams[0];
      console.log(`Received remote track from ${targetId}`);

      const audioCtx = getAudioCtx();

      // Fallback <audio> element — carries the raw WebRTC stream.
      // Normally MUTED (GainNode chain handles output with 2.25× boost).
      // When iOS suspends the AudioContext (browser minimized / screen locked),
      // the 'statechange' handler unmutes this element so audio continues in
      // background.  When the context resumes it is re-muted and GainNode takes over.
      let audioEl = audioElementsRef.current[targetId];
      if (!audioEl) {
        audioEl = document.createElement('audio');
        audioEl.id = `remote-audio-${targetId}`;
        audioEl.style.display = 'none';
        audioEl.autoplay = true;
        document.body.appendChild(audioEl);
        audioElementsRef.current[targetId] = audioEl;
      }
      audioEl.srcObject = remoteStream;
      // Always start muted. The statechange handler sets the correct state once
      // the AudioContext has settled.  Starting unmuted when the context is already
      // 'running' risks a brief window where both the GainNode chain AND the audio
      // element are producing output simultaneously (the source of perceived echo).
      audioEl.muted = true;
      // Immediately correct to fallback mode if the context is already suspended.
      if (audioCtx.state !== 'running') {
        audioEl.muted = false;
        audioEl.volume = 0; // proximity useEffect will set the real level
      }
      audioEl.play().catch(err => console.warn(`Audio play failed for ${targetId}:`, err));

      // Primary (boosted) output path: source → analyser → GainNode → destination.
      if (gainNodesRef.current[targetId]) {
        try { gainNodesRef.current[targetId].disconnect(); } catch {}
        delete gainNodesRef.current[targetId];
      }

      const source = audioCtx.createMediaStreamSource(remoteStream);
      const gainNode = audioCtx.createGain();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;

      gainNode.gain.value = 0; // Start muted; proximity useEffect sets the real level
      source.connect(analyser);
      analyser.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      gainNodesRef.current[targetId] = gainNode;
      analyserNodesRef.current[targetId] = analyser;
      console.log(`✓ Audio chain ready for ${targetId}, triggering proximity update`);
      setAudioReadyTrigger(prev => prev + 1);
    };

    // Detect failures from BOTH connectionState and iceConnectionState — different
    // browsers fire one or the other first.  When detected, fully tear down and
    // attempt to recreate (only if the player is still in the hall).
    const handleFailure = (reason) => {
      console.warn(`Peer ${targetId} ${reason}, cleaning up and attempting reconnect`);
      cleanupPeer(targetId);
      setTimeout(() => {
        if (!socketRef.current || !socketRef.current.connected) return;
        if (!playersRef.current[targetId]) return;
        syncVoicePeersRef.current();
      }, 1500);
    };

    pc.onconnectionstatechange = () => {
      console.log(`Connection state with ${targetId}: ${pc.connectionState}`);
      if (pc.connectionState === 'failed') handleFailure('connection failed');
      else if (pc.connectionState === 'disconnected') {
        // Give it 5s to recover on its own before tearing down
        setTimeout(() => {
          if (peersRef.current[targetId] === pc &&
              (pc.connectionState === 'disconnected' || pc.connectionState === 'failed')) {
            handleFailure('disconnected timeout');
          }
        }, 5000);
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed') handleFailure('ICE failed');
    };

    if (stream) {
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
    }

    return pc;
  };

  const collisionOptions = () => (
    stageAccessRef.current ? { ignoreNames: STAGE_STAIR_NAMES } : {}
  );

  const voicePairAllowed = (ax, ay, bx, by) => {
    const teamSeats = mapDataRef.current?.teamSeats;
    const creatorHall = isCreatorHall(hallMetaRef.current);
    return shouldVoiceConnect(
      voiceGroup({ x: ax, y: ay, teamSeats, sitY: TEAM_SEAT_SIT_Y }),
      voiceGroup({ x: bx, y: by, teamSeats, sitY: TEAM_SEAT_SIT_Y }),
      creatorHall
    );
  };

  syncVoicePeersRef.current = (overridePos) => {
    const sock = socketRef.current;
    if (!sock) return;
    const myPos = overridePos || myPositionRef.current;
    const roster = playersRef.current || {};
    const wanted = new Set();
    Object.values(roster).forEach((player) => {
      if (!player?.id) return;
      if (!voicePairAllowed(myPos.x, myPos.y, player.x, player.y)) return;
      wanted.add(String(player.id));
    });

    Object.keys(peersRef.current).forEach((id) => {
      if (!wanted.has(String(id))) cleanupPeer(id);
    });

    wanted.forEach((id) => {
      if (peersRef.current[id] || connectingPeersRef.current.has(id)) return;
      connectingPeersRef.current.add(id);
      (async () => {
        try {
          if (peersRef.current[id]) return;
          const pc = createPeer(id, sock, localStreamRef.current);
          peersRef.current[id] = pc;
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          sock.emit('webrtc-offer', { offer, to: id, hallSlug: hallSlugRef.current });
        } catch (err) {
          console.error(`Error creating offer for ${id}:`, err);
          cleanupPeer(id);
        } finally {
          connectingPeersRef.current.delete(id);
        }
      })();
    });
  };

  // Connect to socket only after we know guest vs signed-in identity.
  useEffect(() => {
    if (!authReady) return undefined;

    let currentSocket;
    let localMicStream;
    let isMounted = true;

    // Disconnect cleanly when the tab/window is TRULY closed so the server
    // removes the player immediately instead of waiting for ping timeout.
    //
    // Strategy:
    //  • beforeunload      – desktop browsers (tab/window close, navigation)
    //  • pagehide(persisted=false) – iOS Safari / mobile real close
    //    (persisted=true means the browser cached the page in bfcache and may
    //     restore it — do NOT disconnect in that case or we ghost ourselves)
    //  • visibilitychange  – used ONLY for returning-to-tab re-sync, NOT to
    //    disconnect.  Voice chat must keep running when the user alt-tabs to
    //    a game; evicting on hidden would break background voice completely.
    const dropSocket = (sock) => {
      if (!sock) return;
      try { sock.emit('leave-hall'); } catch (_) { /* already gone */ }
      if (sock.io) sock.io.off('reconnect');
      sock.disconnect();
    };
    const handleBeforeUnload = () => { dropSocket(currentSocket); };
    const handlePageHide = (e) => {
      if (!e.persisted) dropSocket(currentSocket);
    };
    // On visibilitychange:visible, resume the AudioContext if iOS suspended it.
    //
    // iOS suspends the AudioContext when the browser is minimized or the screen
    // locks.  The RTCPeerConnection keeps streaming at the OS level (which is why
    // the mic keeps broadcasting), but the JS AudioContext audio graph freezes so
    // incoming audio is silently dropped.  Resuming it restores receive audio
    // without touching any socket or WebRTC connections.
    //
    // Desktop: AudioContext is never suspended on tab switch — resume() is a no-op.
    // Mobile reconnect: If the socket dropped, socket.io auto-reconnect fires the
    //   'reconnect' event which already calls join-hall and re-establishes peers.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const ctx = audioCtxRef.current;
        if (ctx && ctx.state === 'suspended') {
          ctx.resume().catch(() => {});
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide',     handlePageHide);
    document.addEventListener('visibilitychange', handleVisibility);

    const init = async () => {
      // Request microphone access
      try {
        const rawStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,  // reduces iOS VoIP audio session ducking
            noiseSuppression: false,
            autoGainControl: false,   // stops dynamic gain dips during voice
          }
        });
        if (!isMounted) {
          rawStream.getTracks().forEach(track => track.stop());
          return;
        }
        localMicStream = rawStream;

        // Mic grant is a user gesture — resume AudioContext immediately so
        // proximity audio works without needing a separate canvas interaction.
        const audioCtx = getAudioCtx();
        if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => { });
        setIsAudioContextStarted(true);

        // Keep the iOS audio session alive in the background so incoming
        // WebRTC audio continues to play when the browser is minimized.
        // Outbound audio is noise-gated so breathing / room noise isn't broadcast.
        const outboundStream = setupGatedMicOutbound(audioCtx, rawStream);
        localStreamRef.current = outboundStream;
        setLocalStream(outboundStream);
        setIsMicEnabled(true);
        setIsVoiceActive(true);
      } catch (err) {
        console.error('Error accessing microphone:', err);
      }

      if (!isMounted) return;

      // Check if AudioContext is already running
      if (audioCtxRef.current?.state === 'running') {
        setIsAudioContextStarted(true);
      }

      // Connect to the same host as the client. forceNew avoids reusing a
      // Manager that still has a Guest handshake after login.
      currentSocket = io('/', {
        path: '/socket.io',
        forceNew: true,
        query: {
          hallSlug,
          userId: user?.userId || 'guest',
          username: user?.username || 'Guest'
        }
      });
      setSocket(currentSocket);
      socketRef.current = currentSocket;

      // 1. Set up all listeners first
      currentSocket.on('current-players', ({
        players: currentPlayers,
        yourPlayerId,
        membership,
        roomType,
        ownerUserId,
        displayName,
        hallSlug: joinedSlug
      }) => {
        myPlayerIdRef.current = yourPlayerId;
        const meta = {
          slug: joinedSlug || hallSlugRef.current,
          roomType: roomType || null,
          ownerUserId: ownerUserId || null,
          displayName: displayName || null
        };
        hallMetaRef.current = meta;
        setHallMeta(meta);
        if (membership !== undefined) {
          hallMembershipRef.current = membership;
          setHallMembership(membership);
        }
        stageAccessRef.current = hasStageAccess({
          isCreatorHall: isCreatorHall(meta) || isCreatorHall({ slug: hallSlugRef.current }),
          membership,
          user,
          ownerUserId: meta.ownerUserId
        });
        hallKickedRef.current = null;
        setHallKicked(null);
        const nextMod = {};
        Object.values(currentPlayers || {}).forEach((p) => {
          if (!p?.id) return;
          nextMod[String(p.id)] = {
            hallMuted: !!p.hallMuted,
            timedOut: !!p.timedOut,
            timeoutExpiresAt: p.timeoutExpiresAt || null
          };
          if (p.userId != null && String(p.userId) !== String(p.id)) {
            nextMod[String(p.userId)] = nextMod[String(p.id)];
          }
        });
        hallModerationRef.current = nextMod;
        setHallModeration(nextMod);

        // Filter out our own character from the remote players state
        const otherPlayers = { ...currentPlayers };
        if (yourPlayerId && otherPlayers[yourPlayerId]) {
          // Inherit the server's stored position (where this account last was).
          // If the map is already loaded (race condition: map loaded before this event fired),
          // validate the position immediately so we don't land in a collision zone.
          // If the map hasn't loaded yet, the map-load effect will handle the check.
          const serverPos = { x: otherPlayers[yourPlayerId].x, y: otherPlayers[yourPlayerId].y };
          const currentMap = mapDataRef.current;
          if (currentMap) {
            const safePos = isWalkable(serverPos.x, serverPos.y, currentMap, collisionOptions())
              ? serverPos
              : (() => {
                console.warn(`current-players: server pos (${serverPos.x}, ${serverPos.y}) in collision — using safe spawn`);
                const spawns = currentMap.spawnPoints;
                return spawns[Math.floor(Math.random() * spawns.length)];
              })();
            setMyPosition(safePos);
            // Correct the server if we had to move
            if (safePos !== serverPos) {
              setTimeout(() => {
                emitMoveRef.current(safePos.x, safePos.y, 'idle', true);
              }, 500);
            }
          } else {
            // Map not loaded yet — set position as-is; map-load effect will validate it
            setMyPosition(serverPos);
          }
          delete otherPlayers[yourPlayerId];
        }
        Object.values(otherPlayers).forEach(seedRemotePose);
        setPlayers(otherPlayers);

        // PRUNE: any peer/audio state we still hold for IDs NOT in the new roster
        // is stale (the player left while we were disconnected / re-joining).
        const validIds = new Set(Object.keys(otherPlayers));
        Object.keys(peersRef.current).forEach(id => {
          if (!validIds.has(id)) cleanupPeer(id);
        });
        Object.keys(otherPlayers).forEach((id) => cleanupPeer(id));
        setTimeout(() => syncVoicePeersRef.current(), 0);
      });

      currentSocket.on('hall-announce', (payload) => {
        if (!localStreamRef.current) return;
        playHallAnnouncement(payload, audioCtxRef.current);
      });

      currentSocket.on('new-player', async (player) => {
        // If it's us, skip
        if (String(player.id) === String(user?.userId) || String(player.id) === String(myPlayerIdRef.current)) return;

        setPlayers((prev) => ({ ...prev, [player.id]: player }));
        seedRemotePose(player);
        if (player?.id) {
          const flags = {
            hallMuted: !!player.hallMuted,
            timedOut: !!player.timedOut,
            timeoutExpiresAt: player.timeoutExpiresAt || null
          };
          setHallModeration((prev) => {
            const next = {
              ...prev,
              [String(player.id)]: flags
            };
            if (player.userId != null) next[String(player.userId)] = flags;
            hallModerationRef.current = next;
            return next;
          });
        }

        cleanupPeer(player.id);
        setTimeout(() => syncVoicePeersRef.current(), 0);
      });

      currentSocket.on('webrtc-offer', async ({ offer, from, to }) => {
        // Filter messages not intended for us
        if (String(to) !== String(myPlayerIdRef.current) && String(to) !== String(user?.userId)) return;

        const me = myPositionRef.current;
        const them = playersRef.current[from];
        if (!them || !voicePairAllowed(me.x, me.y, them.x, them.y)) return;

        // Glare resolution: both sides sent an offer simultaneously.
        // The peer with the LOWER id string is "impolite" (caller) and keeps its offer.
        // The peer with the HIGHER id string is "polite" (callee) and defers to the incoming offer.
        if (peersRef.current[from]) {
          const myId = String(myPlayerIdRef.current || user?.userId || '');
          const theirId = String(from);
          if (myId < theirId) {
            // We're impolite — our outgoing offer takes priority; ignore theirs.
            console.log(`WebRTC glare (impolite): ignoring offer from ${from}`);
            return;
          }
          // We're polite — roll back our offer and accept theirs instead.
          console.log(`WebRTC glare (polite): closing our outgoing peer for ${from}, accepting their offer`);
          peersRef.current[from].close();
          delete peersRef.current[from];
        }

        const pc = createPeer(from, currentSocket, localStreamRef.current);
        peersRef.current[from] = pc;

        try {
          await pc.setRemoteDescription(new RTCSessionDescription(offer));

          // Process queued ICE candidates
          if (pc.iceCandidatesQueue) {
            while (pc.iceCandidatesQueue.length > 0) {
              const cand = pc.iceCandidatesQueue.shift();
              await pc.addIceCandidate(new RTCIceCandidate(cand)).catch(e => console.error("Error adding queued candidate:", e));
            }
          }

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          currentSocket.emit('webrtc-answer', { answer, to: from, hallSlug: hallSlugRef.current });
        } catch (err) {
          console.error("Error handling WebRTC offer:", err);
          // Clean up failed peer connection
          if (peersRef.current[from] === pc) {
            pc.close();
            delete peersRef.current[from];
          }
        }
      });

      currentSocket.on('webrtc-answer', async ({ answer, from, to }) => {
        // Filter messages not intended for us
        if (String(to) !== String(myPlayerIdRef.current) && String(to) !== String(user?.userId)) return;

        const pc = peersRef.current[from];
        if (pc && pc.signalingState === 'have-local-offer') {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(answer));

            // Process queued ICE candidates
            if (pc.iceCandidatesQueue) {
              while (pc.iceCandidatesQueue.length > 0) {
                const cand = pc.iceCandidatesQueue.shift();
                await pc.addIceCandidate(new RTCIceCandidate(cand)).catch(e => console.error("Error adding queued candidate:", e));
              }
            }
          } catch (err) {
            console.error("Error handling WebRTC answer:", err);
          }
        }
      });

      currentSocket.on('webrtc-ice-candidate', async ({ candidate, from, to }) => {
        // Filter messages not intended for us
        if (String(to) !== String(myPlayerIdRef.current) && String(to) !== String(user?.userId)) return;

        const pc = peersRef.current[from];
        if (pc) {
          if (pc.remoteDescription && pc.remoteDescription.type) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(e => console.error("Error adding ICE candidate:", e));
          } else {
            // Queue candidate until remote description is set
            if (!pc.iceCandidatesQueue) pc.iceCandidatesQueue = [];
            pc.iceCandidatesQueue.push(candidate);
          }
        }
      });

      currentSocket.on('hall-snapshot', (data) => {
        if (!data) return;
        const now = Date.now();
        if (typeof data.t === 'number') {
          snapshotClockOffsetRef.current = applyClockOffset(
            snapshotClockOffsetRef.current,
            data.t,
            now
          );
        }
        const offset = snapshotClockOffsetRef.current || 0;
        const interp = remoteInterpRef.current;
        const roster = playersRef.current;
        (data.players || []).forEach((row) => {
          if (!row || row.id == null) return;
          const packed = row.t != null ? row : { ...row, t: data.t };
          interp[row.id] = applySnapshotPose(interp[row.id], packed, now, offset);
          if (roster[row.id]) {
            roster[row.id] = {
              ...roster[row.id],
              x: row.x,
              y: row.y,
              moveState: intToMoveState(row.s)
            };
          }
        });
        if (data.you && data.you.from && data.you.from !== currentSocket.id) {
          const keys = keysPressed.current;
          const walking = !!(
            keys['w'] || keys['a'] || keys['s'] || keys['d'] || dragTargetRef.current
          );
          if (!walking && !isSittingRef.current) {
            setMyPosition({ x: data.you.x, y: data.you.y });
          }
        }
        applyProximityVoiceGainsRef.current();
        syncVoicePeersRef.current();
      });

      currentSocket.on('player-self-muted', ({ playerId, isMuted }) => {
        // Update self-muted status for this player
        console.log('Received player-self-muted:', { playerId, isMuted });
        setSelfMutedPlayers((prev) => {
          const updated = {
            ...prev,
            [playerId]: isMuted
          };
          console.log('Updated selfMutedPlayers:', updated);
          return updated;
        });
      });

      currentSocket.on('player-disconnected', (id) => {
        if (String(id) === String(user?.userId) || String(id) === String(myPlayerIdRef.current)) return;

        setPlayers((prev) => {
          const newPlayers = { ...prev };
          delete newPlayers[id];
          return newPlayers;
        });

        cleanupPeer(id);
      });

      // 2. Finally connect and join
      currentSocket.on('connect', () => {
        console.log('Connected to LiveWorld socket');
        currentSocket.emit('join-hall', { slug: hallSlugRef.current, voiceOn: !!localStreamRef.current });
      });

      // Hall-level moderation / denial events
      currentSocket.on('hall-denied', ({ slug, reason, timeoutExpiresAt }) => {
        console.warn('Hall join denied:', slug, reason);
        const next = { reason: reason || 'denied', timeoutExpiresAt };
        hallKickedRef.current = next;
        setHallKicked(next);
        setPlayers({});
        remoteInterpRef.current = {};
        snapshotClockOffsetRef.current = null;
        chatBubblesRef.current = {};
        chatBubbleHitsRef.current = [];
      });
      currentSocket.on('hall-kicked', ({ hallSlug: s, reason, timeoutExpiresAt }) => {
        console.warn('Kicked from hall:', s, reason);
        const next = { reason: reason || 'kicked', timeoutExpiresAt };
        hallKickedRef.current = next;
        setHallKicked(next);
        setPlayers({});
        remoteInterpRef.current = {};
        snapshotClockOffsetRef.current = null;
        chatBubblesRef.current = {};
        chatBubbleHitsRef.current = [];
        Object.keys(peersRef.current).forEach(id => cleanupPeer(id));
        Object.keys(gainNodesRef.current).forEach(id => cleanupPeer(id));
        Object.keys(analyserNodesRef.current).forEach(id => cleanupPeer(id));
        Object.keys(audioElementsRef.current).forEach(id => cleanupPeer(id));
      });
      currentSocket.on('hall-member-updated', ({ hallSlug: s, userId, membership }) => {
        if (!s || s.toLowerCase() !== hallSlugRef.current?.toLowerCase()) return;
        const flags = flagsFromMembership(membership);
        const keys = [userId, membership?.userId].filter((k) => k != null).map(String);
        setHallModeration((prev) => {
          const next = { ...prev };
          keys.forEach((key) => { next[key] = flags; });
          hallModerationRef.current = next;
          return next;
        });
        setPlayers((prev) => {
          let changed = false;
          const next = { ...prev };
          keys.forEach((key) => {
            if (!next[key]) return;
            next[key] = { ...next[key], isVip: flags.isVip };
            changed = true;
          });
          return changed ? next : prev;
        });
        const myId = myPlayerIdRef.current;
        const isMe = keys.some((key) =>
          key === String(user?.userId) || key === String(myId)
        );
        if (isMe) {
          setHallMembership(membership);
          hallMembershipRef.current = membership;
          stageAccessRef.current = hasStageAccess({
            isCreatorHall: isCreatorHall(hallMetaRef.current)
              || isCreatorHall({ slug: hallSlugRef.current }),
            membership,
            user,
            ownerUserId: hallMetaRef.current.ownerUserId
          });
        }
      });

      currentSocket.on('hall-force-sit', ({ hallSlug: s, playerId, team, x, y }) => {
        if (!s || s.toLowerCase() !== hallSlugRef.current?.toLowerCase()) return;
        if (String(playerId) !== String(myPlayerIdRef.current) && String(playerId) !== String(user?.userId)) {
          return;
        }
        const seats = mapDataRef.current?.teamSeats || [];
        const seat = seats.find((item) => item.team === team && Math.abs(item.cx - x) < 4)
          || { team, cx: x, cy: y, x: x - 23, y: y - 20, width: 46, height: 32 };
        applyTeamSeatSitRef.current(seat);
      });

      currentSocket.on('hall-team-seat-result', (result) => {
        if (!result || result.ok) return;
        if (result.reason === 'full') {
          alert(`Team ${result.team} LAN seats are full`);
        }
      });
      currentSocket.on('hall-deprecated', () => { /* transition log only */ });

      currentSocket.on('hall-chat-history', ({ hallSlug: s, messages, hasMore }) => {
        if (s && s.toLowerCase() !== hallSlugRef.current?.toLowerCase()) return;
        const list = Array.isArray(messages) ? messages : [];
        chatMessagesRef.current = list;
        setChatMessages(list);
        setChatHasMore(!!hasMore);
      });
      currentSocket.on('hall-chat-message', (dto) => {
        if (!dto?.id) return;
        const next = chatMessagesRef.current.some((m) => m.id === dto.id)
          ? chatMessagesRef.current
          : [...chatMessagesRef.current, dto];
        chatMessagesRef.current = next;
        setChatMessages(next);
        setChatLiveMessage(dto);
        rememberChatBubble(chatBubblesRef.current, dto, Date.now());
      });
      currentSocket.on('hall-chat-updated', (dto) => {
        if (!dto?.id) return;
        const next = chatMessagesRef.current.map((m) => (m.id === dto.id ? dto : m));
        chatMessagesRef.current = next;
        setChatMessages(next);
      });
      currentSocket.on('hall-chat-deleted', ({ messageId }) => {
        const next = chatMessagesRef.current.filter((m) => m.id !== messageId);
        chatMessagesRef.current = next;
        setChatMessages(next);
        forgetChatBubble(chatBubblesRef.current, messageId);
      });
      currentSocket.on('hall-chat-denied', ({ reason }) => {
        if (reason === 'guest-limit') {
          setGuestHallChatCount(GUEST_HALL_CHAT_LIMIT);
          requestSignInPrompt('guest-chat');
        }
        if (reason === 'content-policy') {
          if (!isAuthProp) {
            setGuestHallChatCount(Math.max(0, getGuestHallChatCount() - 1));
          }
          setChatPolicyDenied(true);
        }
      });

      // Handle reconnection - in socket.io-client v4, reconnect event is on the Manager
      currentSocket.io.on('reconnect', () => {
        console.log('Reconnected to LiveWorld socket — full peer reset');
        // Tear down ALL peer/audio state — server has likely lost track of our state too.
        // Fresh current-players response will trigger a clean re-handshake with everyone.
        Object.keys(peersRef.current).forEach(id => cleanupPeer(id));
        // Belt-and-suspenders: also clear any orphaned entries in the other refs
        Object.keys(gainNodesRef.current).forEach(id => cleanupPeer(id));
        Object.keys(analyserNodesRef.current).forEach(id => cleanupPeer(id));
        Object.keys(audioElementsRef.current).forEach(id => cleanupPeer(id));
        animationStateRef.current = {};
        if (!hallKickedRef.current) {
          currentSocket.emit('join-hall', { slug: hallSlugRef.current, voiceOn: !!localStreamRef.current });
        }
      });
    };

    init();

    return () => {
      isMounted = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide',     handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibility);
      dropSocket(currentSocket);

      // Cleanup all connections
      Object.values(peersRef.current).forEach(peer => peer.close());
      Object.values(gainNodesRef.current).forEach(node => node.disconnect());
      Object.values(audioElementsRef.current).forEach(el => {
        el.srcObject = null;
        el.remove();
      });

      if (localMicStream) {
        localMicStream.getTracks().forEach(track => track.stop());
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
      releaseLocalMicGraph();
      localMicRawStreamRef.current = null;
      micGateOpenRef.current = false;
      micGateStateRef.current = createMicGateState();
      micGateTickAtRef.current = 0;
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, [authReady, hallSlug, user?.userId, user?.username]);

  // Load map data
  useEffect(() => {
    setMapLoading(true);
    loadMap().then(data => {
      mapDataRef.current = data; // keep ref current so socket handlers can use it without stale closure
      setMapData(data);
      setMapLoading(false);
      // Validate current position (may have been set by server before map finished loading).
      // Teleport to a safe spawn if inside a collision zone.
      // Then immediately announce the final position to the server so all other devices sync.
      setMyPosition(prev => {
        const safePos = isWalkable(prev.x, prev.y, data, collisionOptions())
          ? prev
          : (() => {
            console.warn(`Spawn (${prev.x}, ${prev.y}) inside collision — teleporting to safe spawn`);
            const spawns = data.spawnPoints;
            return spawns[Math.floor(Math.random() * spawns.length)];
          })();

        // Announce to server after a short delay (lets current-players and React settle first).
        // Uses refs so there is no stale-closure problem.
        setTimeout(() => {
          emitMoveRef.current(safePos.x, safePos.y, 'idle', true);
        }, 500);

        return safePos;
      });
    }).catch(err => {
      console.error('Failed to load map:', err);
      setMapError(err.message);
      setMapLoading(false);
      setMyPosition(DEFAULT_SPAWN);
    });
  }, []);

  // Load sprite sheet
  useEffect(() => {
    const loadSpriteSheet = async () => {
      const img = new Image();
      img.src = '/character-sprite-sheet.png';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load sprite sheet'));
      });

      console.log(`✓ Sprite sheet loaded: ${img.width}x${img.height}`);
      console.log(`  Frame size: ${spriteConfig.frameWidth}x${spriteConfig.frameHeight}px (10 columns × 17 rows)`);
      console.log(`  Render size: ${spriteConfig.renderWidth}x${spriteConfig.renderHeight}px (3x scale)`);
      console.log(`  Row 0 (idle): ${spriteConfig.idleFrames} frames | Row 1 (walk): ${spriteConfig.walkFrames} frames`);
      console.log(`  Collision calculated from bottom-center of sprite (character model: ${spriteConfig.characterWidth}x${spriteConfig.characterHeight}px)`);
      setSpriteSheet(img);
      setSpriteSheetLoaded(true);
    };

    loadSpriteSheet().catch(err => {
      console.error('❌ Sprite sheet loading error:', err);
      setSpriteSheetLoaded(false);
    });
  }, []);

  // Roster identity comes from React state; live x/y/moveState live on the ref
  // (updated by hall-snapshot + RAF interpolation). Do not clobber poses.
  useEffect(() => {
    const prev = playersRef.current || {};
    const next = {};
    Object.keys(players).forEach((id) => {
      const incoming = players[id];
      const pose = prev[id];
      next[id] = pose
        ? { ...incoming, x: pose.x, y: pose.y, moveState: pose.moveState }
        : incoming;
    });
    playersRef.current = next;
  }, [players]);

  // Periodic peer health check — every 5 seconds, scan all peer connections
  // and recreate any that are dead/dying.  Also prunes peers for players we
  // no longer have in our roster (defensive belt-and-suspenders).
  useEffect(() => {
    const interval = setInterval(() => {
      const sock = socketRef.current;
      if (!sock || !sock.connected) return;
      const knownPlayerIds = new Set(Object.keys(playersRef.current));

      Object.entries(peersRef.current).forEach(([id, pc]) => {
        // Player no longer in roster — tear down stale peer
        if (!knownPlayerIds.has(id)) {
          console.log(`Health check: pruning peer for departed player ${id}`);
          cleanupPeer(id);
          return;
        }
        // Peer is dead — recreate
        const isDead = pc.connectionState === 'failed' ||
                       pc.connectionState === 'closed' ||
                       pc.iceConnectionState === 'failed' ||
                       pc.iceConnectionState === 'closed';
        if (isDead) {
          console.warn(`Health check: peer ${id} is ${pc.connectionState}/${pc.iceConnectionState}, recreating`);
          cleanupPeer(id);
          (async () => {
            try {
              const newPc = createPeer(id, sock, localStreamRef.current);
              peersRef.current[id] = newPc;
              const offer = await newPc.createOffer();
              await newPc.setLocalDescription(offer);
              sock.emit('webrtc-offer', { offer, to: id, hallSlug: hallSlugRef.current });
            } catch (err) {
              console.error(`Health check recreate for ${id} failed:`, err);
            }
          })();
        }
      });

      // Also: any player in roster WITHOUT a peer connection — create one
      knownPlayerIds.forEach(id => {
        if (!peersRef.current[id] && localStreamRef.current) {
          console.log(`Health check: missing peer for player ${id}, creating offer`);
          (async () => {
            try {
              const pc = createPeer(id, sock, localStreamRef.current);
              peersRef.current[id] = pc;
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              sock.emit('webrtc-offer', { offer, to: id, hallSlug: hallSlugRef.current });
            } catch (err) {
              console.error(`Health check create for ${id} failed:`, err);
            }
          })();
        }
      });
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle proximity voice volume. Positions come from playersRef (snapshot
  // poses) so remote walkers still update gain at 10 Hz without setPlayers.
  applyProximityVoiceGainsRef.current = () => {
    if (!isAudioContextStarted || !audioCtxRef.current) {
      return;
    }

    const roster = playersRef.current || {};
    const myPos = myPositionRef.current;

    Object.keys(roster).forEach(id => {
      const player = roster[id];
      const gainNode = gainNodesRef.current[id];
      const audioEl = audioElementsRef.current[id];

      if (!gainNode && !audioEl) {
        return;
      }

      const creatorHall = isCreatorHall(hallMetaRef.current);
      const teamSeats = mapDataRef.current?.teamSeats;
      const myGroup = voiceGroup({
        x: myPos.x,
        y: myPos.y,
        teamSeats,
        sitY: TEAM_SEAT_SIT_Y
      });
      const remoteGroup = voiceGroup({
        x: player.x,
        y: player.y,
        teamSeats,
        sitY: TEAM_SEAT_SIT_Y
      });
      const boostedVolume = voiceGainFor(myGroup, remoteGroup, creatorHall, globalVolume);

      const pref = getPlayerAudioPref(playerAudio, id);
      const hallFlags = hallModeration[String(id)]
        || hallModeration[String(player.userId)]
        || {};
      const isMuted = pref.muted || selfMutedPlayers[id] || !!hallFlags.hallMuted;
      const finalVolume = isMuted ? 0 : boostedVolume * pref.volume;

      const normalizedVolume = Math.min(Math.max(finalVolume, 0), 1);
      playerVolumesRef.current[id] = normalizedVolume;

      if (gainNode && audioCtxRef.current) {
        const ctx = audioCtxRef.current;
        gainNode.gain.cancelScheduledValues(ctx.currentTime);
        gainNode.gain.setTargetAtTime(finalVolume, ctx.currentTime, 0.05);
      }

      if (audioEl && audioCtxRef.current?.state !== 'running') {
        audioEl.volume = normalizedVolume;
      }
    });
  };

  useEffect(() => {
    applyProximityVoiceGainsRef.current();
    syncVoicePeersRef.current();
  }, [players, myPosition, mapData, audioReadyTrigger, isAudioContextStarted, globalVolume, playerAudio, selfMutedPlayers, hallModeration, hallMeta, hallMembership]);

  // Handle keyboard movement with smooth continuous movement and diagonal support
  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      const movementKeys = ['w', 'a', 's', 'd', 'W', 'A', 'S', 'D', 'Shift'];
      const isMovementKey = movementKeys.includes(e.key);
      const isInteractKey = e.key === 'e' || e.key === 'E';
      const isSeekKey = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      if (!isMovementKey && !isInteractKey && !isSeekKey) return;

      e.preventDefault();

      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => { });
        setIsAudioContextStarted(true);
      }

      // Hand focus to the TV so YouTube's own skip UI runs. Do not seekTo —
      // that flashes the full player chrome. Do not stand up.
      if (isSeekKey) {
        focusTvPlayer();
        return;
      }

      // WASD / E: take keys back from the YouTube iframe, then sit/walk as usual.
      focusHall();
      if (isTvExpandedRef.current) setIsTvExpanded(false);

      // E key: sit down or stand up
      if (isInteractKey) {
        if (isSittingRef.current) {
          standUp();
        } else if (nearSeatRef.current) {
          sitDown(nearSeatRef.current, playersRef.current);
        }
        return;
      }

      // Movement keys while sitting: stand up, then register key so movement starts immediately
      if (isSittingRef.current) {
        standUp();
      }

      keysPressed.current[e.key.toLowerCase()] = true;
    };

    const handleKeyUp = (e) => {
      const movementKeys = ['w', 'a', 's', 'd', 'W', 'A', 'S', 'D', 'Shift'];
      if (!movementKeys.includes(e.key)) return;
      keysPressed.current[e.key.toLowerCase()] = false;
    };

    // Movement game loop - runs every animation frame for both WASD and drag.
    // All speeds are in world-pixels/second so they are frame-rate independent.
    const WALK_PX_PER_SEC = 180; // 3 px at 60 fps
    const RUN_PX_PER_SEC  = 360; // 6 px at 60 fps (2× walk)
    // Distance from player to drag target (world units) beyond which we run.
    // characterWidth (66) × 3 = 198 world units.
    const RUN_DRAG_THRESHOLD = 3 * 66;

    let lastMoveTime = performance.now();
    let wasMoving = false;

    const movePlayer = () => {
      if (!socket) {
        movementLoopRef.current = requestAnimationFrame(movePlayer);
        return;
      }

      // No movement while seated
      if (isSittingRef.current) {
        lastMoveTime = performance.now();
        wasMoving = false;
        movementLoopRef.current = requestAnimationFrame(movePlayer);
        return;
      }

      // Delta-time: cap at 100 ms to avoid huge jumps after tab background.
      const now = performance.now();
      const dt = Math.min(now - lastMoveTime, 100);
      lastMoveTime = now;

      const isShiftHeld = !!keysPressed.current['shift'];

      // Helper: apply collision + bounds, emit position + moveState, return new state
      const applyMove = (prev, newX, newY, moveState) => {
        if (mapData) {
          const r = resolveCollision(prev.x, prev.y, newX, newY, mapData, collisionOptions());
          newX = Math.max(30, Math.min(mapData.width - 30, r.x));
          newY = Math.max(30, Math.min(mapData.height - 30, r.y));
        } else {
          newX = Math.max(30, Math.min(1920 - 30, newX));
          newY = Math.max(30, Math.min(1920 - 30, newY));
        }
        if (newX !== prev.x || newY !== prev.y) {
          emitMoveRef.current(newX, newY, moveState, false);
          return { x: newX, y: newY };
        }
        return prev;
      };

      // ── WASD movement ──────────────────────────────────────────────────────
      const wasdMoveState = isShiftHeld ? 'run' : 'walk';
      const wasdSpeed = (isShiftHeld ? RUN_PX_PER_SEC : WALK_PX_PER_SEC) * (dt / 1000);
      let deltaX = 0;
      let deltaY = 0;
      if (keysPressed.current['w']) deltaY -= wasdSpeed;
      if (keysPressed.current['s']) deltaY += wasdSpeed;
      if (keysPressed.current['a']) deltaX -= wasdSpeed;
      if (keysPressed.current['d']) deltaX += wasdSpeed;

      const hasKeyMovement = deltaX !== 0 || deltaY !== 0;
      if (hasKeyMovement) {
        setMyPosition(prev => applyMove(prev, prev.x + deltaX, prev.y + deltaY, wasdMoveState));
      }

      // ── Drag / click-hold movement ─────────────────────────────────────────
      // Runs only when no WASD key is pressed to avoid conflicts.
      const dragTarget = dragTargetRef.current;
      let dragMoving = false;
      if (!hasKeyMovement && dragTarget) {
        // Use the animation state's last known position (one draw-frame behind)
        // to compute distance — accurate enough for a speed/animation decision.
        const myPos = animationStateRef.current['self']?.lastPosition || DEFAULT_SPAWN;
        const estDx = dragTarget.x - myPos.x;
        const estDy = dragTarget.y - myPos.y;
        const estDist = Math.sqrt(estDx * estDx + estDy * estDy);
        const dragIsRunning = estDist > RUN_DRAG_THRESHOLD;
        const dragMoveState = dragIsRunning ? 'run' : 'walk';
        const dragSpeed = (dragIsRunning ? RUN_PX_PER_SEC : WALK_PX_PER_SEC) * (dt / 1000);
        dragMoving = estDist >= dragSpeed;

        if (dragMoving) {
          setMyPosition(prev => {
            const mdx = dragTarget.x - prev.x;
            const mdy = dragTarget.y - prev.y;
            const distance = Math.sqrt(mdx * mdx + mdy * mdy);
            if (distance < dragSpeed) return prev; // already at target
            const ratio = dragSpeed / distance;
            return applyMove(prev, prev.x + mdx * ratio, prev.y + mdy * ratio, dragMoveState);
          });
        }
      }

      // Drive idle off actual input. Walk packets are emitted from the
      // setMyPosition updater, which React may run after this rAF callback,
      // so a movedThisFrame flag never flipped and remotes kept the last walk.
      const inputMoving = hasKeyMovement || dragMoving;
      if (inputMoving) {
        wasMoving = true;
      } else if (wasMoving) {
        wasMoving = false;
        const pos = myPositionRef.current;
        emitMoveRef.current(pos.x, pos.y, 'idle', false);
      }

      // Continue the loop
      movementLoopRef.current = requestAnimationFrame(movePlayer);
    };

    // Start the movement loop
    movementLoopRef.current = requestAnimationFrame(movePlayer);

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      clearTimeout(tvFocusTimerRef.current);

      // Stop the movement loop
      if (movementLoopRef.current) {
        cancelAnimationFrame(movementLoopRef.current);
      }

      // Clear pressed keys
      keysPressed.current = {};
    };
  }, [socket, hallSlug, mapData, sitDown, standUp]);

  // Handle all canvas interactions (Mouse & Touch)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !socket) return;

    const getCoords = (e) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches?.[0] || e.changedTouches?.[0];
      const clientX = touch ? touch.clientX : e.clientX;
      const clientY = touch ? touch.clientY : e.clientY;

      // Divide by scale to convert CSS pixels → logical world units.
      const sc = canvasScaleRef.current.scale || 1;
      const x = (clientX - rect.left) / sc;
      const y = (clientY - rect.top) / sc;

      return { x, y };
    };

    // Nametag + mute icon sit above the sprite; include them in hit-tests.
    const NAME_HIT_PAD = 40;

    const occupantOfTeamSeat = (seat) => {
      const sitY = TEAM_SEAT_SIT_Y;
      return Object.values(playersRef.current || {}).find((p) => {
        if (p.id === myPlayerIdRef.current) return false;
        const dx = p.x - seat.cx;
        const dy = p.y - sitY;
        return dx * dx + dy * dy < 70 * 70;
      }) || null;
    };

    const occupantOfAudienceSeat = (seat) => {
      const sitY = seat.y + Math.round(spriteConfig.renderHeight * 0.5);
      return Object.values(playersRef.current || {}).find((p) => {
        if (p.id === myPlayerIdRef.current) return false;
        const dx = p.x - seat.cx;
        const dy = p.y - sitY;
        return dx * dx + dy * dy < 50 * 50;
      }) || null;
    };

    const pointInSeat = (worldX, worldY, seat, pad) => (
      worldX >= seat.x - pad && worldX <= seat.x + seat.width + pad &&
      worldY >= seat.y - pad && worldY <= seat.y + seat.height + pad
    );

    // Hit-test a world-space point against the current players state.
    // Returns the closest player whose character (or nametag) contains the
    // point, or the occupant of a seat whose rect contains the point.
    // Used by tap-to-mute and right-click/long-press for the moderation menu.
    const playerAtWorld = (worldX, worldY, seatPad = 40) => {
      const charHalfWidth = spriteConfig.characterWidth / 2;
      const charHeight = spriteConfig.characterHeight;
      let hit = null;
      let closest = Infinity;
      Object.values(playersRef.current || {}).forEach(p => {
        if (p.id === myPlayerIdRef.current) return; // never target self
        const left = p.x - charHalfWidth;
        const right = p.x + charHalfWidth;
        const top = p.y - charHeight - NAME_HIT_PAD;
        const bottom = p.y;
        if (worldX >= left && worldX <= right && worldY >= top && worldY <= bottom) {
          const centerY = p.y - charHeight / 2;
          const dx = p.x - worldX;
          const dy = centerY - worldY;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < closest) { hit = p; closest = d; }
        }
      });
      if (hit) return hit;

      // Occupied seats swallow most of the visible avatar (clipped at the desk).
      // Treat a click on that seat as a click on the person sitting there.
      if (mapData?.teamSeats) {
        for (const seat of mapData.teamSeats) {
          if (pointInSeat(worldX, worldY, seat, seatPad)) {
            const occ = occupantOfTeamSeat(seat);
            if (occ) return occ;
          }
        }
      }
      if (mapData?.seats) {
        for (const seat of mapData.seats) {
          if (pointInSeat(worldX, worldY, seat, seatPad)) {
            const occ = occupantOfAudienceSeat(seat);
            if (occ) return occ;
          }
        }
      }
      return null;
    };

    const openAudioPanel = (player) => {
      if (!player?.id) return;
      setShowMicGatePanel(false);
      setMenuTarget(null);
      setAudioTarget({
        id: player.id,
        userId: player.userId,
        username: player.username,
        steamId64: player.steamId64,
        youtubeChannelId: player.youtubeChannelId,
        xp: player.xp,
        hallXp: player.hallXp,
        hallSlug,
        isVip: !!player.isVip
      });
    };

    const clearLongPress = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    const selfAtWorld = (worldX, worldY) => {
      const me = myPositionRef.current;
      const charHalfWidth = spriteConfig.characterWidth / 2;
      const charHeight = spriteConfig.characterHeight;
      return (
        worldX >= me.x - charHalfWidth &&
        worldX <= me.x + charHalfWidth &&
        worldY >= me.y - charHeight - NAME_HIT_PAD &&
        worldY <= me.y
      );
    };

    // Opens mic-gate panel for self, or HallMemberMenu for another player.
    const openMenuForTap = (tapX, tapY) => {
      const activeCam = isSittingRef.current ? seatedCameraRef.current : cameraRef.current;
      const worldX = mapData ? tapX + activeCam.x : tapX;
      const worldY = mapData ? tapY + activeCam.y : tapY;
      if (selfAtWorld(worldX, worldY)) {
        setAudioTarget(null);
        setShowMicGatePanel(true);
        return true;
      }
      if (!isAuthProp) return false;
      const player = playerAtWorld(worldX, worldY);
      if (!player || !player.username) return false;
      // Guests and pre-registration Steam users have no DB row, so moderation
      // actions aren't applicable. Skip opening the menu for them.
      const uid = String(player.userId || '');
      if (uid === 'guest' || uid.startsWith('steam-')) return false;
      setShowMicGatePanel(false);
      setAudioTarget(null);
      setMenuTarget({ userId: player.userId, username: player.username });
      return true;
    };

    const handleStart = (e) => {
      focusHall();

      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => { });
        setIsAudioContextStarted(true);
      }

      // Right-click: open moderation menu instead of starting a drag/tap.
      // handleContextMenu (attached below) also fires and suppresses the
      // browser's native menu; we just bail here so the mouseup handler
      // doesn't re-interpret this as a mute-toggle tap.
      if (!e.touches && e.button === 2) {
        interactionRef.current.isActive = false;
        return;
      }

      // Prevent default on touch to avoid scrolling/zooming on canvas
      if (e.touches && e.target === canvas) {
        e.preventDefault();
      }

      const { x, y } = getCoords(e);
      const isTouch = !!e.touches;
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;

      interactionRef.current = {
        startX: x,
        startY: y,
        startClientX: clientX,
        startClientY: clientY,
        isDragging: false,
        isActive: true,
        lastType: isTouch ? 'touch' : 'mouse',
        suppressTap: false // set true when long-press fires so mouseup doesn't toggle mute
      };

      // Start long-press timer (touch only) — if the finger stays put for
      // ~600ms, open the moderation menu for the player under the finger.
      if (isTouch) {
        clearLongPress();
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          if (!interactionRef.current.isActive || interactionRef.current.isDragging) return;
          const opened = openMenuForTap(interactionRef.current.startX, interactionRef.current.startY);
          if (opened) {
            interactionRef.current.suppressTap = true;
          }
        }, 600);
      }
    };

    const handleMove = (e) => {
      if (!interactionRef.current.isActive) return;

      const { x, y } = getCoords(e);
      const isTouch = !!e.touches;
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;

      // Calculate distance from start point
      const dx = clientX - interactionRef.current.startClientX;
      const dy = clientY - interactionRef.current.startClientY;
      const distFromStart = Math.sqrt(dx * dx + dy * dy);

      // High threshold for mobile to prevent accidental drags - ONLY pixel-based
      const dragThreshold = interactionRef.current.lastType === 'touch' ? 40 : 10;

      // Only start dragging if we've moved far enough
      if (!interactionRef.current.isDragging && distFromStart > dragThreshold) {
        interactionRef.current.isDragging = true;
        // User started moving — cancel any pending long-press
        clearLongPress();
      }

      // Block all movement while seated
      if (isSittingRef.current) return;

      // Only move character if we're in drag mode.
      // Instead of calling setMyPosition here (which only moves on pointer
      // events), we store the world-space target and let the RAF movement
      // loop (movePlayer) drive the character toward it every frame.
      // This makes hold-in-place work and gives consistent speed on all devices.
      if (interactionRef.current.isDragging) {
        if (e.cancelable) e.preventDefault();
        const worldX = mapData ? x + cameraRef.current.x : x;
        const worldY = mapData ? y + cameraRef.current.y : y;
        dragTargetRef.current = { x: worldX, y: worldY };
      }
    };

    const handleEnd = (e) => {
      clearLongPress();
      dragTargetRef.current = null; // stop continuous drag movement
      if (!interactionRef.current.isActive) return;

      // Long-press already opened the menu — don't double-fire tap-to-mute.
      if (interactionRef.current.suppressTap) {
        interactionRef.current.isActive = false;
        interactionRef.current.isDragging = false;
        interactionRef.current.suppressTap = false;
        return;
      }

      const wasDragging = interactionRef.current.isDragging;

      // Calculate final distance to be sure
      let endClientX, endClientY;
      if (e.changedTouches) {
        endClientX = e.changedTouches[0].clientX;
        endClientY = e.changedTouches[0].clientY;
      } else {
        endClientX = e.clientX;
        endClientY = e.clientY;
      }

      const dx = endClientX - interactionRef.current.startClientX;
      const dy = endClientY - interactionRef.current.startClientY;
      const totalDist = Math.sqrt(dx * dx + dy * dy);

      // If we never started dragging, it's a tap!
      if (!wasDragging) {
        const tapCoords = getCoords(e);
        let tapX = tapCoords.x;
        let tapY = tapCoords.y;

        // Convert viewport coordinates to world coordinates
        // Use the active camera (seated or normal) so taps map correctly
        const activeCam = isSittingRef.current ? seatedCameraRef.current : cameraRef.current;
        if (mapData) {
          tapX += activeCam.x;
          tapY += activeCam.y;
        }

        // Timestamp / clip / URL bubbles sit above the nametag — open them
        // before sit/stand or player audio so the label stays clickable.
        const bubbleHit = chatBubbleHitAt(chatBubbleHitsRef.current, tapX, tapY);
        if (bubbleHit?.link) {
          activateChatBubbleLinkRef.current(bubbleHit.link);
          interactionRef.current.isActive = false;
          interactionRef.current.isDragging = false;
          return;
        }

        // Tap own sprite → mic gate sensitivity panel (before sit/stand handling)
        if (selfAtWorld(tapX, tapY)) {
          setAudioTarget(null);
          setShowMicGatePanel(true);
          interactionRef.current.isActive = false;
          interactionRef.current.isDragging = false;
          return;
        }

        // Other players (including occupied seats) take priority over stand-up.
        const playerTapRadius = interactionRef.current.lastType === 'touch' ? 50 : 40;
        const tappedPlayer = playerAtWorld(tapX, tapY, playerTapRadius);
        if (tappedPlayer) {
          openAudioPanel(tappedPlayer);
          interactionRef.current.isActive = false;
          interactionRef.current.isDragging = false;
          return;
        }

        // While sitting: tap empty space to stand up
        if (isSittingRef.current) {
          standUp();
          interactionRef.current.isActive = false;
          interactionRef.current.isDragging = false;
          return;
        }

        // Tap on an audience seat → sit, or open the occupant's audio panel
        if (mapData?.seats) {
          let tappedSeat = null;
          const tapRadius = interactionRef.current.lastType === 'touch' ? 30 : 20;
          for (const seat of mapData.seats) {
            if (pointInSeat(tapX, tapY, seat, tapRadius)) {
              tappedSeat = seat;
              break;
            }
          }
          if (tappedSeat) {
            const occupant = occupantOfAudienceSeat(tappedSeat);
            if (occupant) {
              openAudioPanel(occupant);
            } else {
              sitDown(tappedSeat, playersRef.current);
            }
            interactionRef.current.isActive = false;
            interactionRef.current.isDragging = false;
            return;
          }
        }

        // Tap on a team seat (Team A or Team B) → sit if empty, otherwise
        // open the occupant's audio panel. Occupied seats used to swallow the click.
        if (mapData?.teamSeats) {
          let tappedTeamSeat = null;
          const tapRadius = interactionRef.current.lastType === 'touch' ? 50 : 40;
          for (const seat of mapData.teamSeats) {
            if (pointInSeat(tapX, tapY, seat, tapRadius)) {
              tappedTeamSeat = seat;
              break;
            }
          }
          if (tappedTeamSeat) {
            const occupant = occupantOfTeamSeat(tappedTeamSeat);
            if (occupant) {
              openAudioPanel(occupant);
            } else if (lanSitAllowed()) {
              sitInTeamSeat(tappedTeamSeat, playersRef.current);
            }
            interactionRef.current.isActive = false;
            interactionRef.current.isDragging = false;
            return;
          }
        }
      }

      interactionRef.current.isActive = false;
      interactionRef.current.isDragging = false;
    };

    // Desktop right-click → own sprite opens mic gate; others open moderation menu.
    // Suppresses the browser's native context menu over the canvas.
    const handleContextMenu = (e) => {
      e.preventDefault();
      const { x, y } = getCoords(e);
      openMenuForTap(x, y);
    };

    const handleHover = (e) => {
      if (interactionRef.current.isActive) return;
      const { x, y } = getCoords(e);
      const activeCam = isSittingRef.current ? seatedCameraRef.current : cameraRef.current;
      const worldX = mapData ? x + activeCam.x : x;
      const worldY = mapData ? y + activeCam.y : y;
      const hit = chatBubbleHitAt(chatBubbleHitsRef.current, worldX, worldY);
      canvas.style.cursor = hit?.link ? 'pointer' : '';
    };

    const handleMouseLeave = () => {
      canvas.style.cursor = '';
    };

    // Also cancel long-press if the user moves the finger or cancels the touch
    const handleTouchCancel = () => clearLongPress();

    // Use non-passive for touchstart since we call preventDefault
    canvas.addEventListener('touchstart', handleStart, { passive: false });
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: true });
    canvas.addEventListener('mousedown', handleStart);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    canvas.addEventListener('mousemove', handleHover);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('contextmenu', handleContextMenu);

    return () => {
      canvas.removeEventListener('touchstart', handleStart);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
      canvas.removeEventListener('mousedown', handleStart);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      canvas.removeEventListener('mousemove', handleHover);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('contextmenu', handleContextMenu);
      clearLongPress();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, hallSlug, mapData, sitDown, sitInTeamSeat, standUp, isAuthProp, lanSitAllowed]);

  // Reset remote self-mute flags when hall changes. Local mute/volume persist.
  useEffect(() => {
    setSelfMutedPlayers({});
  }, [hallSlug]);

  // Update camera to follow player
  useEffect(() => {
    if (mapError) {
      // Reset camera on error only
      const cam = { x: 0, y: 0 };
      setCamera(cam);
      cameraRef.current = cam;
      return;
    }
    if (!mapData) {
      // Map still loading — keep the pre-centered initial camera, don't reset to (0,0)
      return;
    }

    // Keep player horizontally centred, but anchor vertically so the player
    // sits near the bottom of the viewport.
    // Portrait: 500px below feet (shows more of the world below the player).
    // Landscape: 100px below feet so there is a comfortable margin under the character.
    // Camera bounds are in logical world units (CSS pixels / scale factor).
    const isPortrait = viewportSize.height > viewportSize.width;
    const PLAYER_BOTTOM_MARGIN = isPortrait ? 500 : 100;
    const MAP_NATIVE_WIDTH = 1920;
    const MOBILE_BREAKPOINT = 1024;
    const scale = viewportSize.width < MOBILE_BREAKPOINT
      ? 0.5
      : Math.max(1, viewportSize.width / MAP_NATIVE_WIDTH);
    const logicalW = viewportSize.width / scale;   // world units visible horizontally
    const logicalH = viewportSize.height / scale;  // world units visible vertically

    // Always recompute seated camera target when viewport changes (orientation flip etc.)
    if (isSittingRef.current) {
      const LAN_DESK_BOTTOM_Y = 1488;
      const MAP_HEIGHT = 1920;
      const seat = currentSeatRef.current;
      let newTarget;
      if (seat?.team) {
        // Team seat: always show bottom of the map, camera X centred on the stage
        newTarget = {
          x: Math.max(0, Math.min(1920 - logicalW, 960 - logicalW / 2)),
          y: Math.max(0, MAP_HEIGHT - logicalH),
        };
      } else {
        // Audience seat: portrait = bottom of map, landscape = stage-focused
        newTarget = {
          x: Math.max(0, Math.min(1920 - logicalW, 960 - logicalW / 2)),
          y: isPortrait
            ? Math.max(0, MAP_HEIGHT - logicalH)       // portrait: bottom of map
            : Math.max(0, LAN_DESK_BOTTOM_Y - logicalH), // landscape: stage-focused
        };
      }
      seatCameraTargetRef.current = newTarget;
      // Snap seatedCameraRef immediately so the lerp starts from the correct orientation
      seatedCameraRef.current = newTarget;
    }

    const cam = {
      x: Math.max(0, Math.min(mapData.width - logicalW, myPosition.x - logicalW / 2)),
      y: Math.max(0, Math.min(mapData.height - logicalH, myPosition.y - logicalH + PLAYER_BOTTOM_MARGIN))
    };
    setCamera(cam);
    cameraRef.current = cam;
  }, [myPosition, mapData, mapError, viewportSize]);

  // Compute a FIXED TV rectangle whenever the viewport size changes.
  // Using the camera position when a player stands at the stage barrier (the
  // closest they can get to the stage) as the reference point ensures the TV
  // always fits in the viewport at that position — and because it's fixed it
  // never flexes or resizes as the player moves around.
  useEffect(() => {
    const isPortrait = viewportSize.height > viewportSize.width;
    const MOBILE_BREAKPOINT = 1024;
    const MAP_NATIVE_WIDTH  = 1920;
    const TV_BOTTOM_WORLD_Y = 1312;
    const STAGE_BARRIER_Y   = DEFAULT_SPAWN_Y; // bottom of stage-barrier collision — matches spawn
    const scale = viewportSize.width < MOBILE_BREAKPOINT
      ? 0.5
      : Math.max(1, viewportSize.width / MAP_NATIVE_WIDTH);
    const vW = viewportSize.width;
    const vH = viewportSize.height;
    const logicalH = vH / scale;
    // PLAYER_BOTTOM_MARGIN mirrors the value used in the camera useEffect
    const PLAYER_BOTTOM_MARGIN = isPortrait ? 500 : 100;

    // Only store cssW and cssH — cssL is computed each frame from the live
    // camera X so the TV tracks its world-space position on the map background.
    // cssT is always 0 (TV sits at the top of the viewport).
    let cssW, cssH;
    if (!isPortrait) {
      // Landscape: derive TV height from the reference camera position (player at barrier).
      const refCamY = Math.max(0, STAGE_BARRIER_Y - logicalH + PLAYER_BOTTOM_MARGIN);
      const tvBottomPx = (TV_BOTTOM_WORLD_Y - refCamY) * scale;
      if (tvBottomPx <= 0) { fixedTvRectRef.current = null; return; }
      cssH = tvBottomPx;
      cssW = cssH * (16 / 9);
      if (cssW > vW) { cssW = vW; cssH = cssW * (9 / 16); }
    } else {
      // Portrait: fill the viewport width with 16:9 aspect ratio.
      cssW = vW;
      cssH = cssW * (9 / 16);
    }

    // Store only dimensions — position is world-anchored, computed per-frame in draw().
    fixedTvRectRef.current = { cssW, cssH };
    // Force the TV DOM element to rewrite its base size on the next draw frame.
    prevTvStyleRef.current = { l: null, t: null, w: null, h: null, display: null, baseSizeSet: false };
  }, [viewportSize]);

  // Calculate canvas scale on mount and resize (not every frame for performance)
  useEffect(() => {
    if (!canvasRef.current) return;

    const applyCanvasBox = (w, canvasH) => {
      const canvas = canvasRef.current;
      const overlay = overlayCanvasRef.current;
      if (!canvas) return;
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== canvasH) canvas.height = canvasH;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${canvasH}px`;
      if (overlay) {
        if (overlay.width !== w) overlay.width = w;
        if (overlay.height !== canvasH) overlay.height = canvasH;
        overlay.style.width = `${w}px`;
        overlay.style.height = `${canvasH}px`;
      }
    };

    const updateCanvasScale = ({ force = false } = {}) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Layout size: visualViewport tracks the iOS URL bar, which we want.
      // Do not use it while the page is pinch/input-zoomed (scale !== 1) —
      // that shrinks the reported size and makes the TV drift off the map.
      const vvp = window.visualViewport;
      const zoomed = !!(vvp && Math.abs((vvp.scale || 1) - 1) > 0.02);
      const w = Math.round(zoomed || !vvp ? window.innerWidth : vvp.width);
      const h = Math.round(zoomed || !vvp ? window.innerHeight : vvp.height);
      if (w < 100 || h < 100) return; // defer if rotation lag gives nonsense values

      const prev = viewportSizeRef.current;
      const aspectFlipped = !!prev.width && !!prev.height
        && ((prev.height > prev.width) !== (h > w));

      // iOS keyboard open/close animates visualViewport for ~300–400 ms.
      // Resizing mid-animation hides chat and shrinks the TV, then it snaps back.
      // Never skip a real orientation flip — that stretches the bitmap into the
      // new CSS box. Composer touch-hold is only for keyboard height changes.
      if (!force && !aspectFlipped) {
        if (chatComposerFocusedRef.current) return;
        if (Date.now() < chatViewportHoldUntilRef.current) return;
      }

      // Set the outer wrapper to exactly the measured viewport height.
      // Controls bar + canvas (flex:1) will together fill this space perfectly.
      if (liveWorldWrapperRef.current) {
        liveWorldWrapperRef.current.style.height = `${h}px`;
      }

      // Subtract the controls bar height so canvas pixel dimensions match the
      // actual CSS area the canvas occupies (prevents vertical stretch/squish).
      const controlsH = controlsBarRef.current ? controlsBarRef.current.offsetHeight : 0;
      const canvasH = Math.max(1, h - controlsH);

      // On mobile (< 1024px) scale DOWN to 0.5 so the content appears at half size,
      // showing more of the world while still filling the full viewport.
      // On wide screens scale UP so the map fills 100% width with no black bars.
      const MAP_NATIVE_WIDTH = 1920;
      const MOBILE_BREAKPOINT = 1024;
      const scale = w < MOBILE_BREAKPOINT
        ? 0.5
        : Math.max(1, w / MAP_NATIVE_WIDTH);
      canvasScaleRef.current = { scaleX: scale, scaleY: scale, scale };
      applyCanvasBox(w, canvasH);
      viewportSizeRef.current = { width: w, height: canvasH };
      setViewportSize(prevSize =>
        prevSize.width === w && prevSize.height === canvasH ? prevSize : { width: w, height: canvasH }
      );
    };

    updateCanvasScaleRef.current = updateCanvasScale;

    // iOS Safari animates the address-bar show/hide over ~200 ms.
    // visualViewport.resize fires at the START of that animation, so a single
    // call reads a mid-animation value. Schedule a follow-up at 250 ms to
    // catch the fully-settled height once the animation completes.
    let vpSettleTimer = null;
    const handleVpResize = () => {
      updateCanvasScale();
      clearTimeout(vpSettleTimer);
      vpSettleTimer = setTimeout(updateCanvasScale, 250);
    };

    const handleWindowResize = () => updateCanvasScale();

    // orientationchange: iOS fires this BEFORE the layout has settled.
    // Force the resize even if the chat composer hold is active — rotating
    // mid-scroll otherwise leaves the old bitmap stretched to the new box.
    // Briefly freeze document overflow so an in-flight swipe cannot keep
    // rubber-banding the page after the viewport flips.
    let orientTimer = null;
    const handleOrient = () => {
      const html = document.documentElement;
      const prevOverflow = html.style.overflow;
      html.style.overflow = 'hidden';
      updateCanvasScale({ force: true });
      clearTimeout(orientTimer);
      orientTimer = setTimeout(() => {
        html.style.overflow = prevOverflow;
        updateCanvasScale({ force: true });
        const maxScroll = Math.max(0, html.scrollHeight - window.innerHeight);
        if (window.scrollY > maxScroll || window.scrollY < 0) {
          window.scrollTo(0, Math.min(Math.max(0, window.scrollY), maxScroll));
        }
      }, 400);
    };

    // Run immediately, then after a short delay to catch late layout passes.
    updateCanvasScale();
    const t1 = setTimeout(updateCanvasScale, 100);
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleOrient);
    window.visualViewport?.addEventListener('resize', handleVpResize);
    return () => {
      clearTimeout(t1);
      clearTimeout(vpSettleTimer);
      clearTimeout(orientTimer);
      document.documentElement.style.overflow = '';
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleOrient);
      window.visualViewport?.removeEventListener('resize', handleVpResize);
    };
  }, []);

  // Keep videoUrlRef and videoIdRef in sync (no stale-closure issues in draw/emit loops)
  useEffect(() => {
    videoUrlRef.current = videoUrl;
  }, [videoUrl]);
  useEffect(() => {
    videoIdRef.current = videoId;
  }, [videoId]);
  useEffect(() => {
    momentStartRef.current = momentStartSeconds;
  }, [momentStartSeconds]);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Capture resume start once when the YouTube id changes (for iframe start=).
  // Lives: always 0 — start= on live URLs commonly yields a black player.
  // Live resume uses seekTo after the stream is playing (see restoreVideoProgress).
  useEffect(() => {
    if (isClipPlayback) {
      setEmbedResumeStart(activeClipStart);
      return;
    }
    if (isLive) {
      setEmbedResumeStart(0);
      return;
    }
    if (momentStartSeconds > 0) {
      setEmbedResumeStart(momentStartSeconds);
      return;
    }
    setEmbedResumeStart(getLocalResumeStartSeconds(videoId));
  }, [videoId, isLive, momentStartSeconds, isClipPlayback, activeClipStart]);

  beginMomentProgressHoldRef.current = (youtubeVideoId, momentSeconds, { previousPosition } = {}) => {
    if (!youtubeVideoId || isClipPlaybackRef.current) return;
    const momentT = Math.max(0, Math.floor(Number(momentSeconds) || 0));
    if (!(momentT > 0)) return;
    const existing = momentHoldRef.current;
    if (existing && existing.youtubeVideoId === youtubeVideoId) {
      existing.momentPosition = momentT;
      existing.playedMs = 0;
      existing.promptShown = false;
      setMomentResumePrompt(null);
      return;
    }

    let previous = previousPosition;
    if (previous == null) {
      const livePos = getPlayerPosition(ytPlayerRef.current);
      if (livePos != null && livePos >= 5) previous = livePos;
      else {
        const local = readLocalProgress(youtubeVideoId);
        if (local?.position >= 5) previous = local.position;
      }
    }
    previous = Math.floor(Number(previous) || 0);
    if (!shouldHoldMomentProgress(previous, momentT)) {
      momentHoldRef.current = null;
      setMomentResumePrompt(null);
      return;
    }

    writeLocalProgress(youtubeVideoId, previous);
    if (userRef.current?.userId) {
      lastSavedProgressRef.current = { youtubeVideoId, position: previous };
      saveServerProgress(youtubeVideoId, previous);
    }

    momentHoldRef.current = {
      youtubeVideoId,
      savedPosition: previous,
      momentPosition: momentT,
      playedMs: 0,
      promptShown: false
    };
    setMomentResumePrompt(null);
  };

  const commitMomentHold = (kind) => {
    const hold = momentHoldRef.current;
    setMomentResumePrompt(null);
    if (!hold) return;
    momentHoldRef.current = null;
    const here = getPlayerPosition(ytPlayerRef.current);
    const target = kind === 'resume'
      ? hold.savedPosition
      : (here != null && here >= 0 ? here : hold.momentPosition);
    if (kind === 'resume') seekTvToMoment(target);
    if (hold.youtubeVideoId) {
      writeLocalProgress(hold.youtubeVideoId, target);
      if (userRef.current?.userId) {
        lastSavedProgressRef.current = { youtubeVideoId: hold.youtubeVideoId, position: target };
        saveServerProgress(hold.youtubeVideoId, target);
      }
    }
  };

  // Flush current playback position to localStorage (+ server if logged in)
  const flushVideoProgress = useCallback((youtubeVideoId, { preferBeacon = false } = {}) => {
    if (!youtubeVideoId || shouldSkipProgressWrite({ isClipPlayback: isClipPlaybackRef.current })) return;
    const hold = momentHoldRef.current;
    if (hold && hold.youtubeVideoId === youtubeVideoId) return;
    const player = ytPlayerRef.current;
    const position = getPlayerPosition(player);
    if (position == null) return;
    const duration = getPlayerDuration(player);
    if (!shouldPersist(position, duration, { isLive })) return;

    writeLocalProgress(youtubeVideoId, position);

    const loggedIn = !!userRef.current?.userId;
    if (!loggedIn) return;
    if (!shouldWriteServer(position, lastSavedProgressRef.current.youtubeVideoId === youtubeVideoId
      ? lastSavedProgressRef.current.position
      : null)) {
      return;
    }

    lastSavedProgressRef.current = { youtubeVideoId, position };
    if (preferBeacon) {
      const ok = saveServerProgressBeacon(youtubeVideoId, position);
      if (!ok) {
        saveServerProgress(youtubeVideoId, position);
      }
    } else {
      saveServerProgress(youtubeVideoId, position);
    }
  }, [isLive]);

  // Queue a live DVR seek; applied from onStateChange when PLAYING (or timeout).
  const queueLiveSeek = useCallback((player, youtubeVideoId, positionSeconds, { force = false } = {}) => {
    if (!youtubeVideoId || !(positionSeconds > 0)) return;
    pendingLiveSeekRef.current = { youtubeVideoId, position: positionSeconds, force };
    if (!player) return;

    const applyIfDue = () => {
      const pending = pendingLiveSeekRef.current;
      if (!pending || pending.youtubeVideoId !== videoIdRef.current) return;
      pendingLiveSeekRef.current = null;
      const target = ytPlayerRef.current || player;
      safeSeekTo(target, pending.position, { force: !!pending.force });
    };

    try {
      const state = typeof player.getPlayerState === 'function' ? player.getPlayerState() : null;
      if (state === YT_STATE.PLAYING) {
        applyIfDue();
        return;
      }
    } catch (_) {}

    try {
      if (typeof player.playVideo === 'function') player.playVideo();
    } catch (_) {}

    // Fallback if PLAYING never fires (muted autoplay quirks on some browsers).
    setTimeout(applyIfDue, 1200);
    setTimeout(applyIfDue, 2800);
  }, []);

  // Restore saved position once the YT player is ready for a given video.
  // VODs: prefer iframe start=; seekTo only if start= was ignored.
  // Lives: never start=; seekTo after the stream is playing (DVR window).
  const restoreVideoProgress = useCallback(async (player, youtubeVideoId) => {
    if (!player || !youtubeVideoId) return;
    const restoreKey = isClipPlaybackRef.current
      ? `${youtubeVideoId}:c:${clipRangeRef.current.start}:${clipRangeRef.current.end}`
      : `${youtubeVideoId}:t:${Math.max(0, momentStartRef.current || 0)}`;
    if (progressRestoredForRef.current === restoreKey) return;
    if (isClipPlaybackRef.current) {
      progressRestoredForRef.current = restoreKey;
      const start = clipRangeRef.current.start;
      const end = clipRangeRef.current.end;
      const duration = getPlayerDuration(player);
      const ranged = clipRangeForPlayerDuration(start, end, duration, { isLive });
      if (!ranged.playable) {
        const clipKey = `${youtubeVideoId}:${start}:${end}`;
        if (clipUnplayableNotifiedRef.current !== clipKey) {
          clipUnplayableNotifiedRef.current = clipKey;
          setShareToast('This clip isn’t in the archived stream');
          setTimeout(() => setShareToast(''), 3500);
        }
        return;
      }
      const seekStart = ranged.startSeconds;
      if (seekStart > 0) {
        if (isLive) queueLiveSeek(player, youtubeVideoId, seekStart, { force: true });
        else safeSeekTo(player, seekStart, { force: true });
      } else {
        try {
          if (typeof player.playVideo === 'function') player.playVideo();
        } catch (_) {}
      }
      return;
    }
    progressRestoredForRef.current = restoreKey;

    const local = readLocalProgress(youtubeVideoId);

    const applyResume = (position) => {
      if (!(position > 0)) return;
      if (isLive) {
        queueLiveSeek(player, youtubeVideoId, position);
      } else {
        safeSeekTo(player, position);
      }
    };

    const moment = momentStartRef.current;
    if (moment > 0) {
      applyResume(moment);
      beginMomentProgressHoldRef.current(youtubeVideoId, moment, {
        previousPosition: local?.position
      });

      const loggedInForMoment = !!userRef.current?.userId;
      if (!loggedInForMoment) return;

      const server = await fetchServerProgress(youtubeVideoId);
      if (videoIdRef.current !== youtubeVideoId) return;
      const winner = pickNewer(local, server?.position > 0 ? server : null);
      if (!shouldHoldMomentProgress(winner?.position, moment)) return;
      const hold = momentHoldRef.current;
      if (hold && hold.youtubeVideoId === youtubeVideoId) {
        hold.savedPosition = winner.position;
        writeLocalProgress(youtubeVideoId, winner.position);
      } else {
        beginMomentProgressHoldRef.current(youtubeVideoId, moment, {
          previousPosition: winner.position
        });
      }
      return;
    }

    // Local first (instant); server may refine if newer.
    if (local?.position > 0) {
      applyResume(local.position);
    }

    const loggedIn = !!userRef.current?.userId;
    if (!loggedIn) return;

    const server = await fetchServerProgress(youtubeVideoId);
    // Bail if the user already switched videos while the request was in flight
    if (videoIdRef.current !== youtubeVideoId) return;

    const winner = pickNewer(local, server?.position > 0 ? server : null);
    if (!winner?.position) return;

    // Only seek again when the server copy is actually newer than local.
    if (!local || winner.updatedAt > (local.updatedAt || 0)) {
      applyResume(winner.position);
      writeLocalProgress(youtubeVideoId, winner.position);
    }
  }, [isLive, queueLiveSeek]);

  useEffect(() => {
    prevMomentStartRef.current = null;
  }, [videoId]);

  useEffect(() => {
    if (prevMomentStartRef.current == null) {
      prevMomentStartRef.current = momentStartSeconds;
      return;
    }
    if (prevMomentStartRef.current === momentStartSeconds) return;
    prevMomentStartRef.current = momentStartSeconds;
    if (isClipPlaybackRef.current) return;
    if (!(momentStartSeconds > 0) || !videoId) return;
    beginMomentProgressHoldRef.current(videoId, momentStartSeconds);
    if (isLive) queueLiveSeek(ytPlayerRef.current, videoId, momentStartSeconds);
    else seekTvToMoment(momentStartSeconds);
  }, [momentStartSeconds, videoId, isLive, queueLiveSeek]);

  useEffect(() => {
    hallSlugRef.current = hallSlug;
  }, [hallSlug]);

  useEffect(() => {
    setLocalClip(null);
  }, [videoId, dbVideoId, clipId, propClipStart, propClipEnd]);

  useEffect(() => {
    if (clipDraftRef.current) return;
    setClipArmedUntil(0);
    setClipArmStart(null);
  }, [videoId, hallSlug, dbVideoId, isClipPlayback]);

  useEffect(() => {
    if (!clipArmedUntil) return undefined;
    const ms = clipArmedUntil - Date.now();
    if (ms <= 0) {
      setClipArmedUntil(0);
      return undefined;
    }
    const timer = setTimeout(() => setClipArmedUntil(0), ms);
    return () => clearTimeout(timer);
  }, [clipArmedUntil]);

  useEffect(() => {
    if (!isClipPlayback) return undefined;
    let enteredClip = false;
    const tick = () => {
      const player = ytPlayerRef.current;
      const t = getPlayerPosition(player);
      if (t == null) return;
      const { start, end } = clipRangeRef.current;
      const duration = getPlayerDuration(player);
      const ranged = clipRangeForPlayerDuration(start, end, duration, { isLive });
      if (!ranged.playable) return;
      const decision = clipPauseDecision(t, ranged.startSeconds, ranged.endSeconds, enteredClip);
      enteredClip = decision.entered;
      if (decision.pause) {
        replayClipFromStart(player, ranged.startSeconds);
        enteredClip = false;
      }
    };
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isClipPlayback, activeClipStart, activeClipEnd, videoId, clipReplayNonce, isLive]);

  // Same YouTube id: seek the existing player onto the new clip/timestamp.
  // Lives must not remount (no start=), so this is the only way to leave a clip
  // for a timestamp without jumping to the live edge.
  useEffect(() => {
    const leavingClip = prevClipPlaybackRef.current && !isClipPlayback;
    prevClipPlaybackRef.current = isClipPlayback;
    const player = ytPlayerRef.current;
    if (!player || !videoId) return;
    if (isClipPlayback) {
      const start = Math.max(0, activeClipStart);
      const end = Math.max(0, activeClipEnd);
      const duration = getPlayerDuration(player);
      const ranged = clipRangeForPlayerDuration(start, end, duration, { isLive });
      if (!ranged.playable) {
        const clipKey = `${videoId}:${start}:${end}`;
        if (clipUnplayableNotifiedRef.current !== clipKey) {
          clipUnplayableNotifiedRef.current = clipKey;
          setShareToast('This clip isn’t in the archived stream');
          setTimeout(() => setShareToast(''), 3500);
        }
        return;
      }
      const seekStart = ranged.startSeconds;
      if (isLive) {
        if (seekStart > 0) queueLiveSeek(player, videoId, seekStart, { force: true });
        else {
          try {
            if (typeof player.seekTo === 'function') player.seekTo(0, true);
            if (typeof player.playVideo === 'function') player.playVideo();
          } catch (_) {}
        }
      } else if (seekStart > 0) {
        safeSeekTo(player, seekStart, { force: true });
      } else {
        try {
          if (typeof player.seekTo === 'function') player.seekTo(0, true);
          if (typeof player.playVideo === 'function') player.playVideo();
        } catch (_) {}
      }
      return;
    }
    if (momentStartSeconds > 0) {
      if (isLive) queueLiveSeek(player, videoId, momentStartSeconds);
      else seekTvToMoment(momentStartSeconds);
      return;
    }
    // Clips → Live chip often keeps the same YouTube id (tvPlaybackKey stays
    // "live"), so seek back to the live edge instead of leaving the clip range.
    if (leavingClip && isLive) seekLiveEdge(player);
  }, [videoId, isLive, isClipPlayback, clipId, activeClipStart, activeClipEnd, momentStartSeconds, queueLiveSeek, clipReplayNonce]);

  // If the hall prop changes at runtime (dropdown switcher), ask the existing
  // socket to join the new hall instead of tearing down the connection.
  useEffect(() => {
    if (socketRef.current && hallSlug) {
      socketRef.current.emit('join-hall', { slug: hallSlug, voiceOn: !!localStreamRef.current });
    }
  }, [hallSlug]);
  useEffect(() => {
    isTvExpandedRef.current = isTvExpanded;
  }, [isTvExpanded]);

  // Attach a postMessage controller to the TV iframe (see youtubeEmbedPlayer.js
  // for why this is not YT.Player). It never removes or reloads the iframe.
  useEffect(() => {
    // Flush progress for the previous video before re-attaching the player
    const previousVideoId = lastSavedProgressRef.current.youtubeVideoId;
    const skipProgressThisClip = isClipPlayback;
    if (previousVideoId && previousVideoId !== videoId && !lastClipPlaybackRef.current && !skipProgressThisClip) {
      flushVideoProgress(previousVideoId);
    }
    lastClipPlaybackRef.current = isClipPlayback;
    if (momentHoldRef.current?.youtubeVideoId !== videoId) {
      momentHoldRef.current = null;
      setMomentResumePrompt(null);
    }
    if (isClipPlayback) {
      momentHoldRef.current = null;
      setMomentResumePrompt(null);
    }
    if (videoId !== progressRestoredForRef.current) {
      progressRestoredForRef.current = null;
    }
    pendingLiveSeekRef.current = null;
    lastClipClockRef.current = { t: null, atMs: 0, duration: null, looksLive: false, stuckAtHead: false };

    const attachForVideoId = videoId;
    const player = tvIframeRef.current
      ? attachYoutubeEmbed(tvIframeRef.current, {
        onReady: (event) => {
          restoreVideoProgress(event?.target || ytPlayerRef.current, attachForVideoId);
        },
        onStateChange: (event) => {
          const p = event?.target || ytPlayerRef.current;
          if (event?.data === YT_STATE.ENDED && isClipPlaybackRef.current) {
            replayClipFromStart(p, clipRangeRef.current.start);
            return;
          }
          if (event?.data !== YT_STATE.PLAYING) return;
          const pending = pendingLiveSeekRef.current;
          if (!pending || pending.youtubeVideoId !== videoIdRef.current) return;
          pendingLiveSeekRef.current = null;
          safeSeekTo(p, pending.position, { force: !!pending.force });
        },
      })
      : null;
    ytPlayerRef.current = player;

    return () => {
      // Flush the video this effect instance was bound to (closure), not videoIdRef
      // which may already point at the next video when cleanups run.
      if (!skipProgressThisClip) flushVideoProgress(videoId);
      player?.destroy();
      if (ytPlayerRef.current === player) ytPlayerRef.current = null;
    };
  }, [videoId, tvPlaybackKey, flushVideoProgress, restoreVideoProgress]); // re-attach when the iframe itself is replaced

  // Periodic progress save (every 10s) + flush on tab hide / page close
  useEffect(() => {
    if (!videoId) return undefined;

    const saveTick = () => {
      if (shouldSkipProgressWrite({ isClipPlayback: isClipPlaybackRef.current })) return;
      const hold = momentHoldRef.current;
      if (hold && hold.youtubeVideoId === videoId) return;
      const player = ytPlayerRef.current;
      const position = getPlayerPosition(player);
      if (position == null) return;
      const duration = getPlayerDuration(player);
      if (!shouldPersist(position, duration, { isLive })) return;

      writeLocalProgress(videoId, position);

      if (!userRef.current?.userId) return;
      if (!shouldWriteServer(
        position,
        lastSavedProgressRef.current.youtubeVideoId === videoId
          ? lastSavedProgressRef.current.position
          : null
      )) {
        return;
      }

      lastSavedProgressRef.current = { youtubeVideoId: videoId, position };
      saveServerProgress(videoId, position);
    };

    const intervalId = setInterval(saveTick, 10000);

    const handlePageHide = (e) => {
      // Always flush local; beacon only on real unload (not bfcache freeze)
      flushVideoProgress(videoIdRef.current, { preferBeacon: !e.persisted });
    };
    const handleBeforeUnload = () => {
      flushVideoProgress(videoIdRef.current, { preferBeacon: true });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushVideoProgress(videoIdRef.current, { preferBeacon: false });
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [videoId, flushVideoProgress, isLive]);

  useEffect(() => {
    if (!videoId) return undefined;
    const sampleClock = () => {
      const player = ytPlayerRef.current;
      const raw = readPlayerClock(player);
      const prev = lastClipClockRef.current;
      const playing = raw.playerState === 1 || raw.playerState === 3;
      const stuckAtHead = playing
        && Number(raw.position) < 1
        && Number(raw.duration) > 1;
      const live = inferClipIsLive({
        isLive,
        looksLive: raw.looksLive || prev.looksLive,
        duration: raw.duration,
        previousDuration: prev.duration,
        position: raw.position,
        playerState: raw.playerState,
        wasStuckAtHead: prev.stuckAtHead
      });
      const clock = resolveClipClock({
        position: raw.position,
        duration: raw.duration,
        playerState: raw.playerState,
        isLive: live
      });
      lastClipClockRef.current = {
        t: clock.seconds != null ? clock.seconds : prev.t,
        atMs: clock.seconds != null ? Date.now() : prev.atMs,
        duration: clock.duration ?? raw.duration ?? prev.duration,
        looksLive: live,
        stuckAtHead
      };
    };
    sampleClock();
    const intervalId = setInterval(sampleClock, 1000);
    return () => clearInterval(intervalId);
  }, [videoId, isLive]);

  useEffect(() => {
    if (!videoId) return undefined;
    const intervalId = setInterval(() => {
      if (isClipPlaybackRef.current) return;
      const hold = momentHoldRef.current;
      if (!hold || hold.youtubeVideoId !== videoId || hold.promptShown) return;
      const player = ytPlayerRef.current;
      let state = null;
      try {
        state = player?.getPlayerState?.() ?? null;
      } catch {
        state = null;
      }
      if (state !== YT_STATE.PLAYING) return;
      hold.playedMs += 1000;
      if (hold.playedMs < MOMENT_HOLD_WATCH_MS) return;
      hold.promptShown = true;
      const here = getPlayerPosition(player);
      setMomentResumePrompt({
        savedPosition: hold.savedPosition,
        currentPosition: here != null && here >= 0 ? here : hold.momentPosition
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [videoId]);

  // Audio level monitoring: local noise gate + talking indicators
  useEffect(() => {
    if (!isAudioContextStarted) return undefined;

    micGateTickAtRef.current = 0;
    const checkInterval = setInterval(() => {
      const nowMs = performance.now();
      const prev = micGateTickAtRef.current;
      micGateTickAtRef.current = nowMs;
      const dt = prev ? Math.min(200, Math.max(0, nowMs - prev)) : 50;
      const silenced = !isMicEnabled || isLocalMuted || !!hallMembership?.isMuted;

      if (localAnalyserRef.current && localGateGainRef.current) {
        const levelDb = readSpeechLevelDbRef.current();
        const decision = stepMicGate(micGateStateRef.current, levelDb, dt, {
          offsetDb: micGateOffsetRef.current,
          forceClosed: silenced
        });
        const open = silenced ? false : decision.open;
        writeMicGateGainRef.current(open);
        setIsLocalTalking(open);
        if (showMicGatePanel) {
          setMicGateMeter((meter) => {
            const next = {
              levelDb,
              noiseFloorDb: decision.noiseFloorDb,
              openThresholdDb: decision.openThresholdDb,
              open,
              measured: true
            };
            if (
              meter.measured &&
              meter.open === next.open &&
              Math.round(meter.levelDb) === Math.round(next.levelDb) &&
              Math.round(meter.noiseFloorDb) === Math.round(next.noiseFloorDb) &&
              Math.round(meter.openThresholdDb) === Math.round(next.openThresholdDb)
            ) {
              return meter;
            }
            return next;
          });
        }
      } else {
        writeMicGateGainRef.current(false);
        setIsLocalTalking(false);
        if (showMicGatePanel) {
          setMicGateMeter((meter) => (meter.measured ? { ...meter, open: false, measured: false } : meter));
        }
      }

      // Remote talking glow (hearing others) — fixed level, not this user's noise floor.
      const newTalkingPlayers = {};
      Object.keys(analyserNodesRef.current).forEach(playerId => {
        const analyser = analyserNodesRef.current[playerId];
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        newTalkingPlayers[playerId] = average > REMOTE_TALKING_LEVEL;
      });
      setTalkingPlayers(newTalkingPlayers);
    }, 50);

    return () => {
      clearInterval(checkInterval);
      micGateTickAtRef.current = 0;
    };
  }, [isAudioContextStarted, isMicEnabled, isLocalMuted, hallMembership?.isMuted, showMicGatePanel]);

  // Animation helper: returns the current sprite frame index for a player.
  // moveState: 'idle' | 'walk' | 'run'
  const getAnimationFrame = (playerId, moveState) => {
    const now = Date.now();

    // Initialize animation state for this player if needed
    if (!animationStateRef.current[playerId]) {
      animationStateRef.current[playerId] = {
        currentFrame: 0,
        lastPosition: { x: 0, y: 0 },
        lastFrameUpdate: now,
        facingLeft: true,
        moveState: 'idle'
      };
    }

    const state = animationStateRef.current[playerId];
    const frameCount = moveState === 'run'  ? spriteConfig.runFrames
                     : moveState === 'walk' ? spriteConfig.walkFrames
                     :                        spriteConfig.idleFrames;

    // Mode switch: reset to frame 0 first so the reset isn't immediately
    // overwritten by the advance logic below.
    if (state.moveState !== moveState) {
      state.currentFrame = 0;
      state.lastFrameUpdate = now;
      state.moveState = moveState;
      return 0;
    }

    // Advance frame based on wall-clock time
    if (now - state.lastFrameUpdate > spriteConfig.animationSpeed) {
      state.currentFrame = (state.currentFrame + 1) % frameCount;
      state.lastFrameUpdate = now;
    }

    return state.currentFrame;
  };

  // Draw animated sprite with mirroring support.
  // moveStateOverride: 'idle' | 'walk' | 'run' — supply for the local player
  //   so animation is driven by key / drag state rather than position delta.
  //   When omitted (remote players), a lastMoveTime linger + player.moveState
  //   from the server determines the animation row.
  const MOVE_LINGER_MS = 150;
  // clipBottomWorldY: when provided the sprite is clipped so only the portion
  // ABOVE that world-Y value is drawn (everything at or below is hidden).
  const drawAnimatedSprite = (ctx, x, y, playerId, player, prevPosition, moveStateOverride, clipBottomWorldY = null) => {
    if (!spriteSheetLoaded || !spriteSheet) {
      ctx.beginPath();
      ctx.arc(x, y, 10, 0, 2 * Math.PI);
      ctx.fillStyle = 'white';
      ctx.fill();
      return;
    }

    const now = Date.now();

    // Get or initialize animation state
    if (!animationStateRef.current[playerId]) {
      animationStateRef.current[playerId] = {
        currentFrame: 0,
        lastPosition: { x, y },
        facingLeft: true,
        moveState: 'idle',
        lastMoveTime: 0,
        lastFrameUpdate: 0
      };
    }

    const state = animationStateRef.current[playerId];

    // Resolve moveState for this draw call.
    // Self: caller passes explicit override (keysPressed + drag distance).
    // Remote: position-delta linger determines idle vs moving; player.moveState
    //         (from server) distinguishes walk vs run.
    let moveState;
    if (moveStateOverride !== undefined) {
      moveState = moveStateOverride;
    } else {
      const hasDelta = prevPosition &&
        (Math.abs(player.x - prevPosition.x) > 0.5 ||
          Math.abs(player.y - prevPosition.y) > 0.5);
      if (hasDelta) state.lastMoveTime = now;
      const isMoving = (now - state.lastMoveTime) < MOVE_LINGER_MS;
      if (!isMoving) {
        moveState = 'idle';
      } else {
        moveState = player.moveState === 'run' ? 'run' : 'walk';
      }
    }

    // Update facing direction based on horizontal movement
    if (prevPosition && Math.abs(player.x - prevPosition.x) > 0.5) {
      state.facingLeft = player.x < prevPosition.x;
    }

    // Get current animation frame
    const frame = getAnimationFrame(playerId, moveState);

    // Calculate source position in sprite sheet
    // Row 0 = idle, Row 1 = walk, Row 2 = run
    const srcX = frame * spriteConfig.frameWidth;
    const srcY = moveState === 'run'  ? spriteConfig.frameHeight * 2
               : moveState === 'walk' ? spriteConfig.frameHeight
               :                        0;

    // Save canvas state
    ctx.save();

    // Clip the sprite to only render the portion ABOVE clipBottomWorldY (world coords).
    // Used for team-seat players so only head/shoulders appear above the desk surface.
    if (clipBottomWorldY !== null) {
      const spriteTopY = y - spriteConfig.renderHeight;
      const visibleH = clipBottomWorldY - spriteTopY;
      if (visibleH > 0) {
        ctx.beginPath();
        ctx.rect(
          x - spriteConfig.renderWidth / 2,
          spriteTopY,
          spriteConfig.renderWidth,
          visibleH
        );
        ctx.clip();
      } else {
        // Sprite is entirely below the clip line — skip drawing
        ctx.restore();
        state.lastPosition = { x: player.x, y: player.y };
        return;
      }
    }

    // Mirror sprite if facing right
    if (!state.facingLeft) {
      ctx.translate(x, y);
      ctx.scale(-1, 1);
      ctx.translate(-x, -y);
    }

    // Draw sprite at 3x scale, positioned so player coords are at BOTTOM-CENTER
    ctx.drawImage(
      spriteSheet,
      srcX, srcY,                                       // Source X, Y from sprite sheet
      spriteConfig.frameWidth,                          // Source width
      spriteConfig.frameHeight,                         // Source height
      x - spriteConfig.renderWidth / 2,                 // Dest X (centered horizontally)
      y - spriteConfig.renderHeight,                    // Dest Y (bottom of sprite at player Y)
      spriteConfig.renderWidth,                         // Dest width (240px = 80 * 3)
      spriteConfig.renderHeight                         // Dest height (192px = 64 * 3)
    );

    // Restore canvas state
    ctx.restore();

    // Store position for next frame
    state.lastPosition = { x: player.x, y: player.y };
  };

  // Draw loop
  const draw = () => {
    const canvas = canvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    if (!canvas || !overlayCanvas) return;
    // Cache contexts — getContext() on every frame is measurably expensive on mobile.
    if (!ctxCacheRef.current || ctxCacheRef.current.canvas !== canvas) {
      ctxCacheRef.current = canvas.getContext('2d');
    }
    if (!octxCacheRef.current || octxCacheRef.current.canvas !== overlayCanvas) {
      octxCacheRef.current = overlayCanvas.getContext('2d');
    }
    const ctx  = ctxCacheRef.current;
    const octx = octxCacheRef.current;

    // Pixel-art sprites must never be blurred. Set each frame because
    // some browsers reset this after resize or other canvas state changes.
    ctx.imageSmoothingEnabled = false;
    octx.imageSmoothingEnabled = false;

    const cs = canvasScaleRef.current;
    const scale = cs.scale || 1;
    const logW = canvas.width / scale;
    const logH = canvas.height / scale;

    // Smooth camera lerp when transitioning to/from seated view
    if (isSittingRef.current) {
      const lerp = 0.08;
      seatedCameraRef.current = {
        x: seatedCameraRef.current.x + (seatCameraTargetRef.current.x - seatedCameraRef.current.x) * lerp,
        y: seatedCameraRef.current.y + (seatCameraTargetRef.current.y - seatedCameraRef.current.y) * lerp,
      };
    }
    // Active camera: lerped seated cam when sitting, normal follow-cam otherwise
    const drawCam = isSittingRef.current ? seatedCameraRef.current : cameraRef.current;

    const nextView = { x: drawCam.x, y: drawCam.y, w: logW, h: logH };
    const viewNow = Date.now();
    if (
      (!lastViewEmitRef.current || viewNow - lastViewEmitRef.current >= VIEW_EMIT_MIN_MS)
      && !viewUnchanged(lastViewBoxRef.current, nextView)
    ) {
      lastViewEmitRef.current = viewNow;
      lastViewBoxRef.current = nextView;
      socketRef.current?.emit('hall-view', {
        hallSlug: hallSlugRef.current,
        x: nextView.x,
        y: nextView.y,
        w: nextView.w,
        h: nextView.h
      });
    }

    // Build the TV viewport rect.
    // Both axes are world-anchored so the TV moves with the map background.
    // When SEATED the TV expands to fill all available height above the TV
    // bottom world line so users get the best view from the audience.
    // When STANDING the fixed pre-computed size is used so it never flexes
    // as the camera tracks the player moving around the room.
    const TV_CENTER_WORLD_X = 960;  // horizontal centre of the 1920-px map
    const TV_BOTTOM_WORLD_Y = 1312; // world Y of the bottom edge of the TV
    // Project TV bottom world coordinate to screen pixels (changes with camera)
    const tvBottomScreenPx = (TV_BOTTOM_WORLD_Y - drawCam.y) * scale;
    const isSittingNow = isSittingRef.current;
    const isLandscape = canvas.width >= canvas.height;
    let tvViewportRect = null;

    if (isSittingNow && isLandscape && tvBottomScreenPx > 0) {
      // Landscape + seated: expand TV to fill all available height above the TV
      // bottom line so the audience gets the largest possible view.
      // Portrait seated falls through to the fixed-rect branch below — the viewport
      // width is already the limiting constraint there, so no expansion is needed.
      let seatedH = Math.min(tvBottomScreenPx, canvas.height);
      let seatedW = seatedH * (16 / 9);
      if (seatedW > canvas.width) { seatedW = canvas.width; seatedH = seatedW * (9 / 16); }
      const seatedL = (TV_CENTER_WORLD_X - drawCam.x) * scale - seatedW / 2;
      const seatedT = tvBottomScreenPx - seatedH; // bottom edge stays on TV line
      tvViewportRect = { cssL: seatedL, cssT: seatedT, cssW: seatedW, cssH: seatedH };
    } else if (fixedTvRectRef.current) {
      // Standing (any orientation) or portrait seated: fixed dimensions, world-anchored position.
      const { cssW, cssH } = fixedTvRectRef.current;
      const cssL = (TV_CENTER_WORLD_X - drawCam.x) * scale - cssW / 2;
      const cssT = tvBottomScreenPx - cssH;
      tvViewportRect = { cssL, cssT, cssW, cssH };
    }

    // --- MAIN CANVAS: map background only (stays behind TV iframe) ---
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(scale, scale);

    if (mapData && !mapError) {
      drawMap(ctx, mapData, drawCam.x, drawCam.y, logW, logH);
    } else {
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < logW; i += 50) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, logH); ctx.stroke();
      }
      for (let i = 0; i < logH; i += 50) {
        ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(logW, i); ctx.stroke();
      }
    }
    ctx.restore();

    // Theater blackout: full-width black rect from viewport top down to the TV
    // bottom line.  cssT + cssH == (TV_BOTTOM_WORLD_Y − drawCam.y) × scale, so
    // the blackout tracks the map as the camera moves — same as any world object.
    // Only drawn when a video is loaded.
    const hasVideo = !!videoUrlRef.current;
    // TV bottom on screen = cssT + cssH (may be negative if player is far below stage)
    const tvBottomOnScreen = tvViewportRect ? tvViewportRect.cssT + tvViewportRect.cssH : 0;
    const blackoutH = Math.max(0, tvBottomOnScreen);
    if (hasVideo && blackoutH > 0) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, blackoutH);
    }

    // Keep the transparent scroll-passthrough div synced to the blackout region.
    // Only write to DOM when values actually change — DOM mutations every frame
    // cause unnecessary compositing work even when nothing has moved.
    const scrollDiv = blackoutScrollRef.current;
    if (scrollDiv) {
      if (hasVideo && blackoutH > 0) {
        const ps = prevScrollStyleRef.current;
        const newH = `${Math.round(blackoutH)}px`;
        if (ps.display !== 'block') { scrollDiv.style.display = 'block'; ps.display = 'block'; }
        if (ps.h !== newH) { scrollDiv.style.height = newH; ps.h = newH; }
        const newW = `${canvas.width}px`;
        if (ps.w !== newW) { scrollDiv.style.width = newW; ps.w = newW; }
      } else {
        if (prevScrollStyleRef.current.display !== 'none') {
          scrollDiv.style.display = 'none';
          prevScrollStyleRef.current.display = 'none';
        }
      }
    }

    // --- OVERLAY CANVAS: sprites, names, indicators (drawn above TV iframe) ---
    octx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    octx.save();
    octx.scale(scale, scale);

    // Apply camera transform for world-space drawing
    octx.save();
    octx.translate(-drawCam.x, -drawCam.y);

    // Draw other players — sample interpolated snapshot poses; do not read
    // React `players` x/y (those only change on join/leave).
    const interpNow = Date.now();
    const others = Object.values(playersRef.current || {});
    others.forEach((player) => {
      const sampled = sampleInterp(remoteInterpRef.current[player.id], interpNow);
      if (sampled) {
        player.x = sampled.x;
        player.y = sampled.y;
        player.moveState = sampled.moveState;
      }
    });

    others.forEach((player) => {
      if (String(player.id) === String(user?.userId) || String(player.id) === String(socket?.id) || String(player.id) === String(myPlayerIdRef.current)) return;

      // Get previous position for animation
      const prevPosition = animationStateRef.current[player.id]?.lastPosition;

      // Mute ground marker only (talking uses username glow — no foot ellipse)
      if (selfMutedPlayers[player.id]) {
        octx.save();
        octx.fillStyle = 'rgba(255, 0, 0, 0.4)';
        octx.beginPath();
        octx.ellipse(player.x, player.y, 35, 15, 0, 0, 2 * Math.PI);
        octx.fill();
        octx.restore();
      }

      // Clip remotes only when they are actually sitting (idle on a sit
      // teleport). Aisle standers used to match the old 50px radius.
      const remoteClipY = remoteSeatClipY(player, mapData, {
        teamSitY: TEAM_SEAT_SIT_Y,
        renderHeight: spriteConfig.renderHeight
      });

      // Draw interpolated sprite using the snapshot moveState (walk/run/idle).
      drawAnimatedSprite(octx, player.x, player.y, player.id, player, prevPosition, player.moveState, remoteClipY);
    });

    // SECOND PASS: Draw usernames and mute icons AFTER all sprites (so they're always on top).
    // Talking = green glow outline on the name (no foot ellipse / wave icon).
    octx.font = 'bold 14px Arial';
    octx.textAlign = 'center';

    // Talking: username fill turns green; black outline stays for readability.
    const drawUsernameLabel = (label, x, y, fillStyle, isTalking) => {
      octx.save();
      octx.shadowBlur = 0;
      octx.lineWidth = 4;
      octx.strokeStyle = 'black';
      octx.strokeText(label, x, y);
      octx.fillStyle = isTalking ? '#22c55e' : fillStyle;
      octx.fillText(label, x, y);
      octx.restore();
    };

    others.forEach(player => {
      // Icon position (just above character model) — mute marker only
      const iconY = player.y - spriteConfig.characterHeight - 20;

      if (selfMutedPlayers[player.id]) {
        octx.save();
        octx.fillStyle = '#ff0000';
        octx.strokeStyle = '#ffffff';
        octx.lineWidth = 2;

        octx.beginPath();
        octx.roundRect(player.x - 8, iconY - 15, 16, 20, 3);
        octx.fill();
        octx.stroke();

        octx.strokeStyle = '#ffffff';
        octx.lineWidth = 3;
        octx.beginPath();
        octx.moveTo(player.x - 12, iconY + 10);
        octx.lineTo(player.x + 12, iconY - 18);
        octx.stroke();
        octx.restore();
      }

      const hallFlags = hallModeration[String(player.id)]
        || hallModeration[String(player.userId)]
        || {};
      const isHallMuted = !!hallFlags.hallMuted;
      if (isHallMuted) {
        const badgeX = selfMutedPlayers[player.id] ? player.x + 16 : player.x;
        octx.save();
        octx.fillStyle = '#d97706';
        octx.strokeStyle = '#fde68a';
        octx.lineWidth = 2;
        octx.beginPath();
        octx.roundRect(badgeX - 8, iconY - 15, 16, 20, 3);
        octx.fill();
        octx.stroke();
        octx.fillStyle = '#fffbeb';
        octx.font = 'bold 11px Arial';
        octx.textAlign = 'center';
        octx.fillText('M', badgeX, iconY + 1);
        octx.restore();
      }

      const isLocallyMuted = getPlayerAudioPref(playerAudio, player.id).muted;
      const isRemoteTalking =
        !!talkingPlayers[player.id] &&
        !isLocallyMuted &&
        !selfMutedPlayers[player.id] &&
        !isHallMuted;
      const label = player.username || 'Player';
      const nameFill = isLocallyMuted
        ? '#ff3333'
        : (isHallMuted ? '#fbbf24' : 'white');
      drawUsernameLabel(
        label,
        player.x,
        player.y - spriteConfig.characterHeight - 5,
        nameFill,
        isRemoteTalking
      );
    });

    // Mute ground marker for self only (talking uses username glow)
    if (isLocalMuted && isVoiceActive) {
      octx.save();
      octx.fillStyle = 'rgba(255, 0, 0, 0.4)';
      octx.beginPath();
      octx.ellipse(myPosition.x, myPosition.y, 35, 15, 0, 0, 2 * Math.PI);
      octx.fill();
      octx.restore();
    }

    // Draw self sprite — determine move state from current input so the
    // animation is always in sync with what the player is actually doing.
    const prevSelfPosition = animationStateRef.current['self']?.lastPosition;
    const selfHasKeys = !!(
      keysPressed.current['w'] || keysPressed.current['s'] ||
      keysPressed.current['a'] || keysPressed.current['d']
    );
    const selfShift = !!keysPressed.current['shift'];

    let selfMoveState = 'idle';
    if (!isSittingRef.current) {
      if (selfHasKeys) {
        selfMoveState = selfShift ? 'run' : 'walk';
      } else if (dragTargetRef.current !== null) {
        const selfPos = animationStateRef.current['self']?.lastPosition || myPosition;
        const dxDrag = dragTargetRef.current.x - selfPos.x;
        const dyDrag = dragTargetRef.current.y - selfPos.y;
        const distDrag = Math.sqrt(dxDrag * dxDrag + dyDrag * dyDrag);
        selfMoveState = distDrag > 3 * spriteConfig.characterWidth ? 'run' : 'walk';
      }
    }
    // When seated in a team seat force the correct facing direction immediately
    const teamSeat = currentSeatRef.current?.team ? currentSeatRef.current : null;
    if (teamSeat) {
      const selfAnimState = animationStateRef.current['self'];
      if (selfAnimState) {
        // Team A faces left (default), Team B faces right (mirrored)
        selfAnimState.facingLeft = teamSeat.team === 'A';
      }
    }

    // Seed lastMoveTime so linger keeps animation going even when position
    // hasn't changed yet (e.g. pressing against a wall or drag threshold).
    if (selfMoveState !== 'idle') {
      const selfState = animationStateRef.current['self'];
      if (selfState) selfState.lastMoveTime = Date.now();
    }

    // Clip self sprite based on seat type:
    //   team seat  → clip at seat bottom (desk surface): seat.y + seat.height
    //   audience seat → clip at seat top (chair back): seat.y
    const selfClipY = currentSeatRef.current
      ? (currentSeatRef.current.team
          ? currentSeatRef.current.y + currentSeatRef.current.height
          : currentSeatRef.current.y)
      : null;

    drawAnimatedSprite(octx, myPosition.x, myPosition.y, 'self',
      { x: myPosition.x, y: myPosition.y },
      prevSelfPosition,
      selfMoveState,
      selfClipY
    );

    // Self mute icon + username (talking = name glow only; never when Voice Off)
    const selfIconY = myPosition.y - spriteConfig.characterHeight - 20;
    const selfTalking = isVoiceActive && isLocalTalking && !isLocalMuted;

    if (isLocalMuted && isVoiceActive) {
      octx.save();
      octx.fillStyle = '#ff0000';
      octx.strokeStyle = '#ffffff';
      octx.lineWidth = 2;

      octx.beginPath();
      octx.roundRect(myPosition.x - 8, selfIconY - 15, 16, 20, 3);
      octx.fill();
      octx.stroke();

      octx.strokeStyle = '#ffffff';
      octx.lineWidth = 3;
      octx.beginPath();
      octx.moveTo(myPosition.x - 12, selfIconY + 10);
      octx.lineTo(myPosition.x + 12, selfIconY - 18);
      octx.stroke();
      octx.restore();
    }

    if (hallMembership?.isMuted) {
      octx.save();
      octx.fillStyle = '#d97706';
      octx.strokeStyle = '#fde68a';
      octx.lineWidth = 2;
      octx.beginPath();
      octx.roundRect(myPosition.x + 16, selfIconY - 15, 16, 20, 3);
      octx.fill();
      octx.stroke();
      octx.fillStyle = '#fffbeb';
      octx.font = 'bold 11px Arial';
      octx.textAlign = 'center';
      octx.fillText('M', myPosition.x + 16, selfIconY + 1);
      octx.restore();
    }

    const myLabel = user?.username || 'You';
    drawUsernameLabel(
      myLabel,
      myPosition.x,
      myPosition.y - spriteConfig.characterHeight - 5,
      hallMembership?.isMuted ? '#fbbf24' : '#FFD700',
      selfTalking
    );

    const bubbleNow = Date.now();
    pruneChatBubbles(chatBubblesRef.current, bubbleNow);
    chatBubbleHitsRef.current = [];
    const paintChatBubble = (worldX, worldY, identities) => {
      const bubble = chatBubbleFor(chatBubblesRef.current, identities, bubbleNow);
      if (!bubble) return;
      const link = actionableChatBubbleLink(bubble.links);
      const box = drawChatBubble(
        octx,
        worldX,
        worldY - spriteConfig.characterHeight - 22,
        bubble.text,
        chatBubbleAlpha(bubbleNow, bubble.until),
        {
          clickable: !!link,
          variant: chatBubbleVariant(link, bubble.links)
        }
      );
      if (box && link) chatBubbleHitsRef.current.push({ ...box, link });
    };
    others.forEach((player) => {
      if (String(player.id) === String(user?.userId) || String(player.id) === String(socket?.id) || String(player.id) === String(myPlayerIdRef.current)) return;
      paintChatBubble(player.x, player.y, [player.id, player.userId, player.username]);
    });
    paintChatBubble(myPosition.x, myPosition.y, [
      myPlayerIdRef.current,
      user?.userId,
      user?.username || 'Guest'
    ]);

    // Seat proximity detection and prompt (world-space, before restoring camera)
    if (mapData?.seats && !isSittingRef.current) {
      let closest = null;
      let closestDist = 90;
      for (const seat of mapData.seats) {
        const sdx = myPosition.x - seat.cx;
        const sdy = myPosition.y - seat.cy;
        if (Math.sqrt(sdx * sdx + sdy * sdy) < closestDist) {
          closestDist = Math.sqrt(sdx * sdx + sdy * sdy);
          closest = seat;
        }
      }
      nearSeatRef.current = closest;
      if (closest) {
        octx.save();
        octx.font = 'bold 13px Arial';
        octx.textAlign = 'center';
        const label = 'E / Tap: Sit';
        const lw = octx.measureText(label).width + 16;
        const px = closest.cx;
        const py = closest.y - 18;
        octx.fillStyle = 'rgba(0,0,0,0.65)';
        octx.fillRect(px - lw / 2, py - 15, lw, 20);
        octx.fillStyle = '#ffffff';
        octx.fillText(label, px, py);
        octx.restore();
      }
    } else if (!isSittingRef.current) {
      nearSeatRef.current = null;
    }

    // Restore camera transform on overlay canvas (world → screen-space)
    octx.restore();

    // "Stand up" prompt when seated (screen-space, bottom-center)
    if (isSittingRef.current) {
      octx.save();
      octx.font = 'bold 14px Arial';
      octx.textAlign = 'center';
      const standLabel = 'E / Tap anywhere to stand up';
      const sw = octx.measureText(standLabel).width + 24;
      const sh = 28;
      const sx = logW / 2 - sw / 2;
      const sy = logH - 52;
      octx.fillStyle = 'rgba(0,0,0,0.7)';
      octx.fillRect(sx, sy, sw, sh);
      octx.fillStyle = '#ffffff';
      octx.fillText(standLabel, logW / 2, sy + 19);
      octx.restore();
    }

    // Show loading/error message (screen-space, logical coordinates)
    if (mapLoading) {
      octx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      octx.fillRect(0, 0, logW, logH);
      octx.fillStyle = 'white';
      octx.font = '16px Arial';
      octx.textAlign = 'center';
      octx.fillText('Loading map...', logW / 2, logH / 2);
    } else if (mapError) {
      octx.fillStyle = 'rgba(255, 100, 100, 0.2)';
      octx.fillRect(10, 10, logW - 20, 40);
      octx.fillStyle = 'yellow';
      octx.font = '12px Arial';
      octx.textAlign = 'left';
      octx.fillText('Map failed to load. Using simple mode.', 20, 35);
    }

    // Restore scale transform on overlay canvas
    octx.restore();

    // Update TV overlay position directly via DOM ref - no React state = no re-renders
    const tvContainer = tvContainerRef.current;
    if (tvContainer && videoUrlRef.current) {
      const isExpanded = isTvExpandedRef.current;
      // Detect fullscreen OR sit/stand transitions and reset the style cache so
      // the in-world transform is always rewritten on the very next frame
      // (otherwise the throttle check would suppress it when the camera hasn't moved).
      const pt = prevTvStyleRef.current;
      if (isExpanded !== pt._wasExpanded || isSittingNow !== pt._wasSitting) {
        prevTvStyleRef.current = { l: null, t: null, w: null, h: null, display: null, baseSizeSet: false, _wasExpanded: isExpanded, _wasSitting: isSittingNow };
        const ctrl = tvControlsRef.current;
        if (ctrl) {
          ctrl._baseSizeSet = false;
          ctrl._w = null; ctrl._h = null; ctrl._l = null; ctrl._t = null;
        }
      }
      // When expanded to fullscreen, override all positioning with fixed inset-0
      if (isExpanded) {
        prevTvStyleRef.current._wasExpanded = true;
        prevTvStyleRef.current._wasSitting = isSittingNow;
        tvContainer.style.position = 'fixed';
        tvContainer.style.inset = '0';
        tvContainer.style.width = '100%';
        tvContainer.style.height = '100%';
        tvContainer.style.transform = 'none';
        tvContainer.style.zIndex = '100';
        tvContainer.style.display = 'block';
        tvContainer.style.overflow = 'hidden';
        tvContainer.style.background = '#000';
      } else if (mapData && !mapError) {
        // Restore absolute positioning for in-world mode
        tvContainer.style.position = 'absolute';
        tvContainer.style.inset = 'auto';
        tvContainer.style.left = '0';
        tvContainer.style.top = '0';
        tvContainer.style.zIndex = '5';
        tvContainer.style.overflow = 'hidden';
        tvContainer.style.background = '#000';

        if (tvViewportRect) {
          const { cssL, cssT, cssW, cssH } = tvViewportRect;
          const pt = prevTvStyleRef.current;

          if (pt.display !== 'block') { tvContainer.style.display = 'block'; pt.display = 'block'; }

          // Size the iframe to real CSS pixels and only translate it.
          // CSS scale() on a YouTube embed makes the hover overlays miss at 50%
          // height, which shows up as a 1px horizontal line through the pause icon.
          const w = Math.round(cssW);
          const h = Math.round(cssH);
          const tL = Math.round(cssL);
          const tT = Math.round(cssT);
          if (pt.w !== w) { tvContainer.style.width = `${w}px`; pt.w = w; }
          if (pt.h !== h) { tvContainer.style.height = `${h}px`; pt.h = h; }
          if (!pt.baseSizeSet) {
            tvContainer.style.transformOrigin = '0 0';
            pt.baseSizeSet = true;
          }
          if (pt.l !== tL || pt.t !== tT) {
            tvContainer.style.transform = `translate3d(${tL}px,${tT}px,0)`;
            pt.l = tL; pt.t = tT;
          }
        } else {
          if (prevTvStyleRef.current.display !== 'none') {
            tvContainer.style.display = 'none';
            prevTvStyleRef.current.display = 'none';
          }
        }
      } else {
        tvContainer.style.display = 'none';
      }
    } else if (tvContainer) {
      tvContainer.style.display = 'none';
    }

    // Sync the TV controls overlay to the same position as the TV iframe.
    // Computed fresh each frame so it reacts instantly to camera/resize changes.
    // When there's no video loaded the TV is hidden, so hide its controls too.
    const ctrl = tvControlsRef.current;
    if (ctrl && mapData && !mapError && videoUrlRef.current) {
      // Use the same viewport-fill rect for the controls overlay as for the TV iframe.
      let ctrlL, ctrlT, ctrlW, ctrlH;
      if (tvViewportRect) {
        ({ cssL: ctrlL, cssT: ctrlT, cssW: ctrlW, cssH: ctrlH } = tvViewportRect);
      } else {
        ctrlL = 0; ctrlT = 0; ctrlW = 0; ctrlH = 0;
      }
      const isVisible = ctrlL + ctrlW > 0 && ctrlL < canvas.width && ctrlT + ctrlH > 0 && ctrlT < canvas.height;
      if (isVisible) {
        const w = Math.round(ctrlW);
        const h = Math.round(ctrlH);
        const tL = Math.round(ctrlL);
        const tT = Math.round(ctrlT);
        if (ctrl._w !== w) { ctrl.style.width = `${w}px`; ctrl._w = w; }
        if (ctrl._h !== h) { ctrl.style.height = `${h}px`; ctrl._h = h; }
        if (!ctrl._baseSizeSet) {
          ctrl.style.transformOrigin = '0 0';
          ctrl._baseSizeSet = true;
        }
        if (ctrl.style.display !== 'block') ctrl.style.display = 'block';
        if (ctrl._l !== tL || ctrl._t !== tT) {
          ctrl.style.transform = `translate3d(${tL}px,${tT}px,0)`;
          ctrl._l = tL; ctrl._t = tT;
        }
        ctrl.dataset.orientation = canvas.width >= canvas.height ? 'landscape' : 'portrait';
      } else {
        ctrl.style.display = 'none';
      }
    } else if (ctrl) {
      ctrl.style.display = 'none';
    }

    const chatSlot = hallChatSlotRef.current;
    const chatInner = hallChatScaleRef.current;
    if (chatSlot) {
      const hideChat = () => {
        if (prevChatSlotStyleRef.current.display !== 'none') {
          chatSlot.style.display = 'none';
          prevChatSlotStyleRef.current.display = 'none';
        }
      };
      const portrait = canvas.height > canvas.width;
      let show = false;
      let left = 0;
      let top = 0;
      let width = 0;
      let height = 0;
      // Next-video overlays the TV's right edge in landscape, so chat can
      // start at the TV's right instead of leaving an arrow gutter.
      if (!isTvExpandedRef.current && mapData && !mapError && tvViewportRect) {
        if (portrait) {
          height = Math.round(tvViewportRect.cssT);
          width = canvas.width;
          left = 0;
          top = 0;
          show = height >= 96;
        } else {
          left = Math.round(tvViewportRect.cssL + tvViewportRect.cssW);
          top = Math.round(tvViewportRect.cssT);
          height = Math.round(tvViewportRect.cssH);
          width = Math.round(canvas.width - left);
          show = width >= 36 && height >= 80 && left >= 0;
        }
      }
      if (!show) {
        hideChat();
      } else {
        const s = (!portrait && width < LANDSCAPE_CHAT_LOGICAL_W)
          ? width / LANDSCAPE_CHAT_LOGICAL_W
          : 1;
        const innerW = s < 1 ? LANDSCAPE_CHAT_LOGICAL_W : width;
        const innerH = s < 1 ? Math.round(height / s) : height;
        const ps = prevChatSlotStyleRef.current;
        if (ps.display !== 'block') { chatSlot.style.display = 'block'; ps.display = 'block'; }
        if (ps.l !== left) { chatSlot.style.left = `${left}px`; ps.l = left; }
        if (ps.t !== top) { chatSlot.style.top = `${top}px`; ps.t = top; }
        if (ps.w !== width) { chatSlot.style.width = `${width}px`; ps.w = width; }
        if (ps.h !== height) { chatSlot.style.height = `${height}px`; ps.h = height; }
        if (chatInner && (ps.s !== s || ps.iw !== innerW || ps.ih !== innerH)) {
          chatInner.style.width = `${innerW}px`;
          chatInner.style.height = `${innerH}px`;
          chatInner.style.transform = s < 1 ? `scale(${s})` : 'none';
          chatInner.style.transformOrigin = 'top left';
          ps.s = s; ps.iw = innerW; ps.ih = innerH;
        }
      }
    }

  };

  // Keep drawFnRef pointing at the latest draw() closure so the stable RAF loop
  // below always executes with up-to-date React state without restarting the loop.
  // This assignment runs on every render — assigning to a ref during render is safe.
  drawFnRef.current = draw;

  useEffect(() => {
    // Start the RAF loop ONCE.  The loop itself never restarts on re-renders, so
    // hall-snapshot interpolation + setMyPosition no longer cause a frame drop.
    const loop = () => {
      drawFnRef.current?.();
      requestRef.current = requestAnimationFrame(loop);
    };
    requestRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(requestRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const chatTimedOut = !!(hallMembership?.timeoutExpiresAt
    && new Date(hallMembership.timeoutExpiresAt).getTime() > Date.now());
  const canChatSpeak = !hallKicked && !hallMembership?.isMuted && !chatTimedOut;
  const canChatStaff = user?.status === 'Owner'
    || ['moderator', 'admin', 'creator'].includes(hallMembership?.role);
  const canManageStage = isCreatorHall(hallMeta) && (
    user?.status === 'Owner'
    || hallMembership?.role === 'creator'
    || (hallMeta.ownerUserId != null && user?.userId != null
      && String(hallMeta.ownerUserId) === String(user.userId))
  );
  const stageOccupancy = {
    ...(players || {}),
    ...(myPlayerIdRef.current
      ? { [myPlayerIdRef.current]: { id: myPlayerIdRef.current, x: myPosition.x, y: myPosition.y } }
      : {})
  };
  const teamAFull = !teamHasFreeSeat(mapData?.teamSeats, stageOccupancy, 'A');
  const teamBFull = !teamHasFreeSeat(mapData?.teamSeats, stageOccupancy, 'B');

  const toggleVipFor = (player, next) => {
    if (!player) return;
    if (canLinkHallProfile(player) && player.username) {
      fetch(
        `/api/halls/${encodeURIComponent(hallSlug)}/members/${encodeURIComponent(player.username)}/vip`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isVip: next })
        }
      ).then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(data?.error || 'Could not update VIP');
          return;
        }
        setAudioTarget((prev) => (prev && prev.id === player.id ? { ...prev, isVip: next } : prev));
      }).catch(() => alert('Could not update VIP'));
      return;
    }
    if (!player.id) return;
    socketRef.current?.emit('hall-set-vip', { to: player.id, isVip: next, hallSlug });
    setAudioTarget((prev) => (prev && prev.id === player.id ? { ...prev, isVip: next } : prev));
  };

  const teleportToTeam = (player, team) => {
    if (!player?.id) return;
    socketRef.current?.emit('hall-team-seat', { to: player.id, team, hallSlug });
  };
  const chatMutedReason = hallMembership?.isMuted
    ? 'A hall moderator muted you. You cannot chat until unmuted.'
    : (chatTimedOut ? 'You are timed out from this hall.' : null);

  const sendHallChatBody = (body) => {
    const text = String(body || '').trim();
    if (!text) return false;
    if (!canChatSpeak) return false;
    if (!isAuthProp && getGuestHallChatCount() >= GUEST_HALL_CHAT_LIMIT) {
      requestSignInPrompt('guest-chat');
      return false;
    }
    socketRef.current?.emit('hall-chat', { hallSlug, body: text });
    setChatPolicyDenied(false);
    if (!isAuthProp) bumpGuestHallChatCount();
    return true;
  };

  const handleVideoMomentClick = (e, moment) => {
    if (e) e.preventDefault();
    if (!moment?.videoId) return;
    const sameVideo = dbVideoId != null && String(moment.videoId) === String(dbVideoId);
    if (sameVideo && !isClipPlaybackRef.current) {
      if (videoId) beginMomentProgressHoldRef.current(videoId, moment.t);
      if (isLive) queueLiveSeek(ytPlayerRef.current, videoId, moment.t);
      else seekTvToMoment(moment.t);
      return;
    }
    playHallMediaFromChat({ videoId: moment.videoId, t: moment.t });
  };

  const handleClipClick = (e, clip) => {
    if (e) e.preventDefault();
    if (!clip?.clipId) return;
    const playingId = localClip?.clipId ?? clipId;
    if (isClipPlayback && playingId != null && String(playingId) === String(clip.clipId)) {
      setClipReplayNonce((n) => n + 1);
      return;
    }
    playHallMediaFromChat({ clipId: clip.clipId });
  };

  const playHallMediaFromChat = (link) => {
    fetchHallTvMedia({ hallSlug, ...link })
      .then((result) => {
        if (result.blocked) {
          setShareToast(result.error || 'This video isn’t in this hall');
          setTimeout(() => setShareToast(''), 2500);
          return;
        }
        if (typeof onPlayHallMedia === 'function') {
          onPlayHallMedia(result);
          return;
        }
        if (result.clip && dbVideoId != null && String(result.video.videoId) === String(dbVideoId)) {
          const range = clampClipRange(result.clip.startSeconds, result.clip.endSeconds);
          if (!range) return;
          setLocalClip({
            clipId: result.clip.clipId,
            startSeconds: range.startSeconds,
            endSeconds: range.endSeconds
          });
          if (isLive) {
            queueLiveSeek(ytPlayerRef.current, videoId, range.startSeconds, { force: true });
          } else {
            seekTvToMoment(range.startSeconds);
          }
        }
      })
      .catch(() => {
        setShareToast('Could not load that video');
        setTimeout(() => setShareToast(''), 2500);
      });
  };

  activateChatBubbleLinkRef.current = (link) => {
    if (!link) return;
    if (link.kind === 'clip' && link.clip) {
      handleClipClick(null, link.clip);
      return;
    }
    if (link.kind === 'moment' && link.moment) {
      handleVideoMomentClick(null, link.moment);
      return;
    }
    if (link.kind === 'same-site' && link.path) {
      navigate(link.path);
      return;
    }
    if (link.kind === 'url' && link.href) {
      window.open(link.href, '_blank', 'noopener,noreferrer');
    }
  };

  const copyText = (text) => {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text).catch(() => {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      });
    }
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return Promise.resolve();
  };

  const shareOrCopyUrl = (url, copiedLabel, { postToChat = false } = {}) => {
    if (postToChat) sendHallChatBody(url);
    if (preferNativeShare()) {
      navigator.share({
        title: 'Check out this video on BIGPLAYS.GG!',
        url
      }).catch((err) => {
        if (err?.name === 'AbortError') return;
        copyText(url).then(() => {
          setShareToast(copiedLabel);
          setTimeout(() => setShareToast(''), 2000);
        });
      });
      return;
    }
    copyText(url).then(() => {
      setShareToast(copiedLabel);
      setTimeout(() => setShareToast(''), 2000);
    });
  };

  const handleShareVideo = () => {
    const playingId = localClip?.clipId ?? clipId;
    if (isClipPlayback && playingId != null && playingId !== '') {
      shareOrCopyUrl(buildClipUrl(playingId), 'Clip copied');
      return;
    }
    if (dbVideoId == null) {
      if (onShare) onShare();
      return;
    }
    shareOrCopyUrl(buildVideoMomentUrl(dbVideoId, 0), 'Link copied');
  };

  const handleShareMoment = () => {
    const playingId = localClip?.clipId ?? clipId;
    if (isClipPlayback && playingId != null && playingId !== '') {
      shareOrCopyUrl(buildClipUrl(playingId), 'Clip copied', { postToChat: true });
      return;
    }
    if (dbVideoId == null || clipDraft) return;
    const player = ytPlayerRef.current;
    const raw = readPlayerClock(player);
    const last = lastClipClockRef.current;
    const live = inferClipIsLive({
      isLive,
      looksLive: raw.looksLive || last.looksLive,
      duration: raw.duration ?? last.duration,
      previousDuration: last.duration
    });
    const clock = resolveClipClock({
      position: raw.position,
      duration: raw.duration ?? last.duration,
      playerState: raw.playerState,
      isLive: live
    });
    let t = clock.seconds;
    if (!(t > 0) && last.t != null) {
      const extra = Math.max(0, Math.floor((Date.now() - last.atMs) / 1000));
      t = resolveClipClock({
        position: last.t + extra,
        duration: last.duration ?? clock.duration,
        playerState: raw.playerState,
        isLive: live
      }).seconds;
    }
    const durationSeconds = raw.duration ?? last.duration ?? clock.duration;
    const armed = clipArmStart
      && clipArmedUntil > Date.now()
      && String(clipArmStart.dbVideoId) === String(dbVideoId);
    if (armed) {
      if (!isAuthProp) {
        requestSignInPrompt('clip');
        return;
      }
      if (!(t > 0)) {
        setShareToast(live ? "Can't clip this live" : "Can't read timestamp");
        setTimeout(() => setShareToast(''), 2000);
        return;
      }
      const range = clipRangeFromClockTaps(clipArmStart.seconds, t, {
        elapsedSeconds: (Date.now() - (clipArmStart.armedAtMs || Date.now())) / 1000,
        isLive: live,
        durationSeconds
      });
      if (!range) {
        setShareToast('Clip needs a later timestamp');
        setTimeout(() => setShareToast(''), 2000);
        return;
      }
      setClipDraft({
        startSeconds: range.startSeconds,
        endSeconds: range.endSeconds,
        dbVideoId,
        hallSlug
      });
      setClipTitleInput('');
      return;
    }
    if (!(t > 0)) {
      setShareToast(live ? "Can't clip this live" : "Can't read timestamp");
      setTimeout(() => setShareToast(''), 2000);
      return;
    }
    const timestampUrl = buildVideoMomentUrl(dbVideoId, t, { forceT: true });
    copyText(timestampUrl).then(() => {
      setShareToast('Timestamp copied');
      setTimeout(() => setShareToast(''), 2000);
    });
    setClipArmStart({ seconds: t, dbVideoId, videoId, armedAtMs: Date.now() });
    setClipArmedUntil(Date.now() + CLIP_ARM_MS);
  };

  const closeClipDraft = () => {
    setClipDraft(null);
    setClipTitleInput('');
  };

  const handleSubmitClip = async () => {
    if (!clipDraft || clipSubmitting) return;
    const title = String(clipTitleInput || '').trim();
    if (!title) return;
    if (!isAuthProp) {
      requestSignInPrompt('clip');
      return;
    }
    setClipSubmitting(true);
    try {
      const response = await fetch('/api/clips', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          videoId: clipDraft.dbVideoId,
          title: title.slice(0, CLIP_TITLE_MAX),
          startSeconds: clipDraft.startSeconds,
          endSeconds: clipDraft.endSeconds,
          hallSlug: clipDraft.hallSlug || hallSlug
        })
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.clipId) {
        setShareToast(data?.error || 'Could not save clip');
        setTimeout(() => setShareToast(''), 2000);
        return;
      }
      rememberClipTitle(data.clipId, data.title);
      window.dispatchEvent(new CustomEvent(CLIP_CREATED_EVENT, {
        detail: { clipId: data.clipId, hallSlug: data.hall || hallSlug }
      }));
      closeClipDraft();
      setClipArmedUntil(0);
      setClipArmStart(null);
      shareOrCopyUrl(buildClipUrl(data.clipId), 'Clip copied', { postToChat: true });
    } catch (err) {
      console.error('create clip:', err);
      setShareToast('Could not save clip');
      setTimeout(() => setShareToast(''), 2000);
    } finally {
      setClipSubmitting(false);
    }
  };

  const loadOlderHallChat = async () => {
    if (chatLoadingOlder || !chatHasMore) return;
    const oldest = chatMessagesRef.current[0];
    if (!oldest || !hallSlug) return;
    setChatLoadingOlder(true);
    try {
      const params = new URLSearchParams({
        beforeId: String(oldest.id),
        limit: '50'
      });
      const r = await fetch(
        `/api/halls/${encodeURIComponent(hallSlug)}/chat/messages?${params}`,
        { credentials: 'include' }
      );
      const data = r.ok ? await r.json() : null;
      if (!data) return;
      const ids = new Set(chatMessagesRef.current.map((m) => m.id));
      const older = (data.messages || []).filter((m) => !ids.has(m.id));
      const next = [...older, ...chatMessagesRef.current];
      chatMessagesRef.current = next;
      setChatMessages(next);
      setChatHasMore(!!data.hasMore);
    } catch (err) {
      console.error('load older hall chat:', err);
    } finally {
      setChatLoadingOlder(false);
    }
  };

  const handleChatUsername = (msg) => {
    if (!msg) return;
    const myUserId = user?.userId;
    const isSelf = (myUserId && msg.userId != null && String(msg.userId) === String(myUserId))
      || (!myUserId && msg.userId == null && (msg.username === (user?.username || 'Guest')));
    if (isSelf) {
      setAudioTarget(null);
      setShowMicGatePanel(true);
      return;
    }
    const live = Object.values(playersRef.current || {}).find((p) => {
      if (!p) return false;
      if (msg.userId != null && p.userId != null && String(p.userId) === String(msg.userId)) return true;
      if (msg.userId != null && String(p.id) === String(msg.userId)) return true;
      return p.username === msg.username;
    });
    setShowMicGatePanel(false);
    setMenuTarget(null);
    if (live) {
      setAudioTarget({
        id: live.id,
        userId: live.userId,
        username: live.username,
        steamId64: live.steamId64,
        youtubeChannelId: live.youtubeChannelId,
        xp: live.xp,
        hallXp: live.hallXp,
        hallSlug,
        isVip: !!live.isVip
      });
      return;
    }
    setAudioTarget({
      id: null,
      userId: msg.userId,
      username: msg.username,
      hallSlug
    });
  };

  const worldUiScale = viewportSize.width < 1024
    ? 0.5
    : Math.max(1, viewportSize.width / 1920);
  const landscapeControlsW = landscapeArrowW(viewportSize.width);
  const landscapeArrowIcon = viewportSize.width < 1024 ? 20 : 32;

  return (
    <>
      {shareToast && (
        <div className="fixed top-16 left-0 w-full z-50 flex justify-center pointer-events-none">
          <div className="bg-green-600 bg-opacity-90 rounded-md px-4 py-2 text-sm text-white shadow-lg">
            {shareToast}
          </div>
        </div>
      )}
      {momentResumePrompt && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-80 max-w-[90vw] text-white p-4">
            <h2 className="text-lg font-semibold mb-2">Keep this timestamp?</h2>
            <p className="text-sm text-gray-300 mb-4">
              You&apos;ve been watching from this timestamp for a minute. Jump to the linked time, or resume where you left off.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-500 rounded-md py-2 text-sm font-medium"
                onClick={() => commitMomentHold('here')}
              >
                Continue from here
              </button>
              <button
                type="button"
                className="w-full bg-gray-700 hover:bg-gray-600 rounded-md py-2 text-sm font-medium"
                onClick={() => commitMomentHold('resume')}
              >
                Resume at {formatVideoTime(momentResumePrompt.savedPosition)}
              </button>
            </div>
          </div>
        </div>
      )}
      {clipDraft && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black bg-opacity-60"
          onClick={closeClipDraft}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-80 max-w-[90vw] text-white p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold mb-1">Save clip</h2>
            <p className="text-sm text-gray-400 mb-3">
              {formatVideoTime(clipDraft.startSeconds)}–{formatVideoTime(clipDraft.endSeconds)}
            </p>
            <label className="block text-sm text-gray-300 mb-1" htmlFor="clip-title-input">
              Title
            </label>
            <input
              id="clip-title-input"
              type="text"
              autoFocus
              maxLength={CLIP_TITLE_MAX}
              value={clipTitleInput}
              onChange={(e) => setClipTitleInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmitClip();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  closeClipDraft();
                }
              }}
              className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white mb-4 focus:outline-none focus:border-blue-500"
              style={{ fontSize: 16 }}
              placeholder="Name this clip"
            />
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 bg-gray-700 hover:bg-gray-600 rounded-md py-2 text-sm font-medium"
                onClick={closeClipDraft}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 bg-blue-600 hover:bg-blue-500 rounded-md py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!clipTitleInput.trim() || clipSubmitting}
                onClick={handleSubmitClip}
              >
                {clipSubmitting ? 'Saving…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Orientation-aware visibility for TV controls:
          landscape → show left/right columns, hide portrait-only elements
          portrait  → hide left/right columns, show portrait-only elements
          like/dislike/share bar is shown in both orientations */}
      <style>{`
        [data-orientation="landscape"] [data-side="left"],
        [data-orientation="landscape"] [data-side="right"] { display: flex !important; }
        [data-orientation="landscape"] [data-portrait-only] { display: none !important; }
        [data-orientation="landscape"] > div:not([data-side]):not([data-portrait-only]):not([data-actions]) { display: none !important; }

        [data-orientation="portrait"] [data-side="left"],
        [data-orientation="portrait"] [data-side="right"] { display: none !important; }
        [data-orientation="portrait"] [data-portrait-only] { display: flex !important; }
        [data-orientation="portrait"] > div:not([data-side]):not([data-portrait-only]):not([data-actions]) { display: flex !important; }

        [data-orientation] [data-actions] { display: flex !important; }
      `}</style>

      {/* ── Outer wrapper: flex-column, height = visualViewport.height (set by JS).
           Controls bar (flex-shrink:0) + canvas (flex:1) together fill exactly
           the visible screen — scroll past the App nav to see it fullscreen. ── */}
      <div ref={liveWorldWrapperRef} style={{ display: 'flex', flexDirection: 'column', width: '100%', background: '#000' }}>

      {/* Controls bar — measured so canvas gets the remaining height */}
      <div ref={controlsBarRef} className="flex items-center gap-3 px-4 py-1.5 bg-black border-b border-gray-800 text-white text-xs w-full" style={{ flexShrink: 0 }}>
        <HallDropdown
          slug={hallSlug}
          displayName={hallDisplayName}
          appUser={user}
          isAuthenticated={isAuthProp}
        />
        <span
          className="text-yellow-400 whitespace-nowrap flex-shrink-0"
          title="1 XP per minute in a hall while this tab is visible"
        >
          {formatXpLabel(xpDisplay, hallXpDisplay, hallSlug)}
        </span>
        {hallMembership?.isMuted && (
          <span
            className="text-amber-400 whitespace-nowrap flex-shrink-0"
            title="A hall moderator muted you. This is not your local mute (red name)."
          >
            Hall muted
          </span>
        )}

        {/* Mic / Voice controls */}
        {isVoiceActive && isMicEnabled ? (
          <>
            {/* Mute toggle */}
            <button
              type="button"
              data-bigplays-mute-toggle="true"
              onClick={(e) => { e.stopPropagation(); toggleLocalMute(); }}
              className={`flex items-center gap-1 whitespace-nowrap flex-shrink-0 ${isLocalMuted ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}`}
              title="Toggle mic mute (Right Ctrl with BIGPLAYS extension)"
            >
              <span className="w-2 h-2 rounded-full bg-current flex-shrink-0"></span>
              <span>{isLocalMuted ? 'Mic Muted' : 'Mic On'}</span>
            </button>
            {/* Voice off — stops mic tracks and releases iOS audio session */}
            <button
              onClick={(e) => { e.stopPropagation(); disableVoice(); }}
              className="text-gray-500 hover:text-red-400 whitespace-nowrap flex-shrink-0 text-xs"
              title="Disable voice (improves YouTube volume on iOS)"
            >
              Voice Off
            </button>
          </>
        ) : !isVoiceActive ? (
          <button
            onClick={(e) => { e.stopPropagation(); enableVoice(); }}
            className="flex items-center gap-1 whitespace-nowrap flex-shrink-0 text-gray-500 hover:text-green-400"
          >
            <span className="w-2 h-2 rounded-full bg-current flex-shrink-0"></span>
            <span>Voice Off</span>
          </button>
        ) : (
          <span className="flex items-center gap-1 whitespace-nowrap flex-shrink-0 text-red-400">
            <span className="w-2 h-2 rounded-full bg-current flex-shrink-0"></span>
            <span>Mic Access Required</span>
          </span>
        )}

        {/* Volume — shows "Volume X%" normally; click reveals slider for 5 s */}
        {showVolume ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <input
              type="range"
              min="0"
              max="2"
              step="0.05"
              value={globalVolume}
              onChange={(e) => {
                setGlobalVolume(parseFloat(e.target.value));
                if (volTimeoutRef.current) clearTimeout(volTimeoutRef.current);
                volTimeoutRef.current = setTimeout(() => setShowVolume(false), 5000);
              }}
              className="w-24 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <span className="whitespace-nowrap w-8">{Math.round(globalVolume * 100)}%</span>
          </div>
        ) : (
          <button
            onClick={handleVoiceClick}
            className="whitespace-nowrap flex-shrink-0 text-gray-300 hover:text-white"
          >
            Volume {Math.round(globalVolume * 100)}%
          </button>
        )}

        {/* Metadata from parent: username, timestamp, featured & delete buttons */}
        {metaBar}
      </div>

      {/* Canvas area — fills whatever height the controls bar left over (flex:1) */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#000' }}>
        <div className="relative w-full h-full overflow-hidden bg-black">
          {/* Main canvas – map background only, sits behind the TV iframe */}
          <canvas
            ref={canvasRef}
            width={viewportSize.width}
            height={viewportSize.height}
            tabIndex={-1}
            style={{
              display: 'block',
              width: viewportSize.width,
              height: viewportSize.height,
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              outline: 'none',
            }}
          />

          {/* Transparent scroll-passthrough over the blackout area (above/beside TV).
            z:2 → above canvas, below TV iframe (z:5). touchAction:pan-y lets
            mobile users scroll the page from the black region. pointer-events:auto
            means it captures touches here so the canvas below doesn't block scroll.
            TV taps still work because the TV iframe sits above this div (z:5). */}
          <div
            ref={blackoutScrollRef}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              display: 'none',
              zIndex: 2,
              touchAction: 'pan-y',
              cursor: 'default',
            }}
          />

          {/* TV Screen — position driven by draw loop via ref, zero React re-renders.
            In normal mode: muted, controls hidden, hit-target captures clicks to expand.
            In expanded mode: fixed fullscreen, controls interactive, close button shown. */}
          {videoUrl && (
            <div
              ref={tvContainerRef}
              className="absolute"
              style={{ left: 0, top: 0, display: 'none', zIndex: 5, overflow: 'hidden', background: '#000' }}
            >
              <iframe
                key={`${videoId || 'none'}-${tvPlaybackKey}`}
                ref={tvIframeRef}
                src={(() => {
                  const params = new URLSearchParams({
                    autoplay: '1',
                    mute: '1',
                    // VOD clips only: official controls=0. Lives keep controls=1 so
                    // entering/leaving a live clip does not rewrite iframe src
                    // (that reload commonly blacks out the live player).
                    controls: (isClipPlayback && !isLive) ? '0' : '1',
                    modestbranding: '1',
                    rel: '0',
                    enablejsapi: '1',
                    origin: window.location.origin,
                  });
                  // VOD only: start= so YouTube buffers the resume point first.
                  // Never on lives — start= commonly leaves a permanent black player.
                  // Clips: start= only. end= makes YouTube show a restart control
                  // that jumps to 0 of the parent VOD. We loop the clip in JS.
                  if (isClipPlayback && !isLive) {
                    if (activeClipStart > 0) params.set('start', String(activeClipStart));
                  } else if (!isLive && embedResumeStart > 0) {
                    params.set('start', String(embedResumeStart));
                  }
                  const join = videoUrl.includes('?') ? '&' : '?';
                  return `${videoUrl}${join}${params.toString()}`;
                })()}
                title="TV Screen Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                style={{ border: 'none', outline: 'none', pointerEvents: 'auto', background: '#000' }}
                referrerPolicy="strict-origin-when-cross-origin"
              />

              <div
                ref={seekHintRef}
                className="absolute top-1/2 text-white pointer-events-none"
                style={{
                  opacity: 0,
                  transition: 'opacity 160ms ease',
                  transform: 'translateY(-50%)',
                  zIndex: 21,
                  fontSize: 'clamp(16px, 2.3vw, 26px)',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  textShadow: '0 1px 8px rgba(0,0,0,0.85)',
                }}
                aria-hidden="true"
              />

              {/* Close button — only visible in expanded/fullscreen mode */}
              {isTvExpanded && (
                <button
                  style={{ position: 'absolute', top: 42, right: 16, zIndex: 110, pointerEvents: 'auto' }}
                  onClick={() => {
                    setIsTvExpanded(false);
                  }}
                  className="text-white text-3xl font-bold bg-black bg-opacity-60 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-90"
                  aria-label="Close fullscreen"
                >
                  ×
                </button>
              )}

              {/* Side seek zones: hall always; CSS fullscreen only on iPad, where
                YouTube's desktop player treats taps as pause instead of ±10 skip.
                iPhone fullscreen stays uncovered so YouTube's native skip UI runs. */}
              {(!isTvExpanded || isIPadTouchDevice()) && (() => {
                const DOUBLE_MS = 300;
                const commonStyle = {
                  position: 'absolute',
                  top: '12%',
                  height: '76%',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  cursor: 'pointer',
                  zIndex: 20,
                };

                const handleZone = (zone, seekFn) => ({
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (Date.now() - lastTouchTapAtRef.current < 700) return;
                    const now = Date.now();
                    const last = lastTapRef.current;
                    if (now - last.time < DOUBLE_MS && last.zone === zone) {
                      clearTimeout(singleTapTimerRef.current);
                      singleTapTimerRef.current = null;
                      lastTapRef.current = { time: 0, zone: null };
                      seekFn();
                    } else {
                      const tapTime = now;
                      lastTapRef.current = { time: tapTime, zone };
                      clearTimeout(singleTapTimerRef.current);
                      singleTapTimerRef.current = setTimeout(() => {
                        singleTapTimerRef.current = null;
                        if (lastTapRef.current.time === tapTime) {
                          if (isTvExpandedRef.current) return;
                          setIsTvExpanded(true);
                          focusTvPlayer();
                          tvIframeRef.current?.contentWindow?.postMessage(
                            JSON.stringify({ event: 'command', func: 'unMute', args: [] }), '*'
                          );
                        }
                      }, DOUBLE_MS);
                    }
                  },
                  onTouchEnd: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    lastTouchTapAtRef.current = Date.now();
                    const now = Date.now();
                    const last = lastTapRef.current;
                    if (now - last.time < DOUBLE_MS && last.zone === zone) {
                      clearTimeout(singleTapTimerRef.current);
                      singleTapTimerRef.current = null;
                      lastTapRef.current = { time: 0, zone: null };
                      seekFn();
                    } else {
                      const tapTime = now;
                      lastTapRef.current = { time: tapTime, zone };
                      clearTimeout(singleTapTimerRef.current);
                      singleTapTimerRef.current = setTimeout(() => {
                        singleTapTimerRef.current = null;
                        if (lastTapRef.current.time === tapTime) {
                          if (isTvExpandedRef.current) return;
                          setIsTvExpanded(true);
                          focusTvPlayer();
                          tvIframeRef.current?.contentWindow?.postMessage(
                            JSON.stringify({ event: 'command', func: 'unMute', args: [] }), '*'
                          );
                        }
                      }, DOUBLE_MS);
                    }
                  },
                });

                return (
                  <>
                    {/* Left seek zone: 0–33%, 15%–85% */}
                    <div
                      style={{ ...commonStyle, left: 0, width: '33.33%' }}
                      {...handleZone('left', () => seekTvSide(-10))}
                    />
                    {/* Right seek zone: 67–100%, 15%–85% */}
                    <div
                      style={{ ...commonStyle, right: 0, width: '33.33%' }}
                      {...handleZone('right', () => seekTvSide(10))}
                    />
                  </>
                );
              })()}
            </div>
          )}

          {/* TV controls overlay — same position as TV iframe, z:10 above all canvas layers.
            pointer-events:none on container so TV clicks pass through;
            pointer-events:auto on individual buttons. */}
          <div
            ref={tvControlsRef}
            style={{ position: 'absolute', left: 0, top: 0, display: 'none', zIndex: 10, pointerEvents: 'none' }}
          >
            {/* ── LANDSCAPE: prev / next on the TV's left/right edges ── */}
            <div
              className="absolute top-0 h-full"
              style={{ left: 0, width: landscapeControlsW, pointerEvents: 'none', position: 'absolute' }}
              data-side="left"
            >
              {onPrev && (
                <button
                  onClick={onPrev}
                  disabled={!canGoPrev}
                  style={{
                    pointerEvents: 'auto',
                    opacity: canGoPrev ? 1 : 0,
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    transform: 'translateY(-50%)',
                  }}
                  className="flex items-center justify-center text-white hover:text-gray-300 transition-colors drop-shadow-lg"
                  aria-label="Previous video"
                >
                  <svg width={landscapeArrowIcon} height={landscapeArrowIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
            </div>

            <div
              className="absolute top-0 h-full"
              style={{ right: 0, width: landscapeControlsW, pointerEvents: 'none', position: 'absolute' }}
              data-side="right"
            >
              {onNext && (
                <button
                  onClick={onNext}
                  style={{
                    pointerEvents: 'auto',
                    position: 'absolute',
                    top: '50%',
                    right: 0,
                    transform: 'translateY(-50%)',
                  }}
                  className="flex items-center justify-center text-white hover:text-gray-300 transition-colors drop-shadow-lg"
                  aria-label="Next video"
                >
                  <svg width={landscapeArrowIcon} height={landscapeArrowIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>

            {/* Like · dislike · share/timestamp — YouTube-style pills, centered under the TV */}
            {(isAuthProp && onVote) || onShare || dbVideoId != null ? (
              <div
                data-actions="below"
                className="absolute left-1/2 flex items-center"
                style={{
                  top: '100%',
                  transform: `translateX(-50%) scale(${worldUiScale})`,
                  transformOrigin: 'top center',
                  pointerEvents: 'none',
                  paddingTop: '8px',
                  gap: '8px',
                }}
              >
                {isAuthProp && onVote && (() => {
                  const userVote = votes?.[videoId]?.userVote;
                  const pillBtn = {
                    pointerEvents: 'auto',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    cursor: isVoting ? 'default' : 'pointer',
                    opacity: isVoting ? 0.6 : 1,
                  };
                  return (
                    <div
                      className="flex items-center overflow-hidden"
                      style={{
                        height: '36px',
                        background: '#000',
                        borderRadius: '18px',
                        pointerEvents: 'none',
                      }}
                    >
                      <button
                        onClick={() => onVote(videoId, 'upvote')}
                        disabled={isVoting}
                        style={{ ...pillBtn, padding: '0 14px 0 12px', gap: '6px' }}
                        className="hover:bg-gray-900"
                        title="Like"
                        aria-label="Like"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill={userVote === 'upvote' ? 'currentColor' : 'none'}
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M7 22V11M2 13v7a2 2 0 002 2h12.28a2 2 0 001.97-1.69l1.26-7A2 2 0 0016.54 11H13V6a3 3 0 00-3-3L7 11" />
                        </svg>
                        <span style={{ fontSize: '14px', fontWeight: 500, lineHeight: 1 }}>
                          {votes?.[videoId]?.upvotes || 0}
                        </span>
                      </button>
                      <span
                        aria-hidden="true"
                        style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.2)', flexShrink: 0 }}
                      />
                      <button
                        onClick={() => onVote(videoId, 'downvote')}
                        disabled={isVoting}
                        style={{ ...pillBtn, padding: '0 14px' }}
                        className="hover:bg-gray-900"
                        title="Dislike"
                        aria-label="Dislike"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill={userVote === 'downvote' ? 'currentColor' : 'none'}
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          style={{ transform: 'rotate(180deg)' }}
                        >
                          <path d="M7 22V11M2 13v7a2 2 0 002 2h12.28a2 2 0 001.97-1.69l1.26-7A2 2 0 0016.54 11H13V6a3 3 0 00-3-3L7 11" />
                        </svg>
                      </button>
                    </div>
                  );
                })()}
                {(onShare || dbVideoId != null) && (() => {
                  const shareBtn = {
                    pointerEvents: 'auto',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: 1,
                  };
                  return (
                    <div
                      className="flex items-center overflow-hidden"
                      style={{
                        height: '36px',
                        background: '#000',
                        borderRadius: '18px',
                        pointerEvents: 'none',
                      }}
                    >
                      {(onShare || dbVideoId != null) && (
                        <button
                          onClick={handleShareVideo}
                          style={{ ...shareBtn, padding: '0 14px 0 12px' }}
                          className="hover:bg-gray-900"
                          aria-label={isClipPlayback ? 'Share clip' : 'Share video'}
                          title={isClipPlayback ? 'Share clip' : 'Share video'}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M15 5.63 20.66 12 15 18.37V14s-8.97-.5-11 5c1.5-6 6-10 11-11z" />
                          </svg>
                        </button>
                      )}
                      {(onShare || dbVideoId != null) && dbVideoId != null && (
                        <span
                          aria-hidden="true"
                          style={{ width: '1px', height: '18px', background: 'rgba(255,255,255,0.2)', flexShrink: 0 }}
                        />
                      )}
                      {dbVideoId != null && (
                        <button
                          onClick={handleShareMoment}
                          style={{ ...shareBtn, padding: '0 14px' }}
                          className="hover:bg-gray-900"
                          aria-label={isClipPlayback ? 'Copy clip link' : (clipButtonActive ? 'Create a clip' : 'Copy timestamp')}
                          title={isClipPlayback ? 'Copy clip link' : (clipButtonActive ? 'Mark the end of a clip' : 'Copy a timestamp link')}
                        >
                          {clipButtonActive ? (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <circle cx="6" cy="6" r="3" />
                              <circle cx="6" cy="18" r="3" />
                              <path d="M20 4 8.6 12 20 20" />
                            </svg>
                          ) : (
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <circle cx="12" cy="12" r="9" />
                              <path d="M12 7v5l3 2" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {/* Portrait prev arrow — slightly overlays left edge */}
            {onPrev && (
              <button
                onClick={onPrev}
                disabled={!canGoPrev}
                style={{ pointerEvents: 'auto', position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', opacity: canGoPrev ? 1 : 0 }}
                className="flex items-center justify-center text-white hover:text-gray-300 transition-colors drop-shadow-lg w-8 h-12"
                aria-label="Previous video"
                data-portrait-only="true"
              >
                <svg width={landscapeArrowIcon} height={landscapeArrowIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {/* Portrait next arrow — slightly overlays right edge */}
            {onNext && (
              <button
                onClick={onNext}
                style={{ pointerEvents: 'auto', position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
                className="flex items-center justify-center text-white hover:text-gray-300 transition-colors drop-shadow-lg w-8 h-12"
                aria-label="Next video"
                data-portrait-only="true"
              >
                <svg width={landscapeArrowIcon} height={landscapeArrowIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>

          {/* Overlay canvas – sprites, names, indicators drawn above the TV iframe */}
          <canvas
            ref={overlayCanvasRef}
            width={viewportSize.width}
            height={viewportSize.height}
            className="absolute"
            style={{
              left: 0,
              top: 0,
              width: viewportSize.width,
              height: viewportSize.height,
              pointerEvents: 'none',
              zIndex: 6,
            }}
          />

          <div
            ref={hallChatSlotRef}
            className="min-h-0"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              display: 'none',
              zIndex: 8,
              pointerEvents: 'auto',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <div
              ref={hallChatScaleRef}
              className="flex flex-col min-h-0 h-full"
              style={{ transformOrigin: 'top left' }}
            >
            <HallChatPanel
              messages={chatMessages}
              hasMore={chatHasMore}
              loadingOlder={chatLoadingOlder}
              onLoadOlder={loadOlderHallChat}
              onSend={(body, replyToId) => {
                if (!isAuthProp && getGuestHallChatCount() >= GUEST_HALL_CHAT_LIMIT) {
                  requestSignInPrompt('guest-chat');
                  return;
                }
                setChatPolicyDenied(false);
                socketRef.current?.emit('hall-chat', {
                  hallSlug,
                  body,
                  replyToId: replyToId || undefined
                });
                if (!isAuthProp) bumpGuestHallChatCount();
              }}
              onKeep={(msg, saved) => {
                socketRef.current?.emit('hall-chat-keep', {
                  hallSlug,
                  messageId: msg.id,
                  saved
                });
              }}
              onDelete={(msg) => {
                socketRef.current?.emit('hall-chat-delete', {
                  hallSlug,
                  messageId: msg.id
                });
              }}
              onUsernameClick={handleChatUsername}
              onVideoMomentClick={handleVideoMomentClick}
              onClipClick={handleClipClick}
              onComposerFocusChange={(focused) => {
                if (focused) {
                  chatComposerFocusedRef.current = true;
                  chatViewportHoldUntilRef.current = 0;
                  return;
                }
                chatComposerFocusedRef.current = false;
                chatViewportHoldUntilRef.current = Date.now() + 700;
                setTimeout(() => updateCanvasScaleRef.current(), 750);
              }}
              canSpeak={canChatSpeak}
              canStaff={canChatStaff}
              mutedReason={chatMutedReason}
              policyError={chatPolicyDenied ? "That message isn't allowed." : null}
            />
            </div>
          </div>

        </div>
      </div>
      </div> {/* end liveWorldWrapperRef flex-column */}

      {hallKicked && (() => {
        const copy = hallRestrictionCopy(hallKicked);
        return (
          <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/80 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-w-sm w-full p-6 text-white text-center">
              <h2 className="text-xl font-bold mb-2">{copy.title}</h2>
              <p className="text-gray-300 text-sm mb-5">{copy.body}</p>
              {copy.canRejoin && (
                <button
                  type="button"
                  onClick={() => {
                    hallKickedRef.current = null;
                    setHallKicked(null);
                    socketRef.current?.emit('join-hall', { slug: hallSlugRef.current, voiceOn: !!localStreamRef.current });
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg mb-2"
                >
                  Rejoin hall
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full text-sm text-gray-400 hover:text-white py-2"
              >
                Go to Commons
              </button>
            </div>
          </div>
        );
      })()}

      {/* Right-click / long-press moderation menu for other players */}
      <HallMemberMenu
        isOpen={!!menuTarget}
        onClose={() => setMenuTarget(null)}
        hallSlug={hallSlug}
        actor={user}
        actorMembership={hallMembership}
        target={menuTarget}
        canManageStage={canManageStage}
        teamAFull={teamAFull}
        teamBFull={teamBFull}
        onToggleVip={toggleVipFor}
        onTeleportTeam={teleportToTeam}
      />

      {audioTarget && (
        <PlayerAudioPanel
          player={audioTarget}
          playerAudio={playerAudio}
          liveMessage={chatLiveMessage}
          onVideoMomentClick={handleVideoMomentClick}
          onClipClick={handleClipClick}
          onClose={() => setAudioTarget(null)}
          onMuteToggle={audioTarget.id ? () => {
            const pref = getPlayerAudioPref(playerAudio, audioTarget.id);
            setPlayerAudio((prev) => patchPlayerAudio(prev, audioTarget.id, { muted: !pref.muted }));
          } : undefined}
          onVolumeChange={audioTarget.id ? (volume) => {
            setPlayerAudio((prev) => patchPlayerAudio(prev, audioTarget.id, { volume }));
          } : undefined}
          canManageStage={canManageStage}
          teamAFull={teamAFull}
          teamBFull={teamBFull}
          onToggleVip={audioTarget.id ? (next) => toggleVipFor(audioTarget, next) : undefined}
          onTeleportTeam={audioTarget.id ? (team) => teleportToTeam(audioTarget, team) : undefined}
        />
      )}

      {/* Tap own sprite → profile header + mic gate margin */}
      {showMicGatePanel && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-black bg-opacity-60"
          onClick={() => setShowMicGatePanel(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-80 max-w-[90vw] text-white p-4"
          >
            <PlayerPopupHeader
              username={user?.username || 'Guest'}
              steamId64={user?.steamId64}
              youtubeChannelId={user?.youtubeChannelId}
              xp={xpDisplay}
              hallXp={hallXpDisplay}
              hallSlug={hallSlug}
              onClose={() => setShowMicGatePanel(false)}
              canLinkProfile={canLinkHallProfile({
                username: user?.username,
                userId: user?.userId
              })}
            />
            <div className="font-semibold mb-2">Mic sensitivity</div>
            <p className="text-xs text-gray-400 mb-3">
              The mic follows your room noise and opens when your voice rises above it.
              Lower is easier to open. Higher ignores more background noise.
            </p>
            <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
              <span>Noise margin</span>
              <span className="tabular-nums">{formatGateOffset(micGateOffset)}</span>
            </div>
            <input
              type="range"
              min={MIC_GATE_OFFSET_MIN}
              max={MIC_GATE_OFFSET_MAX}
              step="1"
              value={micGateOffset}
              aria-label="Noise margin"
              onChange={(e) => setMicGateOffsetPersisted(e.target.value)}
              className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className="flex justify-between text-[10px] text-gray-500 mt-1">
              <span>Easier to open</span>
              <span>Ignores more noise</span>
            </div>
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-300 mb-1">
                <span>Your level</span>
                <span className={`tabular-nums ${micGateMeter.open ? 'text-green-400' : 'text-gray-400'}`}>
                  {micGateMeter.measured ? `${Math.round(micGateMeter.levelDb)} dB` : '—'}
                  {micGateMeter.measured ? (micGateMeter.open ? ' · open' : ' · closed') : ''}
                </span>
              </div>
              <div className="h-2 w-full bg-gray-800 rounded overflow-hidden relative">
                <div
                  className={`h-full ${micGateMeter.open ? 'bg-green-500' : 'bg-gray-500'}`}
                  style={{ width: `${micGateMeterView(micGateMeter, micGateOffset).levelPercent}%` }}
                />
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-yellow-400"
                  style={{ left: `${micGateMeterView(micGateMeter, micGateOffset).openPercent}%` }}
                />
              </div>
              <div className="mt-1 text-[10px] text-gray-500">
                Room noise {micGateMeter.measured ? `${Math.round(micGateMeter.noiseFloorDb)} dB` : '—'}. Yellow line is where the mic opens.
              </div>
            </div>
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={() => setMicGateOffsetPersisted(MIC_GATE_OFFSET_DEFAULT)}
                className="text-xs text-gray-400 hover:text-white"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => setShowMicGatePanel(false)}
                className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <VoiceQuestionUI
        socket={socket}
        hallSlug={hallSlug}
        hallMeta={hallMeta}
        user={user}
        isCreatorHall={isCreatorHall(hallMeta)}
      />
    </>
  );
};

export default LiveWorld;
